import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Users, CheckCircle, XCircle, Clock, Trash2, Loader2, Shield, UserCheck, UserX, Mail, Send, Plus, X, Calendar, Lock, ChevronDown, UserPlus, Eye, EyeOff, Copy, Download, Upload, Database, ArrowRightLeft, Globe, RefreshCw, Edit2, Search, Tag, Sparkles, MapPin, AlertTriangle, DollarSign, Layers, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, Area, ComposedChart } from "recharts";
import { toast } from "sonner";
import DelegadosPanel from "@/components/admin/DelegadosPanel";
import PandoStatsByUser from "@/components/admin/PandoStatsByUser";
import LlmSettingsPanel from "@/components/admin/LlmSettingsPanel";
import CronErrorsPanel from "@/components/admin/CronErrorsPanel";
import LinkTender360Modal from "@/components/offer/LinkTender360Modal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const DAYS_ES = { monday: "Lunes", tuesday: "Martes", wednesday: "Miércoles", thursday: "Jueves", friday: "Viernes" };
const ROLES = { admin: "Administrador", it_validator: "Validador TIC", cfo: "Director Financiero", offer_manager: "Resp. Oferta", tech: "Técnico", checklist_manager: "Gestor Checklists", delegado_regional: "Delegado Regional" };

// Módulos agrupados por categoría. El id debe coincidir con el path de la ruta o
// con la convención usada por el guard en `components/ModuleGuard.jsx` (si existe)
// y por `allowed_modules` en backend.
const MODULE_GROUPS = [
  {
    label: "Comercial & Licitaciones",
    items: [
      { id: "licitaciones",       label: "Licitaciones SU",       desc: "Apoyo Pando a la realización de ofertas (Carlos Montero, María Folgado, Francisco Morales)" },
      { id: "ofertas-comercial",  label: "Contratación VSM",      desc: "Valoración técnica TIC para Contratación (antes: Ofertas Comerciales · acceso Susana Martín)" },
      { id: "offers",             label: "Ofertas TIC internas",  desc: "Workflow Borrador → Validación TIC → CFO" },
      { id: "tender360",          label: "US_BID360",             desc: "Suite Tender360 — pipeline pre-licitación (antes: Tender360)" },
      { id: "adjudicaciones",     label: "Adjudicaciones",        desc: "Histórico de adjudicaciones VSM" },
      { id: "ora-zaragoza",       label: "ORA Zaragoza",          desc: "Sistema gestión zona azul Zaragoza" },
    ],
  },
  {
    label: "Proyectos & Operaciones",
    items: [
      { id: "projects",           label: "Proyectos",                  desc: "Listado y dashboard de proyectos activos" },
      { id: "backlog",            label: "Backlog",                     desc: "Backlog priorizado por proyecto" },
      { id: "sprint-dashboard",   label: "Sprint Dashboard",           desc: "Velocidad y burndown por sprint" },
      { id: "assignments",        label: "Asignaciones",               desc: "Reparto de personas por proyecto" },
      { id: "checklists",         label: "Checklists de implantación", desc: "Estado item-por-item de cada implantación" },
      { id: "devops",             label: "Azure DevOps Sync",          desc: "Sincronización con Azure DevOps" },
      { id: "demanda",            label: "Gestión de la Demanda",      desc: "Cola de intake Fast track / Desarrollo → Azure DevOps" },
      { id: "horas",              label: "Gestión de Proyectos",       desc: "Horas + estado + entregables (antes: Gestión de Horas · Carlos M., María F., Francisco M., Jesús H.)" },
    ],
  },
  {
    label: "Analítica & Reporting",
    items: [
      { id: "executive",          label: "Mando Ejecutivo",       desc: "KPIs ejecutivos en tiempo real" },
      { id: "committee",          label: "Comité",                desc: "Notas de comité + tarjetas Pando + auto-actualizar" },
      { id: "weekly-summary",     label: "Resumen Semanal",       desc: "Informe semanal generado por Claude" },
      { id: "resource-report",    label: "Informe de Recursos",   desc: "Carga de trabajo y disponibilidad" },
      { id: "reports",            label: "Informes",              desc: "Informes parametrizables exportables" },
      { id: "budget",             label: "Presupuesto",           desc: "Presupuesto vs ejecutado" },
      { id: "resources",          label: "Recursos",              desc: "Catálogo de recursos / personas / proveedores" },
      { id: "ai-history",         label: "Historial IA · Licitaciones",          desc: "Trazabilidad de prompts y consumos de análisis IA sobre licitaciones (checklist, GO/NO-GO, etc.)" },
    ],
  },
  {
    label: "Plataforma TIC",
    items: [
      { id: "catalogo",              label: "Catálogo TIC",          desc: "Servicios y aplicaciones TIC corporativas" },
      { id: "catalogo-aplicaciones", label: "Catálogo de Aplicaciones", desc: "Inventario aplicaciones de negocio" },
      { id: "catalogo-servicios",    label: "Catálogo de Servicios",    desc: "Servicios IT internos para empleados" },
      { id: "innovacion",            label: "Innovación Digital",       desc: "Fichas de innovación + venta externa" },
      { id: "manuales",              label: "Manuales",                  desc: "Manuales de usuario y operación" },
      { id: "poc-ai",                label: "PoC_AI",                    desc: "Prueba de Concepto Pando — casos de uso (Jesús H., Natalia S., Juan Domingo)" },
    ],
  },
  {
    label: "Atención TIC",
    items: [
      { id: "it_peticiones",      label: "Peticiones IT/Pando", desc: "Tickets y peticiones del equipo a TIC" },
    ],
  },
];

// Flat list para iterar/filtrar
const MODULES = MODULE_GROUPS.flatMap(g => g.items);

// Botón compacto R/W/D individual (estilo "chip")
const SCOPE_COLORS = {
  blue:  { on: "bg-blue-600 text-white border-blue-600",   off: "bg-white text-blue-400 border-blue-200 hover:bg-blue-50",   label: "Lectura" },
  amber: { on: "bg-amber-500 text-white border-amber-500", off: "bg-white text-amber-400 border-amber-200 hover:bg-amber-50", label: "Edición" },
  red:   { on: "bg-red-600 text-white border-red-600",     off: "bg-white text-red-400 border-red-200 hover:bg-red-50",     label: "Borrado" },
};
function ScopeToggle({ label, active, color, onClick, testId }) {
  const c = SCOPE_COLORS[color] || SCOPE_COLORS.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={`${c.label} ${active ? "activo" : "inactivo"}`}
      className={`text-[10px] font-bold w-6 h-6 rounded border flex items-center justify-center transition-all ${active ? c.on : c.off}`}
    >
      {label}
    </button>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  // Iter242 · Cola de guardado secuencial por usuario para /permissions.
  // Bug: 2 clicks rápidos (R luego W) disparaban 2 PUT concurrentes que leían
  // el mismo `users` state (stale, aún sin el 1er cambio aplicado). Si el 2º
  // PUT resolvía ANTES que el 1º, el 1º pisaba el resultado con datos viejos
  // → el cambio "se perdía" tras recargar. Ahora cada toggle se encola por
  // uid y lee/escribe sobre un ref con el último estado conocido (no sobre
  // React state, que va con retraso), garantizando orden y sin lost updates.
  const scopesRef = useRef({});      // uid -> module_scopes autoritativo local
  const scopesQueueRef = useRef({}); // uid -> Promise (cola secuencial)
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [msg, setMsg] = useState("");
  // Iter160 · Admin en 6 pestañas: agrupa las ~15 secciones dispersas.
  const [activeTab, setActiveTab] = useState(() => {
    // N12 (roadmap-valoriza-jev-2026-10-11, Ola 4) · un enlace desde Mi
    // trabajo con "?tab=quality" debe abrir esa pestaña directamente, sin
    // depender de lo que quedó guardado de una sesión anterior.
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("tab");
      if (fromUrl) return fromUrl;
      return sessionStorage.getItem("admin-active-tab") || "users";
    } catch { return "users"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("admin-active-tab", activeTab); } catch (_e) { /* no-op */ }
  }, [activeTab]);
  // Create user modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "", name: "", role: "tech", password: "",
    // iter 129: delegado_regional provisioning
    provincias: [], score_min: 75, high_value_floor: 5000000,
  });
  const [creating, setCreating] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [showPw, setShowPw] = useState(false);
  // Permisos colapsados y búsqueda
  const [expandedPerms, setExpandedPerms] = useState({});
  const [userSearch, setUserSearch] = useState("");
  // Weekly report state
  const [wr, setWr] = useState({ enabled: false, recipients: [], send_day: "monday", send_hour: 8, last_sent: null });
  const [wrLoading, setWrLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  // Sync state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = useRef(null);
  // Direct sync to production
  const [syncing, setSyncing] = useState(false);
  const [syncSteps, setSyncSteps] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState("");
  const [syncForm, setSyncForm] = useState({ prod_url: "", email: "dredondo@valorizasm.com", password: "" });
  const [syncPreset, setSyncPreset] = useState({ configured: false, prod_url: "", email: "", has_password: false, last_synced_at: null });
  const [showSyncForm, setShowSyncForm] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);

  const load = useCallback(async () => {
    try {
      const [usersRes, wrRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/settings/weekly-report`),
      ]);
      setUsers(usersRes.data);
      setWr(wrRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveWr = async (updated) => {
    setWrLoading(true);
    try {
      const { data } = await axios.put(`${API}/settings/weekly-report`, updated);
      setWr(data);
      setMsg("Configuración guardada");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { alert("Error guardando configuración"); }
    finally { setWrLoading(false); }
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes("@")) return;
    const updated = { ...wr, recipients: [...wr.recipients.filter(e => e !== newEmail), newEmail] };
    setWr(updated);
    setNewEmail("");
    saveWr(updated);
  };

  const removeRecipient = (email) => {
    const updated = { ...wr, recipients: wr.recipients.filter(e => e !== email) };
    setWr(updated);
    saveWr(updated);
  };

  const toggleWr = () => {
    const updated = { ...wr, enabled: !wr.enabled };
    setWr(updated);
    saveWr(updated);
  };

  const sendNow = async () => {
    if (!window.confirm("⚠ Vas a ENVIAR el informe semanal por email ahora a todos los destinatarios configurados. ¿Continuar?")) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/settings/weekly-report/send-now`);
      setMsg(data.message || "Informe enviado");
      setTimeout(() => setMsg(""), 5000);
      load();
    } catch (e) { alert("Error enviando el informe"); }
    finally { setSending(false); }
  };

  const handleApprove = async (uid, approve) => {
    setProcessing(prev => ({...prev, [uid]: true}));
    try {
      const endpoint = approve ? "approve" : "reject";
      await axios.post(`${API}/admin/users/${uid}/${endpoint}`);
      setMsg(approve ? "Usuario aprobado y notificado por email" : "Usuario rechazado");
      setTimeout(() => setMsg(""), 4000);
      load();
    } catch (e) { alert("Error"); }
    finally { setProcessing(prev => ({...prev, [uid]: false})); }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("⚠ Vas a ELIMINAR este usuario de forma permanente (no se puede deshacer). ¿Continuar?")) return;
    await axios.delete(`${API}/admin/users/${uid}`);
    setUsers(prev => prev.filter(u => u.id !== uid));
  };

  const handleRoleChange = async (uid, newRole) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/${uid}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, role: data.role } : u));
      setMsg("Rol actualizado");
      setTimeout(() => setMsg(""), 3000);
    } catch { alert("Error cambiando el rol"); }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.name) return;
    setCreating(true);
    try {
      const payload = { ...createForm };
      if (!payload.password) delete payload.password;
      // iter 129: sólo enviar campos delegado si el rol lo es
      if (payload.role !== "delegado_regional") {
        delete payload.provincias; delete payload.score_min; delete payload.high_value_floor;
      }
      const { data } = await axios.post(`${API}/admin/users`, payload);
      setCreatedUser(data);
      setCreateForm({ email: "", name: "", role: "tech", password: "",
                      provincias: [], score_min: 75, high_value_floor: 5000000 });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error creando usuario"); }
    finally { setCreating(false); }
  };

  const handlePermissionToggle = async (uid, moduleId, current) => {
    const user = users.find(u => u.id === uid);
    if (!user) return;
    const perms = user.module_permissions || [];
    const newPerms = perms.includes(moduleId) ? perms.filter(p => p !== moduleId) : [...perms, moduleId];
    try {
      const { data } = await axios.put(`${API}/admin/users/${uid}/permissions`, { module_permissions: newPerms });
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, module_permissions: data.module_permissions, module_scopes: data.module_scopes } : u));
    } catch { alert("Error actualizando permisos"); }
  };

  // Devuelve los scopes vigentes del módulo (con fallback al esquema legacy)
  const getScope = (u, moduleId) => {
    const scopes = u.module_scopes || {};
    if (scopes[moduleId]) return scopes[moduleId];
    // Compatibilidad: si está en legacy `module_permissions`, asumimos R+W
    if ((u.module_permissions || []).includes(moduleId)) {
      return { read: true, write: true, delete: false };
    }
    return { read: false, write: false, delete: false };
  };

  // Iter242 · Encola un guardado de module_scopes por uid: `mutateFn` recibe
  // el último estado CONOCIDO (ref, no React state) y devuelve el siguiente.
  // Los PUT del mismo uid se ejecutan en orden estricto → sin lost updates.
  const queueScopeSave = (uid, mutateFn) => {
    if (!(uid in scopesRef.current)) {
      const u = users.find(x => x.id === uid);
      scopesRef.current[uid] = (u?.module_scopes && Object.keys(u.module_scopes).length > 0)
        ? { ...u.module_scopes }
        : Object.fromEntries((u?.module_permissions || []).map(m => [m, { read: true, write: true, delete: false }]));
    }
    const nextScopes = mutateFn({ ...scopesRef.current[uid] });
    scopesRef.current[uid] = nextScopes;
    // Optimista: refleja el cambio en UI de inmediato, antes de que resuelva el PUT.
    setUsers(prev => prev.map(u => u.id === uid ? { ...u, module_scopes: nextScopes } : u));
    const prevInQueue = scopesQueueRef.current[uid] || Promise.resolve();
    scopesQueueRef.current[uid] = prevInQueue.then(async () => {
      try {
        const { data } = await axios.put(`${API}/admin/users/${uid}/permissions`, { module_scopes: nextScopes });
        setUsers(prev => prev.map(u => u.id === uid ? { ...u, module_permissions: data.module_permissions, module_scopes: data.module_scopes } : u));
      } catch (err) {
        alert("Error actualizando scopes: " + (err.response?.data?.detail || err.message));
      }
    });
    return scopesQueueRef.current[uid];
  };

  // Toggle granular: cambia un único scope (read/write/delete) de un módulo
  const handleScopeToggle = (uid, moduleId, scopeName) => {
    queueScopeSave(uid, (currentAll) => {
      const cur = currentAll[moduleId] || { read: false, write: false, delete: false };
      let next = { ...cur, [scopeName]: !cur[scopeName] };
      // Iter243 · Orden correcto: si el usuario quita explícitamente "read",
      // se cascada a quitar write/delete SIEMPRE (antes la regla de abajo lo
      // revertía y era imposible revocar un módulo con W/D activos en 1 click).
      if (scopeName === "read" && !next.read) {
        next = { read: false, write: false, delete: false };
      } else if ((next.write || next.delete) && !next.read) {
        // Activar write/delete mientras read estaba apagado → forzamos read.
        next.read = true;
      }
      if (!next.read && !next.write && !next.delete) {
        delete currentAll[moduleId];
      } else {
        currentAll[moduleId] = next;
      }
      return currentAll;
    });
  };

  // Aplicar a un grupo entero un nivel: "all" (R+W+D) o "read" (sólo lectura) o "off"
  const applyGroupLevel = (uid, groupItems, level) => {
    queueScopeSave(uid, (currentAll) => {
      for (const it of groupItems) {
        if (level === "off") {
          delete currentAll[it.id];
        } else if (level === "read") {
          currentAll[it.id] = { read: true, write: false, delete: false };
        } else if (level === "write") {
          currentAll[it.id] = { read: true, write: true, delete: false };
        } else if (level === "all") {
          currentAll[it.id] = { read: true, write: true, delete: true };
        }
      }
      return currentAll;
    });
  };

  const handleDirectSync = async () => {
    setSyncing(true); setSyncError(""); setSyncSteps([]); setSyncResult(null);
    const steps = [
      { label: "Exportando datos locales…", status: "running" },
    ];
    setSyncSteps([...steps]);
    try {
      const { data } = await axios.post(`${API}/admin/sync-to-production`, syncForm);
      setSyncResult(data);
      setSyncSteps([
        { label: `Exportación: ${data.total_docs_synced} documentos`, status: "ok" },
        { label: "Autenticación en producción", status: "ok" },
        { label: "Importación completada en producción", status: "ok" },
      ]);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error desconocido";
      setSyncError(msg);
      setSyncSteps(prev => prev.map(s => s.status === "running" ? { ...s, status: "error" } : s));
    } finally {
      setSyncing(false);
    }
  };

  // Cargar preset al abrir Admin
  const loadSyncPreset = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sync-preset`);
      setSyncPreset(data);
      if (data.configured) {
        setSyncForm(f => ({ ...f, prod_url: data.prod_url, email: data.email }));
      }
    } catch { /* admin only — silent */ }
  }, []);

  useEffect(() => { loadSyncPreset(); }, [loadSyncPreset]);

  const handleSavePreset = async () => {
    if (!syncForm.prod_url || !syncForm.email || !syncForm.password) {
      setSyncError("Rellena URL, email y password antes de guardar el preset");
      return;
    }
    setSavingPreset(true); setSyncError("");
    try {
      await axios.post(`${API}/admin/sync-preset`, syncForm);
      await loadSyncPreset();
      setShowSyncForm(false);
    } catch (err) {
      setSyncError(err.response?.data?.detail || "Error guardando preset");
    } finally { setSavingPreset(false); }
  };

  const handleQuickSync = async () => {
    setSyncing(true); setSyncError(""); setSyncSteps([]); setSyncResult(null);
    setSyncSteps([{ label: "Lanzando sincronización con preset…", status: "running" }]);
    try {
      const { data } = await axios.post(`${API}/admin/sync-to-production-quick`);
      setSyncResult(data);
      setSyncSteps([
        { label: `Exportación: ${data.total_docs_synced} documentos`, status: "ok" },
        { label: "Autenticación en producción", status: "ok" },
        { label: "Importación completada en producción", status: "ok" },
      ]);
      await loadSyncPreset();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Error desconocido";
      setSyncError(msg);
      setSyncSteps(prev => prev.map(s => s.status === "running" ? { ...s, status: "error" } : s));
    } finally {
      setSyncing(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!window.confirm("¿Borrar el preset guardado?")) return;
    try {
      await axios.delete(`${API}/admin/sync-preset`);
      await loadSyncPreset();
      setSyncForm({ prod_url: "", email: "dredondo@valorizasm.com", password: "" });
    } catch (err) { setSyncError(err.response?.data?.detail || "Error"); }
  };

  const pending = users.filter(u => !u.approved && !u.rejected);
  const approved = users.filter(u => u.approved);
  const rejected = users.filter(u => u.rejected);

  const roleColor = {
    admin: "bg-purple-100 text-purple-700",
    it_validator: "bg-amber-100 text-amber-700",
    cfo: "bg-blue-100 text-blue-700",
    offer_manager: "bg-teal-100 text-teal-700",
    tech: "bg-blue-100 text-blue-700",
    checklist_manager: "bg-emerald-100 text-emerald-700",
    delegado_regional: "bg-orange-100 text-orange-700",
  };

  return (
    <div className="space-y-5 w-full">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">{t("admin.title")}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{t("admin.subtitle")} · {users.length}</p>
      </div>

      {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="admin-success-msg"><CheckCircle size={16}/>{msg}</div>}

      {/* Iter161 · Búsqueda global: salta al tab que contiene el término.
          El catálogo asocia palabras/frases → tab_key. La caja se resuelve
          on-change (no on-submit) para respuesta instantánea. */}
      <AdminSearchBar onJump={setActiveTab} activeTab={activeTab}/>

      {/* Iter160 · Barra de pestañas. Agrupa las secciones dispersas en 6 tabs. */}
      <div className="border-b border-gray-200 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
        {[
          { key: "users",       label: "Usuarios & Permisos" },
          { key: "automations", label: "Automatizaciones" },
          { key: "sync",        label: "Sincronización & Salud" },
          { key: "pando",       label: "Pando & IA" },
          { key: "data",        label: "Datos externos" },
          { key: "quality",     label: "Cola de Calidad" },
          { key: "rules",       label: "Reglas de negocio" },
          { key: "system",      label: "Sistema" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`text-xs font-semibold px-3 py-2 border-b-2 -mb-px whitespace-nowrap transition ${
              activeTab === t.key
                ? "border-emerald-600 text-emerald-800"
                : "border-transparent text-gray-500 hover:text-emerald-700 hover:bg-gray-50"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── TAB: USERS ─────────────────────────────────────────────── */}
      {activeTab === "users" && (<>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Pendientes", count: pending.length, icon: Clock, color: "bg-yellow-100 text-yellow-700" },
          { label: "Aprobados", count: approved.length, icon: UserCheck, color: "bg-green-100 text-green-700" },
          { label: "Rechazados", count: rejected.length, icon: UserX, color: "bg-red-100 text-red-700" },
        ].map(({ label, count, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${color}`}>
              <Icon size={18} />
            </div>
            <p className="text-2xl font-bold text-gray-900 font-['Outfit']">{count}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="bg-white rounded-xl border border-yellow-200 p-5" data-testid="pending-users-section">
          <h3 className="font-bold text-gray-900 font-['Outfit'] mb-4 flex items-center gap-2">
            <Clock size={18} className="text-yellow-500" />
            Pendientes de aprobación ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg border border-yellow-50 bg-yellow-50/50 hover:bg-yellow-50" data-testid={`pending-user-${u.id}`}>
                <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-700 font-bold text-sm flex-shrink-0">
                  {u.name?.slice(0,2).toUpperCase() || "??"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{u.name}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                  <p className="text-xs text-gray-400">{new Date(u.created_at).toLocaleString("es-ES")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleApprove(u.id, true)} disabled={processing[u.id]}
                    className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1.5 disabled:opacity-60"
                    data-testid={`approve-user-${u.id}`}>
                    {processing[u.id] ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12}/>} Aprobar
                  </button>
                  <button onClick={() => handleApprove(u.id, false)} disabled={processing[u.id]}
                    className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1.5 disabled:opacity-60"
                    data-testid={`reject-user-${u.id}`}>
                    <XCircle size={12}/> Rechazar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All users */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <Users size={18} className="text-[#007934]" /> Todos los usuarios ({users.length})
          </h3>
          <button
            onClick={() => { setShowCreate(true); setCreatedUser(null); }}
            className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] transition-all"
            data-testid="create-user-btn"
          >
            <UserPlus size={14} /> Crear Usuario
          </button>
        </div>

        {/* Create user modal */}
        {showCreate && (
          <div className="mb-5 bg-[#E8F5E9] rounded-xl p-5 border border-[#007934]/20">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2"><UserPlus size={15} className="text-[#007934]" /> Nuevo Usuario</h4>
              <button onClick={() => { setShowCreate(false); setCreatedUser(null); }} aria-label="Cerrar" className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            {createdUser ? (
              <div className="bg-white rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-700"><CheckCircle size={16} /><span className="font-semibold text-sm">Usuario creado correctamente</span></div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Email:</span><span className="font-medium">{createdUser.email}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Nombre:</span><span className="font-medium">{createdUser.name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Rol:</span><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor[createdUser.role] || "bg-gray-100 text-gray-600"}`}>{ROLES[createdUser.role] || createdUser.role}</span></div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Contraseña temporal</p>
                      <p className="font-mono font-bold text-gray-900">{showPw ? createdUser.temp_password : "••••••••••"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowPw(!showPw)} className="text-gray-400 hover:text-gray-600 p-1">{showPw ? <EyeOff size={14}/> : <Eye size={14}/>}</button>
                      <button onClick={() => { navigator.clipboard.writeText(createdUser.temp_password); setMsg("Contraseña copiada"); setTimeout(() => setMsg(""), 2000); }} aria-label="Copiar contraseña" className="text-gray-400 hover:text-[#007934] p-1"><Copy size={14}/></button>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">Comparte esta contraseña de forma segura con el usuario. Podrá cambiarla en su perfil.</p>
                </div>
                <button onClick={() => { setShowCreate(false); setCreatedUser(null); }} className="w-full bg-[#007934] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#006528]">Cerrar</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Email *</label>
                  <input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})}
                    placeholder="usuario@valorizasm.com" data-testid="create-user-email"
                    className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Nombre completo *</label>
                  <input type="text" value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})}
                    placeholder="Nombre Apellidos" data-testid="create-user-name"
                    className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Rol</label>
                  <select value={createForm.role} onChange={e => setCreateForm({...createForm, role: e.target.value})}
                    data-testid="create-user-role"
                    className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    {Object.entries(ROLES).map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Contraseña (opcional)</label>
                  <input type="text" value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})}
                    placeholder="Dejar vacío para generar automáticamente"
                    className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                {createForm.role === "delegado_regional" && (
                  <div className="sm:col-span-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3" data-testid="delegado-provisioning-form">
                    <div className="flex items-center gap-2 text-xs font-bold text-indigo-800">
                      <MapPin size={12}/> Zona del delegado regional (obligatorio para verlas)
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-600 mb-1 block">
                        Provincias asignadas ({(createForm.provincias || []).length})
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-indigo-200 rounded p-2 grid grid-cols-3 gap-1 bg-white">
                        {[
                          "A Coruña","Álava","Albacete","Alicante","Almería","Asturias","Ávila","Badajoz","Baleares",
                          "Barcelona","Burgos","Cáceres","Cádiz","Cantabria","Castellón","Ceuta","Ciudad Real","Córdoba",
                          "Cuenca","Girona","Granada","Guadalajara","Guipúzcoa","Huelva","Huesca","Jaén","La Rioja",
                          "Las Palmas","León","Lleida","Lugo","Madrid","Málaga","Melilla","Murcia","Navarra","Ourense",
                          "Palencia","Pontevedra","Salamanca","Santa Cruz de Tenerife","Segovia","Sevilla","Soria",
                          "Tarragona","Teruel","Toledo","Valencia","Valladolid","Vizcaya","Zamora","Zaragoza",
                        ].map(p => (
                          <label key={p} className="text-[10px] flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(createForm.provincias || []).includes(p)}
                              onChange={() => setCreateForm({
                                ...createForm,
                                provincias: (createForm.provincias || []).includes(p)
                                  ? createForm.provincias.filter(x => x !== p)
                                  : [...(createForm.provincias || []), p],
                              })}
                              className="accent-indigo-600"
                              data-testid={`prov-check-${p}`}/>
                            {p}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="text-[11px] text-slate-700">
                        <span className="font-semibold block mb-1">Score Pando mínimo · {createForm.score_min}</span>
                        <input type="range" min="0" max="100" step="5"
                          value={createForm.score_min}
                          onChange={e => setCreateForm({...createForm, score_min: parseInt(e.target.value)})}
                          className="w-full accent-indigo-600"
                          data-testid="prov-score-min"/>
                      </label>
                      <label className="text-[11px] text-slate-700">
                        <span className="font-semibold block mb-1">High-value floor (€)</span>
                        <input type="number" step="100000" min="0"
                          value={createForm.high_value_floor}
                          onChange={e => setCreateForm({...createForm, high_value_floor: Number(e.target.value)})}
                          className="w-full border border-indigo-200 rounded px-2 py-1"
                          data-testid="prov-high-value"/>
                      </label>
                    </div>
                    {(createForm.provincias || []).length === 0 && (
                      <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1">
                        ⚠ Sin provincias, el delegado no verá ninguna oportunidad. Puedes ajustar más tarde en <b>/admin/delegados</b>.
                      </p>
                    )}
                  </div>
                )}
                <div className="sm:col-span-2 flex gap-2">
                  <button onClick={() => { setShowCreate(false); setCreatedUser(null); }}
                    className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm hover:bg-gray-50">Cancelar</button>
                  <button onClick={handleCreateUser} disabled={creating || !createForm.email || !createForm.name}
                    data-testid="confirm-create-user-btn"
                    className="flex-1 bg-[#007934] text-white rounded-lg py-2 text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center justify-center gap-2">
                    {creating ? <Loader2 size={14} className="animate-spin"/> : <UserPlus size={14}/>}
                    {creating ? "Creando…" : "Crear usuario"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {loading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-14 rounded loading-shimmer" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead><tr className="text-xs text-gray-400 uppercase border-b border-gray-50">
                <th className="text-left pb-2 pr-4">Usuario</th>
                <th className="text-left pb-2 pr-4">Email</th>
                <th className="text-left pb-2 pr-4">Rol</th>
                <th className="text-left pb-2 pr-4">Estado</th>
                <th className="text-left pb-2 pr-4">Registro</th>
                <th></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-xs font-bold">
                          {u.name?.slice(0,2).toUpperCase() || "??"}
                        </div>
                        <span className="text-sm font-medium text-gray-900">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600">{u.email}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor[u.role] || "bg-gray-100 text-gray-600"}`}>{ROLES[u.role] || u.role}</span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.approved ? "bg-green-100 text-green-700" : u.rejected ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {u.approved ? "Aprobado" : u.rejected ? "Rechazado" : "Pendiente"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-xs text-gray-400">{new Date(u.created_at).toLocaleDateString("es-ES")}</td>
                    <td className="py-3">
                      {u.role !== "admin" && (
                        <button onClick={() => handleDelete(u.id)} className="text-gray-200 hover:text-red-500 transition-colors p-1" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Module permissions management */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="module-permissions-section">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <Lock size={18} className="text-[#007934]" /> Control de acceso a módulos avanzados
          </h3>
          <div className="flex items-center gap-2">
            <a
              href="/admin/audit-log"
              className="text-[11px] px-3 py-1.5 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 flex items-center gap-1.5"
              title="Ver dashboard de seguridad TIC con denegaciones, top usuarios y módulos"
              data-testid="open-audit-log-btn"
            >
              <Shield size={11} /> Audit log
            </a>
            <button
              onClick={async () => {
              if (!window.confirm("⚠ Vas a MIGRAR los scopes (R/W/D) de todos los usuarios legacy. Es idempotente pero afecta a todos los usuarios. ¿Continuar?")) return;
              try {
                const { data } = await axios.post(`${API}/admin/users/migrate-scopes`);
                setMsg(`Scopes migrados: ${data.migrated} usuarios · ${data.skipped_existing} ya migrados`);
                setTimeout(() => setMsg(""), 5000);
                load();
              } catch (e) {
                alert("Error migrando scopes: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="text-[11px] px-3 py-1.5 rounded-lg border border-[#007934] text-[#007934] hover:bg-green-50 flex items-center gap-1.5"
            title="Genera module_scopes para usuarios que sólo tienen el formato legacy. Idempotente."
            data-testid="migrate-scopes-btn"
          >
            <Shield size={11} /> Migrar scopes (R/W/D)
          </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-3">Los administradores tienen acceso completo. Para el resto, activa <strong>Lectura (R)</strong>, <strong>Edición (W)</strong> y/o <strong>Borrado (D)</strong> por módulo. Edición y borrado requieren lectura.</p>

        {/* Buscador de usuarios */}
        <div className="relative mb-3">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            placeholder="Buscar usuario por nombre o email…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
            data-testid="permissions-search"
          />
        </div>

        <div className="space-y-2">
          {users.filter(u => u.approved && u.role !== "admin").filter(u => {
            if (!userSearch) return true;
            const q = userSearch.toLowerCase();
            return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
          }).map(u => {
            const isExpanded = expandedPerms[u.id];
            // Contar módulos activos
            const activeCount = Object.keys(u.module_scopes || {}).filter(k => (u.module_scopes[k]?.read || u.module_scopes[k]?.write)).length + (u.module_permissions || []).filter(p => !(u.module_scopes || {})[p]).length;
            return (
            <div key={u.id} className="border border-gray-100 rounded-xl overflow-hidden" data-testid={`permissions-row-${u.id}`}>
              {/* Header colapsable */}
              <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedPerms(prev => ({ ...prev, [u.id]: !isExpanded }))}
              >
                <div className="flex items-center gap-3">
                  <AvatarUploader user={u} onUploaded={load}/>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor[u.role] || "bg-gray-100 text-gray-600"}`}>{ROLES[u.role] || u.role}</span>
                  <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium border border-green-100">
                    {activeCount} módulos activos
                  </span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </div>
              </div>

              {/* Panel de permisos (expandido) */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50/40">
                  {/* Cambio de rol inline */}
                  <div className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-xs text-gray-500 font-semibold">Rol del usuario:</span>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#007934] bg-white"
                      data-testid={`role-select-${u.id}`}
                      onClick={e => e.stopPropagation()}
                    >
                      {Object.entries(ROLES).filter(([r]) => r !== "admin").map(([val, lbl]) => (
                        <option key={val} value={val}>{lbl}</option>
                      ))}
                    </select>
                  </div>
                  {/* Editor de aliases de nombre — para que Hall of Fame agrupe por email */}
                  <NameAliasesEditor user={u} onSaved={load}/>
                {MODULE_GROUPS.map(group => {
                  // Estadísticas: cuántos módulos de este grupo tienen cada scope activado
                  const stats = group.items.reduce((acc, it) => {
                    const sc = getScope(u, it.id);
                    if (sc.read) acc.read++;
                    if (sc.write) acc.write++;
                    if (sc.delete) acc.delete++;
                    return acc;
                  }, { read: 0, write: 0, delete: 0 });
                  return (
                    <div key={group.label} className="border border-gray-100 rounded-lg p-3 bg-gray-50/40">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{group.label}</span>
                          <span className="text-[10px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-500">
                            R {stats.read}/{group.items.length} · W {stats.write}/{group.items.length} · D {stats.delete}/{group.items.length}
                          </span>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => applyGroupLevel(u.id, group.items, "read")}
                            className="text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50"
                            data-testid={`group-read-${u.id}-${group.label}`}
                            title="Otorgar sólo lectura a todos los módulos del grupo"
                          >
                            Todos · Lectura
                          </button>
                          <button
                            onClick={() => applyGroupLevel(u.id, group.items, "write")}
                            className="text-[10px] px-2 py-0.5 rounded border border-amber-400 text-amber-700 hover:bg-amber-50"
                            data-testid={`group-write-${u.id}-${group.label}`}
                            title="Lectura + edición a todos los módulos del grupo"
                          >
                            Todos · Edición
                          </button>
                          <button
                            onClick={() => applyGroupLevel(u.id, group.items, "all")}
                            className="text-[10px] px-2 py-0.5 rounded border border-[#007934] text-[#007934] hover:bg-green-50"
                            data-testid={`group-all-${u.id}-${group.label}`}
                            title="Lectura + edición + borrado a todos los módulos del grupo"
                          >
                            Todos · R/W/D
                          </button>
                          <button
                            onClick={() => applyGroupLevel(u.id, group.items, "off")}
                            disabled={stats.read === 0 && stats.write === 0 && stats.delete === 0}
                            className={`text-[10px] px-2 py-0.5 rounded border ${stats.read === 0 && stats.write === 0 && stats.delete === 0 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-red-300 text-red-600 hover:bg-red-50"}`}
                            data-testid={`group-none-${u.id}-${group.label}`}
                          >
                            Quitar todos
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {group.items.map(mod => {
                          const sc = getScope(u, mod.id);
                          return (
                            <div key={mod.id} className="flex items-center justify-between gap-3 px-2 py-1.5 bg-white rounded border border-gray-100" data-testid={`perm-row-${u.id}-${mod.id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-800 truncate" title={mod.desc || ""}>{mod.label}</div>
                                {mod.desc && <div className="text-[10px] text-gray-400 truncate">{mod.desc}</div>}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <ScopeToggle label="R" active={sc.read}    color="blue"  onClick={() => handleScopeToggle(u.id, mod.id, "read")}   testId={`scope-read-${u.id}-${mod.id}`}/>
                                <ScopeToggle label="W" active={sc.write}   color="amber" onClick={() => handleScopeToggle(u.id, mod.id, "write")}  testId={`scope-write-${u.id}-${mod.id}`}/>
                                <ScopeToggle label="D" active={sc.delete} color="red"   onClick={() => handleScopeToggle(u.id, mod.id, "delete")} testId={`scope-delete-${u.id}-${mod.id}`}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                </div>
            )}
            </div>
          );
          })}
          {users.filter(u => u.approved && u.role !== "admin").length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No hay usuarios aprobados (no-admin) aún.</p>
          )}
        </div>
      </div>

      {/* Weekly report settings */}
      </>)}

      {/* ─── TAB: AUTOMATIONS ─────────────────────────────────────── */}
      {activeTab === "automations" && (<>
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="weekly-report-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#007934]" />
            <div>
              <h3 className="font-bold text-gray-900 font-['Outfit']">Informe Semanal Automático</h3>
              <p className="text-xs text-gray-400">Envía el cuadro de mando al comité de dirección cada semana</p>
            </div>
          </div>
          {/* Toggle */}
          <button
            onClick={toggleWr}
            className={`relative w-12 h-6 rounded-full transition-colors ${wr.enabled ? "bg-[#007934]" : "bg-gray-200"}`}
            data-testid="weekly-report-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${wr.enabled ? "translate-x-6" : "translate-x-0"}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {/* Schedule info */}
          <div className="col-span-2 flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Calendar size={16} className="text-gray-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-gray-500 mb-1">Programación</p>
              <div className="flex items-center gap-2">
                <select
                  value={wr.send_day}
                  onChange={e => { const u = {...wr, send_day: e.target.value}; setWr(u); saveWr(u); }}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-[#007934]"
                >
                  {Object.entries(DAYS_ES).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                </select>
                <span className="text-xs text-gray-400">a las</span>
                <select
                  value={wr.send_hour}
                  onChange={e => { const u = {...wr, send_hour: parseInt(e.target.value)}; setWr(u); saveWr(u); }}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-[#007934]"
                >
                  {[6,7,8,9,10,11,12].map(h => <option key={h} value={h}>{h}:00</option>)}
                </select>
              </div>
            </div>
            {wr.last_sent && (
              <div className="text-right">
                <p className="text-xs text-gray-400">Último envío</p>
                <p className="text-xs font-medium text-gray-600">{new Date(wr.last_sent).toLocaleDateString("es-ES")}</p>
              </div>
            )}
          </div>
          {/* Send now */}
          <button
            onClick={sendNow}
            disabled={sending || !wr.recipients.length}
            className="flex items-center justify-center gap-2 border border-[#007934] text-[#007934] rounded-lg py-2 text-sm font-medium hover:bg-[#E8F5E9] disabled:opacity-50 transition-all"
            data-testid="send-report-now-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Enviando…" : "Enviar ahora"}
          </button>
        </div>

        {/* Recipients */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2">Destinatarios ({wr.recipients.length})</p>
          <div className="space-y-1.5 mb-3">
            {wr.recipients.map(email => (
              <div key={email} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <Mail size={12} className="text-gray-400" />
                <span className="text-sm flex-1 text-gray-700">{email}</span>
                <button onClick={() => removeRecipient(email)} aria-label={`Quitar ${email}`} className="text-gray-300 hover:text-red-500 transition-colors"><X size={14} /></button>
              </div>
            ))}
            {wr.recipients.length === 0 && (
              <p className="text-xs text-gray-400 italic py-2">No hay destinatarios. Añade al menos uno para activar el envío automático.</p>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="email@valorizasm.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addRecipient()}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              data-testid="add-recipient-input"
            />
            <button onClick={addRecipient} className="bg-[#007934] text-white px-3 py-2 rounded-lg text-sm hover:bg-[#006528] flex items-center gap-1.5" data-testid="add-recipient-btn">
              <Plus size={14} /> Añadir
            </button>
          </div>
        </div>

        <div className={`mt-3 text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${wr.enabled ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}`}>
          <span className={`w-2 h-2 rounded-full ${wr.enabled ? "bg-green-500" : "bg-gray-300"}`} />
          {wr.enabled
            ? `Activo · El informe se enviará cada ${DAYS_ES[wr.send_day]?.toLowerCase()} a las ${wr.send_hour}:00`
            : "Inactivo · Activa el toggle para habilitar el envío automático"}
        </div>
      </div>

      {/* Sync to Production */}
      </>)}

      {/* ─── TAB: SYNC ────────────────────────────────────────────── */}
      {activeTab === "sync" && (<>
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="sync-section">
        <div className="flex items-center gap-2 mb-1">
          <ArrowRightLeft size={16} className="text-[#007934]" />
          <h3 className="font-bold text-gray-900 font-['Outfit']">Sincronización con Producción</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Exporta los datos de este entorno (preview) e impórtalos en producción
          (<span className="font-medium text-gray-700">plataformascorporativas.valorizasm.com</span>).
          Incluye: Ofertas TIC, Checklists, Proyectos y Catálogo TIC.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {/* Export */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                <Download size={15} className="text-[#007934]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Exportar datos</p>
                <p className="text-xs text-gray-400">Descarga un archivo JSON del entorno actual</p>
              </div>
            </div>
            <button
              onClick={async () => {
                setExporting(true);
                try {
                  const resp = await axios.get(`${API}/admin/export-data`, { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  const cd = resp.headers["content-disposition"] || "";
                  const match = cd.match(/filename=(.+)/);
                  a.download = match ? match[1] : "valoriza_export.json";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { alert("Error al exportar"); }
                finally { setExporting(false); }
              }}
              disabled={exporting}
              className="w-full flex items-center justify-center gap-2 bg-[#007934] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 transition-all"
              data-testid="export-data-btn"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting ? "Exportando..." : "Descargar JSON"}
            </button>
          </div>
          {/* Import */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Upload size={15} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Importar datos</p>
                <p className="text-xs text-gray-400">Carga un JSON exportado (upsert por ID)</p>
              </div>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setImporting(true);
                setImportResult(null);
                try {
                  const fd = new FormData();
                  fd.append("file", file);
                  const { data } = await axios.post(`${API}/admin/import-data`, fd, {
                    headers: { "Content-Type": "multipart/form-data" }
                  });
                  setImportResult(data);
                } catch (err) {
                  alert(`Error al importar: ${err.response?.data?.detail || err.message}`);
                } finally {
                  setImporting(false);
                  e.target.value = "";
                }
              }}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-all"
              data-testid="import-data-btn"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {importing ? "Importando..." : "Seleccionar JSON"}
            </button>
          </div>
        </div>
        {importResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="import-result">
            <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
              <CheckCircle size={14} /> Importación completada
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(importResult.summary || {}).map(([col, info]) => (
                <div key={col} className="bg-white rounded-lg p-2 text-center border border-green-100">
                  <p className="text-lg font-bold text-[#007934]">{info.upserted}</p>
                  <p className="text-xs text-gray-500 capitalize">{col}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">Exportado el: {importResult.source_exported_at?.slice(0, 16).replace("T", " ")}</p>
          </div>
        )}
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mt-1">
          <p className="text-xs text-amber-700">
            <strong>Flujo manual:</strong> (1) Exportar aquí en preview → (2) Acceder al panel Admin en producción → (3) Importar el JSON descargado. La operación es idempotente (no duplica registros existentes).
          </p>
        </div>

        {/* ── Direct Sync to Production ── */}
        <div className="mt-4 border-2 border-[#007934]/20 rounded-xl p-4 bg-gradient-to-br from-[#E8F5E9] to-white" data-testid="direct-sync-panel">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#007934] flex items-center justify-center">
              <ArrowRightLeft size={14} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">Sincronización directa a Producción</p>
              <p className="text-xs text-gray-500">Un clic — no necesitas descargar ni subir archivos</p>
            </div>
          </div>

          {syncResult ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm font-semibold">
                <CheckCircle size={15} /> ¡Sincronización completada!
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(syncResult.summary || {}).map(([col, info]) => (
                  <div key={col} className="bg-white rounded-lg p-2 text-center border border-gray-100 shadow-sm">
                    <p className="text-lg font-bold text-[#007934]">{info.upserted ?? 0}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{col}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                {syncResult.total_docs_synced} docs → {syncResult.prod_url} · {syncResult.synced_at?.slice(0,16).replace("T"," ")}
              </p>
              <button onClick={() => setSyncResult(null)}
                className="text-xs text-gray-500 underline hover:text-gray-700">
                Nueva sincronización
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {/* ── 1-Click Quick Sync (cuando hay preset) ─────────────── */}
              {syncPreset.configured && !showSyncForm && (
                <div className="bg-white rounded-lg border border-[#007934]/30 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-[#007934] uppercase tracking-wide">Preset guardado · listo</p>
                      <p className="text-xs text-gray-700 truncate" title={syncPreset.prod_url}>📡 {syncPreset.prod_url}</p>
                      <p className="text-[10px] text-gray-400">👤 {syncPreset.email}</p>
                      {syncPreset.last_synced_at && (
                        <p className="text-[10px] text-gray-400 mt-0.5">Último sync: {syncPreset.last_synced_at.slice(0,16).replace("T"," ")}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => setShowSyncForm(true)} className="text-[10px] text-gray-500 hover:text-[#007934] underline" data-testid="sync-edit-preset">Editar</button>
                      <button onClick={handleDeletePreset} className="text-[10px] text-rose-500 hover:text-rose-700 underline" data-testid="sync-delete-preset">Borrar</button>
                    </div>
                  </div>
                  {syncSteps.length > 0 && (
                    <div className="space-y-1.5">
                      {syncSteps.map((s, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-1.5 ${
                          s.status === "ok" ? "bg-green-50 text-green-700" :
                          s.status === "running" ? "bg-blue-50 text-blue-700" :
                          "bg-red-50 text-red-700"
                        }`}>
                          {s.status === "ok" ? <CheckCircle size={12} /> :
                           s.status === "running" ? <Loader2 size={12} className="animate-spin" /> :
                           <XCircle size={12} />}
                          {s.label}
                        </div>
                      ))}
                    </div>
                  )}
                  {syncError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700" data-testid="sync-error">
                      {syncError}
                    </div>
                  )}
                  <button
                    onClick={handleQuickSync}
                    disabled={syncing}
                    className="w-full bg-gradient-to-r from-[#007934] to-[#4CAF50] text-white py-3 rounded-lg text-sm font-bold hover:from-[#006528] hover:to-[#3d9b41] disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-md"
                    data-testid="quick-sync-btn"
                  >
                    {syncing ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                    {syncing ? "Sincronizando…" : "🚀 Sincronizar ahora (1 click)"}
                  </button>
                </div>
              )}

              {/* ── Formulario completo (primera vez o editar) ──────────── */}
              {(!syncPreset.configured || showSyncForm) && (
                <>
                  {syncPreset.configured && (
                    <button onClick={() => setShowSyncForm(false)} className="text-[10px] text-gray-500 hover:text-[#007934] underline" data-testid="sync-cancel-edit">
                      ← Volver al preset
                    </button>
                  )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">URL de producción</label>
                <input
                  type="url"
                  value={syncForm.prod_url}
                  onChange={e => setSyncForm(f => ({...f, prod_url: e.target.value}))}
                  placeholder="https://tu-app.emergentagent.com"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                  data-testid="sync-prod-url"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Email admin producción</label>
                  <input
                    type="email"
                    value={syncForm.email}
                    onChange={e => setSyncForm(f => ({...f, email: e.target.value}))}
                    placeholder="admin@valorizasm.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="sync-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Contraseña</label>
                  <input
                    type="password"
                    value={syncForm.password}
                    onChange={e => setSyncForm(f => ({...f, password: e.target.value}))}
                    placeholder="••••••••"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="sync-password"
                  />
                </div>
              </div>

              {syncSteps.length > 0 && (
                <div className="space-y-1.5 py-1">
                  {syncSteps.map((s, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-1.5 ${
                      s.status === "ok" ? "bg-green-50 text-green-700" :
                      s.status === "running" ? "bg-blue-50 text-blue-700" :
                      "bg-red-50 text-red-700"
                    }`}>
                      {s.status === "ok" ? <CheckCircle size={12} /> :
                       s.status === "running" ? <Loader2 size={12} className="animate-spin" /> :
                       <XCircle size={12} />}
                      {s.label}
                    </div>
                  ))}
                </div>
              )}

              {syncError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700" data-testid="sync-error">
                  {syncError}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleDirectSync}
                  disabled={syncing || !syncForm.prod_url || !syncForm.email || !syncForm.password}
                  className="flex-1 bg-[#007934] text-white py-2.5 rounded-lg text-sm font-bold hover:bg-[#006528] disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  data-testid="direct-sync-btn"
                >
                  {syncing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
                  {syncing ? "Sincronizando…" : "Sincronizar ahora"}
                </button>
                <button
                  onClick={handleSavePreset}
                  disabled={savingPreset || !syncForm.prod_url || !syncForm.email || !syncForm.password}
                  title="Guardar preset para sync 1-click"
                  className="px-3 bg-white border-2 border-[#007934] text-[#007934] py-2.5 rounded-lg text-xs font-bold hover:bg-[#007934]/5 disabled:opacity-40 flex items-center gap-1"
                  data-testid="save-preset-btn"
                >
                  {savingPreset ? <Loader2 size={12} className="animate-spin" /> : "💾"} Guardar preset
                </button>
              </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Platform Info */}
      </>)}

      {/* ─── TAB: SYSTEM ──────────────────────────────────────────── */}
      {activeTab === "system" && (<>
      <div className="bg-gradient-to-br from-[#007934] to-[#4CAF50] rounded-xl p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={18} />
          <h3 className="font-bold font-['Outfit']">Información de la Plataforma</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-green-200 text-xs">Administrador principal</p><p className="font-medium">dredondo@valorizasm.com</p></div>
          <div><p className="text-green-200 text-xs">Versión</p><p className="font-medium">1.0.0 · 2026</p></div>
          <div><p className="text-green-200 text-xs">Organización DevOps</p><p className="font-medium">valorizasm</p></div>
          <div><p className="text-green-200 text-xs">Presupuesto máx.</p><p className="font-medium">460.000€</p></div>
        </div>
      </div>

      {/* CC01 · Diagnóstico de entorno/build/dataset (doc completitud-inferencia-cierre) */}
      <EnvBuildDiagnosticPanel />

      {/* CC03/CC06 · Reglas del motor de completitud (auto-aplica vs proponer) */}
      <CompletitudRulesPanel />

      {/* Iter145 · Diagnóstico de env vars + banner env-missing */}
      <EnvDiagnosticsPanel />
      </>)}

      {/* ─── TAB: DATA EXTERNAL ───────────────────────────────────── */}
      {activeTab === "data" && (<>
      {/* VORTAL RPA panel */}
      <VortalSessionPanel />
      </>)}

      {/* ─── TAB: CALIDAD ───────────────────────────────────────────── */}
      {activeTab === "quality" && (<>
      <QualityQueuePanel />

      {/* V02/V16 (revisión 2026-10-11, Jev caso 3) · normalización de catálogos con sugerencia IA */}
      <CatalogNormalizationPanel />

      {/* N02 (roadmap-valoriza-jev-2026-10-11, Ola 1) · hacer observable el JEV que ya existe */}
      <JevObservabilityPanel />

      {/* N07 (roadmap-valoriza-jev-2026-10-11, Ola 3) · tarifas y fórmulas pendientes de aprobación de Finanzas */}
      <TarifasApprovalPanel />
      </>)}

      {/* ─── TAB: REGLAS DE NEGOCIO ─────────────────────────────────── */}
      {/* Reglas de negocio v1 (valoriza-reglas-negocio-iniciales-v1-
          2026-10-12.md, sección 16): "Administración permite buscar,
          filtrar por ámbito/origen, editar, simular impacto, activar desde
          fecha, ver historial y restaurar versión." */}
      {activeTab === "rules" && (<>
      <BusinessRulesPanel />
      </>)}

      {/* ─── TAB: PANDO ───────────────────────────────────────────── */}
      {activeTab === "pando" && (<>
      <LlmSettingsPanel />

      <PandoStatsByUser />

      {/* ANTHROPIC USAGE panel */}
      <AnthropicUsagePanel />

      {/* R24 (criterios 2026-10-11) · catálogo de precios IA */}
      <AiPricingCatalogPanel />

      {/* INTERNAL HOURS IT RATES panel */}
      <InternalHoursRatesPanel />

      {/* OPENAI-COMPAT QUOTAS panel */}
      <OpenAICompatQuotasPanel />
      </>)}

      {/* ─── TAB: AUTOMATIONS (extras) ────────────────────────────── */}
      {activeTab === "automations" && (<>
      {/* WOW DISPLAY config panel */}
      <WowDisplayPanel />

      {/* TENDER KEYWORDS panel */}
      <TenderKeywordsPanel />

      {/* Iter217 · TENDER CPVs panel — códigos CPV personalizados */}
      <TenderCpvsPanel />

      {/* Iter217 · PLACSP sync stats — histórico ZIP vs Live feed */}
      <PlacspSyncStatsPanel />

      {/* Iter237 · MongoDB Atlas latency panel */}
      <MongoHealthPanel />

      {/* Iter218 · Historial de cambios watchlist (keywords + CPVs) */}
      <WatchlistAuditLogPanel />
      </>)}

      {/* ─── TAB: USERS (extras) ──────────────────────────────────── */}
      {activeTab === "users" && (<>
      {/* DELEGADOS REGIONALES panel */}
      <DelegadosPanel />
      </>)}

      {/* ─── TAB: SYNC (extras) ───────────────────────────────────── */}
      {activeTab === "sync" && (<>
      {/* SYNC HEALTH panel — estado en vivo de los crons */}
      <SyncHealthPanel />

      {/* Iter161 · Errores reales de cron */}
      <CronErrorsPanel />

      {/* MERCADO · Backfill geográfico (iter 131 + 133 Pando) */}
      <MercadoGeoBackfillPanel />

      {/* Iter198 · AI Benchmark descargable (XLSX + PDF) sobre una oferta real */}
      <AIBenchmarkPanel />

      {/* Iter200 · Health-check del feed PLACSP · detecta drift antes del sync */}
      <PLACSPHealthPanel />

      {/* Iter168 · Migración de estados MERCADO a PROPUESTA_OPORTUNIDAD */}
      <MercadoMigratePanel />

      {/* Iter175 · Backfill de fecha_presentacion vía scrape del pliego */}
      <FechaPresentacionBackfillPanel />

      {/* Iter179 · Enrichment masivo multi-campo (background) */}
      <MercadoEnrichScrapePanel />

      {/* Iter179.7 · Auto-clasificador DG por CPV + keywords */}
      <MercadoAutoClassifyDGPanel />

      {/* Jev (revisión 2026-10-11, caso 5) · motivo de "No presentada" */}
      <MotivoNoPresentacionJevPanel />

      {/* Iter174 · Diccionario de alias de operadores de competencia */}
      <CompetitionAliasesPanel />

      {/* Iter170 · Panel Ownership · Reparto de opps por comercial */}
      <MercadoOwnershipPanel />

      {/* SYNC ENV panel */}
      <SyncEnvPanel />
      </>)}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// Iter145 · Diagnóstico de variables de entorno + banner env-missing
// ══════════════════════════════════════════════════════════════════════════════
function EnvDiagnosticsPanel() {
  const [envData, setEnvData] = useState(null);
  const [otherEnvData, setOtherEnvData] = useState(null);   // Iter146
  const [comparing, setComparing] = useState(false);        // Iter146
  const [lastCascade, setLastCascade] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [envR, lastR] = await Promise.all([
        axios.get(`${API}/admin/env-check`),
        axios.get(`${API}/mercado/admin/nightly-cascade/last`).catch(() => ({ data: null })),
      ]);
      setEnvData(envR.data);
      setLastCascade(lastR?.data || null);
    } catch (e) {
      // 403 si el usuario no es admin — no fatal.
      if (e.response?.status !== 403) {
        toast.error("No se pudo cargar diagnóstico: " + (e.message || e));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Iter146 · Compara Preview vs Producción llamando al env-check del otro pod.
  const otherEnvUrl = useMemo(() => {
    const here = window.location.origin;
    if (here.includes("backlog-hub.emergent.host")) return "https://i18n-phase2-1.preview.emergentagent.com";
    return "https://backlog-hub.emergent.host";
  }, []);
  const otherEnvLabel = otherEnvUrl.includes("backlog-hub") ? "Producción" : "Preview";
  const runComparison = async () => {
    if (comparing) return;
    setComparing(true);
    try {
      // Iter147 · usamos el endpoint público (sin auth) primero → funciona
      // aunque el JWT no valga en el otro entorno. Fallback al endpoint auth
      // si el público no existe (deploys antiguos).
      let j = null;
      try {
        const rPub = await fetch(`${otherEnvUrl}/api/env-check-signals`);
        if (rPub.ok) j = await rPub.json();
      } catch { /* seguirá al fallback */ }
      if (!j) {
        const token = localStorage.getItem("token") || "";
        const r = await fetch(`${otherEnvUrl}/api/admin/env-check`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (!r.ok) {
          toast.error(`No pude leer ${otherEnvLabel} (${r.status}).`);
          setOtherEnvData({ error: true, code: r.status });
          return;
        }
        j = await r.json();
      }
      setOtherEnvData(j);
    } catch (e) {
      toast.error(`Error contactando ${otherEnvLabel}: ${e.message}`);
      setOtherEnvData({ error: true, message: e.message });
    } finally { setComparing(false); }
  };

  // Banner env-missing: se muestra si la última cascada tiene >24h y no procesó
  // nada, o si faltan claves críticas.
  const showBanner = useMemo(() => {
    if (!envData) return false;
    if (envData.critical_missing > 0) return true;
    if (!lastCascade || lastCascade.status === "empty") return false;
    const finished = new Date(lastCascade.finished_at || lastCascade.started_at || 0);
    const hoursAgo = (Date.now() - finished.getTime()) / 3600000;
    const totalProcessed = (lastCascade.steps || []).reduce((s, st) => s + (st.updated || st.classified || 0), 0);
    return hoursAgo > 24 && totalProcessed === 0;
  }, [envData, lastCascade]);

  if (loading || !envData) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="env-diagnostics-panel">
      {showBanner && (
        <div className="bg-rose-50 border-2 border-rose-500 rounded-lg p-3 flex items-start gap-3" data-testid="env-missing-banner">
          <div className="text-2xl">⚠️</div>
          <div className="flex-1">
            <div className="text-sm font-bold text-rose-900">
              {envData.critical_missing > 0
                ? `Faltan ${envData.critical_missing} claves críticas en este entorno`
                : "La última cascada no procesó ningún doc en > 24 h"}
            </div>
            <div className="text-xs text-rose-700 mt-0.5">
              Revisa las claves marcadas en rojo abajo o abre <b>Deploy Settings</b> en Emergent para añadir las que falten y redepliega.
              Si sigue fallando tras redeploy, contacta a soporte.
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">🔐 Diagnóstico de variables de entorno</h3>
          <p className="text-[10px] text-gray-500">Muestra qué claves están definidas en este pod sin exponer valores. Actualizado tras cada refresco.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runComparison}
            disabled={comparing}
            data-testid="env-compare-btn"
            title={`Consulta el env-check de ${otherEnvLabel} y muestra ambas columnas`}
            className="text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-60 px-3 py-1 rounded flex items-center gap-1">
            {comparing ? "Consultando…" : `⇄ Comparar con ${otherEnvLabel}`}
          </button>
          {otherEnvData && (
            <button
              onClick={() => setOtherEnvData(null)}
              data-testid="env-compare-close"
              className="text-xs text-slate-500 hover:underline">
              Ocultar comparación
            </button>
          )}
          <button onClick={load} data-testid="env-diagnostics-refresh" className="text-xs text-blue-600 hover:underline">Refrescar</button>
        </div>
      </div>

      {otherEnvData && !otherEnvData.error ? (
        <>
          {/* Iter147 · CTA copiar env vars faltantes */}
          <EnvCopyPanel local={envData} remote={otherEnvData} remoteLabel={otherEnvLabel}/>
          {/* Iter146 · Vista comparativa 2 columnas */}
          <div className="grid grid-cols-2 gap-2" data-testid="env-compare-grid">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 col-span-1">
            Este entorno ({envData.env_label || "actual"}) · <span className="text-slate-400 font-normal">{envData.env_hostname?.slice(0, 20)}</span>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 col-span-1">
            {otherEnvLabel} · <span className="text-slate-400 font-normal">{otherEnvData.env_hostname?.slice(0, 20)}</span>
          </div>
          {envData.vars.map((v, idx) => {
            const otherV = otherEnvData.vars.find(x => x.key === v.key) || {};
            const diff = v.present !== otherV.present;
            return (
              <React.Fragment key={v.key}>
                <EnvVarCard v={v} diff={diff} testid={`env-var-local-${v.key}`}/>
                <EnvVarCard v={otherV} diff={diff} testid={`env-var-remote-${v.key}`}/>
              </React.Fragment>
            );
          })}
        </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {envData.vars.map(v => (
            <EnvVarCard key={v.key} v={v} testid={`env-var-${v.key}`}/>
          ))}
        </div>
      )}

      {otherEnvData && otherEnvData.error && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="env-compare-error">
          No pude leer {otherEnvLabel} ({otherEnvData.code || "network"}). Suele pasar si tu sesión sólo existe aquí. Ábrelo en pestaña nueva, haz login y vuelve a comparar.
        </div>
      )}

      <div className="text-[10px] text-slate-400 font-mono">
        Hostname: {envData.env_hostname || "—"} · {(envData.checked_at || "").replace("T", " ").slice(0, 19)}
      </div>
    </div>
  );
}

// Tarjeta reutilizable de env var
function EnvVarCard({ v, diff = false, testid }) {
  if (!v || !v.key) {
    return (
      <div data-testid={testid} className="border border-dashed border-slate-200 rounded-lg px-3 py-2 text-[10px] text-slate-400 italic">
        (No devuelto)
      </div>
    );
  }
  return (
    <div
      data-testid={testid}
      className={`border rounded-lg px-3 py-2 flex items-center gap-2 ${
        v.present
          ? diff
            ? "bg-amber-50 border-amber-300"
            : "bg-emerald-50 border-emerald-200"
          : v.importance === "critical"
            ? "bg-rose-50 border-rose-300"
            : v.importance === "important"
              ? "bg-amber-50 border-amber-200"
              : "bg-slate-50 border-slate-200"
      }`}
    >
      <div className={`w-2.5 h-2.5 rounded-full ${v.present ? "bg-emerald-500" : v.importance === "critical" ? "bg-rose-500" : v.importance === "important" ? "bg-amber-500" : "bg-slate-400"}`}></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <code className="text-[11px] font-mono font-semibold text-slate-800 truncate">{v.key}</code>
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${v.importance === "critical" ? "bg-rose-100 text-rose-700" : v.importance === "important" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
            {(v.importance || "").toUpperCase()}
          </span>
          {diff && <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-amber-500 text-white">Δ</span>}
        </div>
        <div className="text-[10px] text-slate-500 truncate" title={v.description}>{v.description}</div>
      </div>
      <div className={`text-[11px] font-bold whitespace-nowrap ${v.present ? "text-emerald-700" : "text-rose-700"}`}>
        {v.present ? `✓ ${v.length ?? "?"} chars` : "✗ Falta"}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Iter147 · EnvCopyPanel: sugiere qué claves faltan en el otro entorno y
// genera un bloque .env listo para pegar en Deploy Settings.
// ═══════════════════════════════════════════════════════════════════════════
function EnvCopyPanel({ local, remote, remoteLabel }) {
  const missingInRemote = local.vars.filter(v => {
    const r = remote.vars.find(x => x.key === v.key);
    return v.present && !(r && r.present) && v.importance !== "optional";
  });
  if (missingInRemote.length === 0) return null;

  const envSnippet = missingInRemote.map(v =>
    `${v.key}=${v.importance === "critical" ? "<PEGA_AQUÍ_EL_VALOR_DE_PREVIEW>" : "<opcional>"}`
  ).join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(envSnippet);
      toast.success(`${missingInRemote.length} claves copiadas al portapapeles`);
    } catch {
      toast.error("No pude copiar. Selecciona y copia manualmente.");
    }
  };

  return (
    <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-3" data-testid="env-copy-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-bold text-indigo-900 flex items-center gap-1">
            📋 Faltan {missingInRemote.length} clave{missingInRemote.length !== 1 ? "s" : ""} en {remoteLabel}
          </div>
          <div className="text-[11px] text-indigo-700 mt-0.5">
            Están presentes aquí pero no allá. Copia este bloque, pégalo en <b>Deploy Settings</b> y rellena los valores desde tu vault (nunca los verás desde aquí).
          </div>
        </div>
        <button
          onClick={copy}
          data-testid="env-copy-btn"
          className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
          📋 Copiar .env
        </button>
      </div>
      <pre className="bg-white border border-indigo-100 rounded p-2 text-[10px] font-mono text-slate-700 overflow-x-auto" data-testid="env-copy-snippet">
{envSnippet}
      </pre>
      <div className="text-[10px] text-indigo-500 mt-1 italic">
        Nota: el sistema nunca conoce los valores concretos, sólo si la clave está definida. Pégalos desde tu gestor de secretos.
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MERCADO · Backfill provincia + comunidad_autónoma
// ══════════════════════════════════════════════════════════════════════════════
function MercadoGeoBackfillPanel() {
  const [stats, setStats] = useState(null);
  const [last, setLast] = useState(null);
  const [running, setRunning] = useState(false);
  const [runningKind, setRunningKind] = useState(null);
  // Iter149 · progreso del enrich-all
  const [enrichProgress, setEnrichProgress] = useState(null); // {done, target, updated}
  // Iter196 · Mega-Enrich status + collapse avanzado
  const [megaStatus, setMegaStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Iter197 · Estimación de coste (EUR) antes de lanzar
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  const enrichAllRemaining = async () => {
    let done = 0;
    let updatedTotal = 0;
    let consecutiveEmpty = 0;
    setEnrichProgress({ done: 0, updated: 0 });
    try {
      // Máx 50 iteraciones (~500 docs) por invocación
      for (let i = 0; i < 50; i++) {
        try {
          const r = await axios.post(`${API}/mercado/admin/enrich-placsp?limit=10`, null, { timeout: 90000 });
          const upd = r.data?.updated || 0;
          const scanned = r.data?.scanned || 0;
          done += scanned;
          updatedTotal += upd;
          setEnrichProgress({ done, updated: updatedTotal });
          if (scanned === 0) break;              // No quedan candidatos
          if (upd === 0) consecutiveEmpty += 1;
          else consecutiveEmpty = 0;
          if (consecutiveEmpty >= 3) break;      // 3 lotes seguidos sin resultado → cortar
        } catch (batchErr) {
          const msg = batchErr.response?.status === 524
            ? "Timeout proxy" : (batchErr.message || "");
          toast.warning(`Lote ${i + 1} falló (${msg}). Continuando…`);
          await new Promise(res => setTimeout(res, 3000));
        }
      }
      toast.success(`Enrich completo: ${updatedTotal} docs actualizados (${done} escaneados).`, { duration: 12000 });
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setEnrichProgress(null);
      setRunning(false);
      setRunningKind(null);
      load();
    }
  };

  const load = async () => {
    try {
      const [s, l, m, e] = await Promise.all([
        axios.get(`${API}/mercado/admin/geo-stats`),
        axios.get(`${API}/mercado/admin/backfill-geo/last`),
        axios.get(`${API}/mercado/admin/mega-enrich/last`).catch(() => ({ data: null })),
        axios.get(`${API}/mercado/admin/mega-enrich/estimate`).catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setLast(l.data || null);
      setMegaStatus(m.data && m.data.status !== "empty" ? m.data : null);
      setEstimate(e.data || null);
    } catch { /* no-op */ }
  };

  const refreshEstimate = async () => {
    setEstimateLoading(true);
    try {
      const r = await axios.get(`${API}/mercado/admin/mega-enrich/estimate`);
      setEstimate(r.data);
    } catch { /* no-op */ }
    finally { setEstimateLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Poll durante ejecución
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 3500);
    return () => clearInterval(t);
  }, [running]);

  const kick = async (kind) => {
    setRunning(true);
    setRunningKind(kind);
    try {
      if (kind === "heur") {
        await axios.post(`${API}/mercado/admin/backfill-geo?only_empty=true&limit=5000`);
        toast.success("Backfill heurístico lanzado. Actualizando cada 3s…");
      } else if (kind === "heur-all") {
        await axios.post(`${API}/mercado/admin/backfill-geo?only_empty=false&limit=5000`);
        toast.success("Backfill heurístico (TODOS) lanzado.");
      } else if (kind === "ai") {
        await axios.post(`${API}/mercado/admin/backfill-geo-ai?limit=200`);
        toast.success("Backfill Pando lanzado (200 docs, ~40s).");
      } else if (kind === "enrich") {
        // Iter148 · síncrono con lote pequeño (≤15) para no timeout de proxy
        const r = await axios.post(`${API}/mercado/admin/enrich-placsp?limit=10`, null, { timeout: 90000 });
        toast.success(`Enricher: ${r.data?.updated || 0}/${r.data?.scanned || 0} actualizados`);
      } else if (kind === "enrich-all") {
        // Iter149 · bucle hasta cubrir todos los docs con URL sin enriquecer
        await enrichAllRemaining();
        return;   // enrichAllRemaining maneja el toast final
      } else if (kind === "score") {
        await axios.post(`${API}/mercado/admin/score-bulk?limit=20`);
        toast.success("Score Pando lanzado (20 docs, 60-90s).");
      } else if (kind === "mega") {
        // Iter197 · Confirmar coste antes de lanzar
        const cost = estimate?.total_eur;
        const costMsg = cost !== undefined && cost !== null
          ? `Coste estimado IA: ${cost.toFixed(3)} €.\n\n¿Continuar con el Mega-Enriquecimiento?`
          : "¿Continuar con el Mega-Enriquecimiento? (Coste estimado no disponible)";
        if (!window.confirm(costMsg)) {
          setRunning(false); setRunningKind(null);
          return;
        }
        await axios.post(`${API}/mercado/admin/mega-enrich?days=7`);
        toast.success(`🚀 Mega-enriquecimiento lanzado${cost ? ` (~${cost.toFixed(3)} €)` : ""}. 10-20 min.`, { duration: 12000 });
      } else if (kind === "cascade") {
        await axios.post(`${API}/mercado/admin/nightly-cascade`);
        toast.success("Cascada completa lanzada (heur → Pando → enrich → score → alerta). 5-10 min.");
      } else if (kind === "demo") {
        const r = await axios.post(`${API}/mercado/admin/nightly-cascade?demo=true`);
        const st = r.data?.steps || [];
        const summary = st.map(s => `${s.stage}: ${s.would_scan}`).join(" · ");
        toast.success(`Demo OK · ${r.data?.total_docs || 0} docs · ${summary}`, { duration: 15000 });
        setTimeout(() => { setRunning(false); setRunningKind(null); load(); }, 500);
        return;
      }
      setTimeout(() => { setRunning(false); setRunningKind(null); load(); }, 90000);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
      setRunning(false);
      setRunningKind(null);
    }
  };

  const coverage = stats?.coverage_pct || 0;
  const pctColor = coverage >= 90 ? "bg-emerald-600" : coverage >= 60 ? "bg-amber-500" : "bg-rose-600";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="mercado-geo-backfill-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Mercado · Enriquecimiento del pipeline</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Herramientas para mantener <code>mercado_opportunities</code> completo (geografía, presupuesto, CPV, scoring IA…).
            <br/>Usa el <b>botón verde de arriba</b> para todo en uno. Las fases individuales sólo son para debug.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="mercado-geo-refresh"
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg">Refrescar</button>
      </div>

      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Total docs</div>
            <div className="text-xl font-bold text-slate-800" data-testid="mercado-geo-total">{stats.total}</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700">Con provincia</div>
            <div className="text-xl font-bold text-emerald-800" data-testid="mercado-geo-with-prov">{stats.with_provincia}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-blue-700">Con CCAA</div>
            <div className="text-xl font-bold text-blue-800" data-testid="mercado-geo-with-ccaa">{stats.with_ccaa}</div>
          </div>
          <div className="bg-rose-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-rose-700">Sin resolver</div>
            <div className="text-xl font-bold text-rose-800" data-testid="mercado-geo-unresolved">{stats.unresolved}</div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-500 mb-3">Cargando estadísticas…</p>
      )}

      {stats && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1 text-xs text-gray-600">
            <span>Cobertura</span>
            <span className="font-mono" data-testid="mercado-geo-coverage-pct">{coverage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className={`h-full ${pctColor} transition-all`} style={{width: `${coverage}%`}}/>
          </div>
        </div>
      )}

      {/* Iter196 · Sección A · MEGA-ENRIQUECIMIENTO (One-Click) ────────── */}
      <div className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 mb-4"
           data-testid="mercado-mega-enrich-section">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <h4 className="text-sm font-bold text-emerald-900 flex items-center gap-2">
              🚀 Mega-Enriquecimiento (Todo en uno)
            </h4>
            <p className="text-[11px] text-emerald-800 mt-1 leading-relaxed">
              Ejecuta en cascada: <b>1)</b> scrape PLACSP/BOE/TED (7 días) → <b>2)</b> backfill geografía (heurístico + IA) → <b>3)</b> enrich PLACSP + Perplexity → <b>4)</b> score IA GO/NO-GO → <b>5)</b> alerta ≥80% → <b>6)</b> enrich-scrape Playwright (background).
              <br/>Duración típica: 10-20 min. Coste IA: ~1-3 €. Idempotente (relanzable).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => kick("mega")}
            disabled={running}
            data-testid="mercado-mega-enrich-btn"
            title="Encadena TODO el pipeline. Es lo único que necesitas para dejar la pestaña Mercado 100% enriquecida."
            className="text-sm bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-5 py-2.5 rounded-lg shadow-md disabled:opacity-50 flex items-center gap-2">
            {running && runningKind === "mega" ? (<><Loader2 className="animate-spin" size={16}/> Lanzando…</>) : "🚀 Ejecutar Mega-Enriquecimiento AHORA"}
          </button>
          <button
            onClick={() => kick("demo")}
            disabled={running}
            data-testid="mercado-mega-enrich-demo-btn"
            title="Simulación sin coste — sólo cuenta qué haría cada paso."
            className="text-xs bg-white border-2 border-emerald-500 text-emerald-800 hover:bg-emerald-50 px-3 py-1.5 rounded-lg disabled:opacity-50">
            🧪 Modo demo (sin coste)
          </button>
          <button
            onClick={refreshEstimate}
            disabled={estimateLoading}
            data-testid="mercado-mega-enrich-refresh-cost"
            title="Recalcular la estimación de coste (docs pendientes actuales)"
            className="text-xs bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-1.5 rounded-lg disabled:opacity-50">
            {estimateLoading ? "…" : "↻ Recalcular"}
          </button>
        </div>

        {/* Iter197 · Card de coste estimado — antes de lanzar */}
        {estimate && (
          <div className="mt-3 bg-white border-2 border-emerald-300 rounded-lg p-3" data-testid="mega-enrich-cost-card">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-900">💶 Coste estimado IA de este run</span>
              <span className="text-2xl font-bold text-emerald-700 font-mono" data-testid="mega-enrich-cost-total-eur">
                {(estimate.total_eur || 0).toFixed(3)} €
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {(estimate.steps || []).filter(s => (s.eur || 0) > 0).map(s => (
                <div key={s.stage} className="bg-emerald-50 rounded p-2" data-testid={`mega-enrich-cost-${s.stage}`}>
                  <div className="text-[10px] font-semibold text-emerald-800 uppercase">{s.stage.replace(/_/g, " ")}</div>
                  <div className="flex items-baseline justify-between mt-0.5">
                    <span className="text-[10px] text-gray-500">{s.docs_to_process}/{s.candidates} docs</span>
                    <span className="text-xs font-mono font-bold text-emerald-800">{s.eur.toFixed(3)} €</span>
                  </div>
                </div>
              ))}
            </div>
            {(estimate.remaining_after_run?.geo > 0 || estimate.remaining_after_run?.enrich > 0 || estimate.remaining_after_run?.score > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900" data-testid="mega-enrich-cost-remaining">
                <b>Quedarán pendientes tras este run</b> (relanza el mega-enrich para procesarlos):{" "}
                geo <b>{estimate.remaining_after_run.geo}</b>,{" "}
                enrich <b>{estimate.remaining_after_run.enrich}</b>,{" "}
                score <b>{estimate.remaining_after_run.score}</b>
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
              {estimate.note} · Conversión USD→EUR: {estimate.usd_to_eur_rate}
            </p>
          </div>
        )}
        {megaStatus && (
          <div className="mt-3 bg-white border border-emerald-200 rounded-lg p-3" data-testid="mega-enrich-status">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-emerald-900">
                Último Mega-Enrich · <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                  megaStatus.status === "running" ? "bg-amber-100 text-amber-800"
                  : megaStatus.status === "done" ? "bg-emerald-100 text-emerald-800"
                  : "bg-gray-100 text-gray-700"
                }`}>{megaStatus.status}</span>
              </span>
              <span className="text-[10px] text-gray-500 font-mono">{(megaStatus.started_at || "").replace("T", " ").slice(0, 19)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              {["sync", "cascade", "enrich_scrape"].map(step => {
                const s = megaStatus.steps?.[step];
                const label = { sync: "1. Scrape fuentes", cascade: "2. Cascada IA", enrich_scrape: "3. Enrich-scrape" }[step];
                const bg = !s ? "bg-gray-50 text-gray-400"
                  : s.status === "ok" ? "bg-emerald-50 text-emerald-800"
                  : s.status === "running" ? "bg-amber-50 text-amber-800"
                  : "bg-rose-50 text-rose-800";
                return (
                  <div key={step} className={`rounded p-2 ${bg}`} data-testid={`mega-step-${step}`}>
                    <div className="font-semibold text-[10px]">{label}</div>
                    <div className="text-[10px] mt-0.5">{s ? s.status : "pendiente"}</div>
                  </div>
                );
              })}
            </div>
            {megaStatus.triggered_by && (
              <div className="text-[10px] text-gray-500 mt-1.5">Lanzado por {megaStatus.triggered_by}</div>
            )}
          </div>
        )}
      </div>

      {/* Iter196 · Sección B · FASES INDIVIDUALES (avanzado, para debug/mantenimiento) */}
      <div className="mb-4" data-testid="mercado-advanced-section">
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          data-testid="mercado-advanced-toggle"
          className="w-full flex items-center justify-between text-xs font-semibold text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-2">
          <span className="flex items-center gap-2">
            <ChevronDown size={12} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`}/>
            Fases individuales (avanzado · debug / mantenimiento)
          </span>
          <span className="text-[10px] text-gray-500 font-normal">Sólo si necesitas re-lanzar un paso concreto</span>
        </button>
        {showAdvanced && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50/50">
            <p className="text-[11px] text-gray-500 mb-2">Estas acciones son piezas individuales del Mega-Enrich. Úsalas sólo para re-lanzar un paso específico que ha fallado.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                onClick={() => kick("heur")}
                disabled={running}
                data-testid="mercado-geo-backfill-heur-btn"
                className="text-xs bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left">
                <b>1.</b> {running && runningKind === "heur" ? "Ejecutando…" : "Backfill geo heurístico (vacíos)"}
              </button>
              <button
                onClick={() => kick("heur-all")}
                disabled={running}
                data-testid="mercado-geo-backfill-all-btn"
                className="text-xs border border-slate-300 hover:bg-slate-50 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left">
                <b>1b.</b> {running && runningKind === "heur-all" ? "Ejecutando…" : "Re-backfill geo (TODOS los docs)"}
              </button>
              <button
                onClick={() => kick("ai")}
                disabled={running}
                data-testid="mercado-geo-backfill-ai-btn"
                title="Claude Haiku 4.5 — ~$0.0002 por doc, 200 docs máx"
                className="text-xs bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left">
                <b>2.</b> {running && runningKind === "ai" ? "Pando analizando…" : "Backfill geo IA (fallback, 200 docs)"}
              </button>
              <button
                onClick={() => kick("enrich")}
                disabled={running}
                data-testid="mercado-geo-enrich-btn"
                title="Perplexity + Claude leen la ficha oficial y rellenan título/CPV/presupuesto/etc."
                className="text-xs bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white hover:from-fuchsia-700 hover:to-pink-700 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left">
                <b>3.</b> {running && runningKind === "enrich" ? "Leyendo PLACSP…" : "Enrich PLACSP + Perplexity (10 docs)"}
              </button>
              <button
                onClick={() => kick("enrich-all")}
                disabled={running}
                data-testid="mercado-geo-enrich-all-btn"
                title="Bucle automático de lotes 10 hasta cubrir todos los pendientes (~500 docs máx por invocación)"
                className="text-xs bg-gradient-to-r from-fuchsia-700 to-purple-700 text-white hover:from-fuchsia-800 hover:to-purple-800 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left font-semibold">
                <b>3b.</b> {running && runningKind === "enrich-all"
                  ? (enrichProgress ? `${enrichProgress.updated}/${enrichProgress.done} enriched…` : "Enriqueciendo todos…")
                  : "Enrich PLACSP — TODOS los pendientes (bucle)"}
              </button>
              <button
                onClick={() => kick("score")}
                disabled={running}
                data-testid="mercado-geo-score-btn"
                title="Win probability con Perplexity + Claude — ~$0.01 por doc"
                className="text-xs bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left">
                <b>4.</b> {running && runningKind === "score" ? "Puntuando…" : "Score IA GO/NO-GO (20 docs)"}
              </button>
              <button
                onClick={() => kick("cascade")}
                disabled={running}
                data-testid="mercado-nightly-cascade-btn"
                title="Ejecuta en secuencia heurístico + Pando + enrich + score + alerta ≥80%. Igual que el cron 03:00 UTC."
                className="text-xs bg-gradient-to-r from-slate-900 to-emerald-800 text-white hover:from-slate-800 hover:to-emerald-700 disabled:opacity-50 px-3 py-1.5 rounded-lg text-left font-semibold col-span-1 md:col-span-2">
                <b>5.</b> {running && runningKind === "cascade" ? "Cascada en curso…" : "Cascada nocturna completa (sin scrape + sin enrich-scrape)"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Iter149 · Barra de progreso enrich-all (cuando aplique) */}
      {enrichProgress && (
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-2" data-testid="enrich-all-progress">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="font-semibold text-fuchsia-800">Enriqueciendo en bucle…</span>
            <span className="font-mono text-fuchsia-700">{enrichProgress.updated} actualizados / {enrichProgress.done} escaneados</span>
          </div>
          <div className="w-full bg-fuchsia-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-fuchsia-600 h-full transition-all"
              style={{ width: `${Math.min(100, (enrichProgress.done / 500) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {last && last.status !== "empty" && (
        <div className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="mercado-geo-last">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-800">Última ejecución · <code className="text-[10px] bg-gray-200 px-1 rounded">{last.kind || "backfill_geo"}</code></span>
            <span className="text-gray-500 text-[10px] font-mono">{(last.created_at || "").replace("T", " ").slice(0, 19)}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div><span className="text-gray-500">Scanned</span> <span className="font-mono font-semibold ml-1">{last.scanned ?? "—"}</span></div>
            <div><span className="text-gray-500">Actualizados</span> <span className="font-mono font-semibold ml-1 text-emerald-700">{last.updated ?? "—"}</span></div>
            <div><span className="text-gray-500">Sin resolver</span> <span className="font-mono font-semibold ml-1 text-rose-700">{last.unresolved ?? "—"}</span></div>
          </div>
          {last.triggered_by && (
            <div className="text-[10px] text-gray-500 mt-1">Lanzado por {last.triggered_by}</div>
          )}
        </div>
      )}

      {/* Iter143 · Histórico de clasificaciones DG */}
      <MercadoClassifyHistoryPanel />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter198 · AI Benchmark · Informe descargable de rendimiento IA por oferta
// ═══════════════════════════════════════════════════════════════════════════
function AIBenchmarkPanel() {
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const [l, h] = await Promise.all([
        axios.get(`${API}/admin/ai-benchmark/latest`),
        axios.get(`${API}/admin/ai-benchmark/history?limit=5`),
      ]);
      setRun(l.data && l.data.status !== "empty" ? l.data : null);
      setHistory(h.data?.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Poll cada 6s mientras un run esté corriendo
  useEffect(() => {
    if (run?.status !== "running") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [run?.status, load]);

  const start = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/ai-benchmark/run`);
      toast.success("🧪 Benchmark IA lanzado — 60-180 s. Polling activo.", { duration: 8000 });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al lanzar el benchmark");
    } finally { setRunning(false); }
  };

  const downloadReport = async (runId, format) => {
    try {
      const r = await axios.get(`${API}/admin/ai-benchmark/${runId}/report.${format}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai_benchmark_${runId.slice(0, 8)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Error descargando .${format}: ${e.response?.data?.detail || e.message}`);
    }
  };

  const totals = run?.totals || {};
  const isDone = run?.status === "done";
  const isRunning = run?.status === "running";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-4" data-testid="ai-benchmark-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            🧪 AI Benchmark · Informe descargable
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Ejecuta TODAS las herramientas IA (Compliance, Competitive, Resumen CFO, GO/NO-GO) sobre una oferta real y genera un informe
            <b> XLSX + PDF</b> con tokens, coste, latencia y KPIs de rendimiento.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ai-benchmark-refresh"
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg">↻ Refrescar</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <button
          onClick={start}
          disabled={running || isRunning}
          data-testid="ai-benchmark-launch-btn"
          title="Lanza el benchmark completo. Coste típico 0.05-0.10 €. Duración 60-180 s."
          className="text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-4 py-2 rounded-lg shadow-md disabled:opacity-50 flex items-center gap-2">
          {running || isRunning ? (<><Loader2 className="animate-spin" size={16}/> {isRunning ? "Corriendo…" : "Lanzando…"}</>) : "🚀 Lanzar benchmark IA"}
        </button>
        <span className="text-[11px] text-gray-500">Coste típico: <b>0.05-0.10 €</b> · Duración: <b>60-180 s</b> · 4 herramientas × Claude Sonnet 4.6</span>
      </div>

      {run && (
        <div className="border border-indigo-200 rounded-lg p-3 bg-gradient-to-br from-indigo-50 to-purple-50 mb-3" data-testid="ai-benchmark-current">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-semibold text-indigo-900">
                Último benchmark · <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                  isDone ? "bg-emerald-100 text-emerald-800"
                  : isRunning ? "bg-amber-100 text-amber-800 animate-pulse"
                  : "bg-gray-100 text-gray-700"
                }`} data-testid="ai-benchmark-status">{run.status}</span>
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {(run.started_at || "").replace("T", " ").slice(0, 19)} · Oferta: <b>{run.oferta_cliente}</b>
              </div>
              <div className="text-[10px] text-gray-500">{(run.oferta_objeto || "").slice(0, 140)}</div>
            </div>
            {isDone && (
              <div className="flex gap-1.5">
                <button
                  onClick={() => downloadReport(run.id, "xlsx")}
                  data-testid="ai-benchmark-download-xlsx"
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                  <Download size={12}/> XLSX
                </button>
                <button
                  onClick={() => downloadReport(run.id, "pdf")}
                  data-testid="ai-benchmark-download-pdf"
                  className="text-xs bg-rose-600 hover:bg-rose-700 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                  <Download size={12}/> PDF
                </button>
              </div>
            )}
          </div>

          {isDone && totals && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              <div className="bg-white rounded p-2 border border-indigo-200">
                <div className="text-[9px] text-gray-500 uppercase tracking-wide">Coste total</div>
                <div className="text-base font-bold text-emerald-700 font-mono" data-testid="ai-benchmark-cost">{(totals.cost_eur || 0).toFixed(4)} €</div>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-200">
                <div className="text-[9px] text-gray-500 uppercase tracking-wide">Tokens</div>
                <div className="text-base font-bold text-indigo-700 font-mono" data-testid="ai-benchmark-tokens">{(totals.tokens_total || 0).toLocaleString()}</div>
                <div className="text-[9px] text-gray-500">{totals.tokens_in?.toLocaleString()} in / {totals.tokens_out?.toLocaleString()} out</div>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-200">
                <div className="text-[9px] text-gray-500 uppercase tracking-wide">Latencia</div>
                <div className="text-base font-bold text-purple-700 font-mono" data-testid="ai-benchmark-latency">{(totals.latency_s_total || 0).toFixed(1)} s</div>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-200">
                <div className="text-[9px] text-gray-500 uppercase tracking-wide">Llamadas</div>
                <div className="text-base font-bold text-slate-700 font-mono" data-testid="ai-benchmark-calls">{totals.calls || 0}{totals.errors > 0 && <span className="text-rose-600 text-xs"> ({totals.errors} err)</span>}</div>
              </div>
            </div>
          )}

          {(run.steps || []).length > 0 && (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="ai-benchmark-steps-table">
                <thead>
                  <tr className="bg-white/60">
                    <th className="px-2 py-1 text-left">Herramienta</th>
                    <th className="px-2 py-1 text-right">Tokens in</th>
                    <th className="px-2 py-1 text-right">Tokens out</th>
                    <th className="px-2 py-1 text-right">Coste €</th>
                    <th className="px-2 py-1 text-right">Latencia ms</th>
                    <th className="px-2 py-1 text-center">OK</th>
                  </tr>
                </thead>
                <tbody>
                  {(run.steps || []).map((s, i) => (
                    <tr key={i} className="border-t border-indigo-100" data-testid={`ai-benchmark-step-${s.tool}`}>
                      <td className="px-2 py-1 font-medium">{s.title || s.tool}</td>
                      <td className="px-2 py-1 text-right font-mono">{(s.tokens_in || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-mono">{(s.tokens_out || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-mono text-emerald-700">{(s.cost_eur || 0).toFixed(4)}</td>
                      <td className="px-2 py-1 text-right font-mono">{(s.latency_ms || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-center">{s.error ? <span className="text-rose-600">✗</span> : <span className="text-emerald-600">✓</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {history.length > 1 && (
        <div>
          <div className="text-[11px] font-semibold text-gray-600 mb-1.5">Histórico (últimos {history.length})</div>
          <div className="space-y-1">
            {history.slice(1).map(h => (
              <div key={h.id} className="text-[10px] flex items-center justify-between border border-gray-100 rounded px-2 py-1 bg-gray-50" data-testid={`ai-benchmark-hist-${h.id}`}>
                <span className="text-gray-500 font-mono">{(h.started_at || "").slice(0, 19).replace("T", " ")}</span>
                <span className="text-gray-700">{h.oferta_cliente || "—"}</span>
                <span className="font-mono text-emerald-700">{(h.totals?.cost_eur || 0).toFixed(4)} €</span>
                <div className="flex gap-1">
                  {h.status === "done" && (
                    <>
                      <button onClick={() => downloadReport(h.id, "xlsx")} className="text-emerald-700 hover:underline">XLSX</button>
                      <span>·</span>
                      <button onClick={() => downloadReport(h.id, "pdf")} className="text-rose-700 hover:underline">PDF</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// CC01 · Diagnóstico de entorno / build / dataset — para no comparar PRE/PRO
// a ciegas y que cada prueba/exportación sepa qué versión y datos valida.
// ═══════════════════════════════════════════════════════════════════════════
function EnvBuildDiagnosticPanel() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/diagnostic`).then(r => setD(r.data)).catch(() => setErr(true));
  }, []);
  const fmt = (s) => s ? String(s).replace("T", " ").slice(0, 19) : "—";
  if (err) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mt-4" data-testid="env-build-diagnostic-panel">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">🧭 Diagnóstico de entorno / build / datos</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">Qué versión y qué datos valida este entorno. Al comparar con producción: no compares cantidades PRE/PRO a ciegas.</p>
      {!d ? <p className="text-xs text-gray-400">Cargando…</p> : (
        <div className="space-y-3 text-[12px]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-gray-50 rounded p-2"><div className="text-[9px] uppercase text-gray-500">Entorno</div><div className="font-bold" data-testid="diag-environment">{d.environment}</div></div>
            <div className="bg-gray-50 rounded p-2"><div className="text-[9px] uppercase text-gray-500">Build / commit</div><div className="font-mono font-bold" data-testid="diag-commit">{d.build?.commit}</div></div>
            <div className="bg-gray-50 rounded p-2"><div className="text-[9px] uppercase text-gray-500">Versión reglas</div><div className="font-bold" data-testid="diag-rules">{d.rules_version}</div></div>
            <div className="bg-gray-50 rounded p-2"><div className="text-[9px] uppercase text-gray-500">Hora servidor</div><div className="font-mono">{fmt(d.server_time)}</div></div>
          </div>
          <div>
            <div className="text-[9px] uppercase text-gray-500 mb-1">Últimos cortes de datos</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div><span className="text-gray-500">Health PLACSP:</span> <span className="font-mono">{fmt(d.data_cuts?.last_placsp_health_check)}</span></div>
              <div><span className="text-gray-500">Sync externo:</span> <span className="font-mono">{fmt(d.data_cuts?.last_external_sync)}</span></div>
              <div><span className="text-gray-500">Import comercial:</span> <span className="font-mono">{fmt(d.data_cuts?.last_comercial_import)}</span></div>
              <div><span className="text-gray-500">Oferta actualizada:</span> <span className="font-mono">{fmt(d.data_cuts?.last_oferta_updated)}</span></div>
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase text-gray-500 mb-1">Conteos del dataset (entorno actual)</div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              {Object.entries(d.dataset_counts || {}).map(([k, v]) => (
                <span key={k} className="bg-gray-100 rounded px-2 py-0.5" data-testid={`diag-count-${k}`}>{k}: <b>{v ?? "—"}</b></span>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-gray-400">{d.note}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CC03/CC06 · Reglas del motor de completitud (editables por admin): qué
// derivaciones se auto-aplican (deterministas) y cuáles se proponen a revisión.
// ═══════════════════════════════════════════════════════════════════════════
function CompletitudRulesPanel() {
  const [rules, setRules] = useState(null);
  const [meta, setMeta] = useState({});
  const load = useCallback(() => {
    axios.get(`${API}/admin/completitud-rules`)
      .then(r => { setRules(r.data.rules); setMeta({ by: r.data.updated_by, at: r.data.updated_at }); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const setMode = async (key, mode) => {
    try {
      await axios.put(`${API}/admin/completitud-rules/${key}`, { mode });
      setRules(prev => prev.map(r => r.key === key ? { ...r, mode } : r));
      toast.success("Regla actualizada");
    } catch { toast.error("No se pudo actualizar la regla"); }
  };
  if (!rules) return null;
  const KIND = { exacta: "bg-emerald-100 text-emerald-700", derivada: "bg-sky-100 text-sky-700", inferida: "bg-amber-100 text-amber-700" };
  const MODES = [["auto", "Auto-aplica"], ["propose", "Proponer"], ["off", "Desactivada"]];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mt-4" data-testid="completitud-rules-panel">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">🧩 Reglas del motor de completitud</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">Qué derivaciones se auto-aplican y cuáles se proponen para revisión humana. «Auto-aplica» escribe el dato (con auditoría); «Proponer» crea una propuesta a revisar; «Desactivada» no ejecuta la regla.</p>
      <div className="space-y-2">
        {rules.map(r => (
          <div key={r.key} className="flex items-start gap-3 border border-gray-100 rounded-lg p-2.5" data-testid={`compl-rule-${r.key}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-gray-800 flex items-center gap-2 flex-wrap">
                {r.label}
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${KIND[r.kind] || "bg-gray-100 text-gray-600"}`}>{r.kind}</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">{r.descripcion}</div>
            </div>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
              {MODES.map(([m, label]) => (
                <button key={m} onClick={() => setMode(r.key, m)} data-testid={`compl-rule-${r.key}-${m}`}
                  className={`text-[10px] font-semibold px-2 py-1 transition-colors ${r.mode === m
                    ? (m === "off" ? "bg-gray-500 text-white" : m === "auto" ? "bg-[#007934] text-white" : "bg-sky-600 text-white")
                    : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {meta.by && <p className="text-[10px] text-gray-400 mt-2">Última edición: {meta.by}{meta.at ? ` · ${String(meta.at).replace("T", " ").slice(0, 16)}` : ""}</p>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Iter200 · Health-check del feed PLACSP · detecta drift/fallo antes del sync
// ═══════════════════════════════════════════════════════════════════════════
function PLACSPHealthPanel() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, h] = await Promise.all([
        axios.get(`${API}/admin/placsp-health`),
        axios.get(`${API}/admin/placsp-health/history?limit=10`),
      ]);
      setData(l.data?.status && l.data.status !== "empty" ? l.data : null);
      setHistory(h.data?.items || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/placsp-health/check-now`);
      setData(r.data);
      toast.success(`Health-check ejecutado · status: ${r.data.status}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error ejecutando health-check");
    } finally {
      setRunning(false);
    }
  };

  const badge = (status) => {
    const cls = status === "ok" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
              : status === "warn" ? "bg-amber-100 text-amber-800 border-amber-300"
              : status === "error" ? "bg-rose-100 text-rose-800 border-rose-300"
              : "bg-gray-100 text-gray-600 border-gray-200";
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${cls}`}>{status}</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-4" data-testid="placsp-health-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">🩺 PLACSP Feed Health</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Detecta cambios de esquema, redirects HTML o fallos de red en el feed oficial de sindicación PLACSP
            <b> antes</b> de que rompa el cron <code>daily-placsp-sync</code>.
            Se ejecuta automáticamente cada día a las 06:00 UTC (1h antes del sync).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="placsp-health-refresh" className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg">↻ Refrescar</button>
          <button onClick={runNow} disabled={running} data-testid="placsp-health-check-now"
                  className="text-xs bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold px-3 py-1.5 rounded-lg disabled:opacity-50">
            {running ? <><Loader2 className="animate-spin inline" size={12}/> Comprobando…</> : "▶ Comprobar AHORA"}
          </button>
        </div>
      </div>

      {data ? (
        <div className={`rounded-lg border p-3 ${
          data.status === "ok" ? "bg-emerald-50 border-emerald-200"
          : data.status === "warn" ? "bg-amber-50 border-amber-200"
          : "bg-rose-50 border-rose-200"
        }`} data-testid="placsp-health-current">
          <div className="flex items-center gap-2 mb-2">
            <span data-testid="placsp-health-status">{badge(data.status)}</span>
            <span className="text-[11px] text-gray-500 font-mono">{(data.checked_at || "").replace("T", " ").slice(0, 19)}</span>
            <span className="text-[11px] text-gray-500 ml-auto">Mes analizado: <b>{data.ym}</b>{data.fallback_from_ym && <span> (fallback desde {data.fallback_from_ym})</span>}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mb-2">
            <div className="bg-white/70 rounded p-1.5">
              <div className="text-[9px] text-gray-500 uppercase">HTTP</div>
              <div className="font-mono font-bold" data-testid="placsp-health-http">{data.http_status ?? "—"}</div>
            </div>
            <div className="bg-white/70 rounded p-1.5">
              <div className="text-[9px] text-gray-500 uppercase">Content-Length</div>
              <div className="font-mono font-bold">{data.content_length ? `${(data.content_length/1024).toFixed(0)} KB` : "—"}</div>
            </div>
            <div className="bg-white/70 rounded p-1.5">
              <div className="text-[9px] text-gray-500 uppercase">Muestreado</div>
              <div className="font-mono font-bold">{data.downloaded_bytes ? `${(data.downloaded_bytes/1024).toFixed(0)} KB` : "—"}</div>
            </div>
            <div className="bg-white/70 rounded p-1.5">
              <div className="text-[9px] text-gray-500 uppercase">Entries sample</div>
              <div className="font-mono font-bold">{data.entries_sampled ?? 0}</div>
            </div>
          </div>
          <div className="text-[11px] space-y-1">
            <div>
              <b>Schema OK:</b> {data.schema_ok ? <span className="text-emerald-700">✓</span> : <span className="text-rose-700">✗</span>}
              <span className="ml-3"><b>Drift:</b> {data.drift ? <span className="text-amber-700 font-bold">⚠ SÍ</span> : <span className="text-emerald-700">no</span>}</span>
              <span className="ml-3 font-mono text-gray-500">{data.duration_ms} ms</span>
            </div>
            {data.missing_tags?.length > 0 && (
              <div className="text-rose-700"><b>Tags críticos faltantes:</b> <span className="font-mono text-[10px]">{data.missing_tags.join(", ")}</span></div>
            )}
            {data.unknown_namespaces?.length > 0 && (
              <div className="text-amber-700"><b>Namespaces nuevos:</b> <span className="font-mono text-[10px]">{data.unknown_namespaces.join(", ")}</span></div>
            )}
            {data.parse_error && (
              <div className="text-amber-800"><b>Parse:</b> {data.parse_error}</div>
            )}
            {data.download_error && (
              <div className="text-rose-800"><b>Descarga:</b> {data.download_error}</div>
            )}
          </div>

          {data.degraded_note && (
            <div className="mt-2 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2.5 py-1.5" data-testid="placsp-health-degraded-note">
              <b>✓ Sync operativo:</b> {data.degraded_note}
            </div>
          )}

          {data.live_feed && (
            <div className="mt-2 flex items-center gap-2 text-[11px] border rounded px-2.5 py-1.5 bg-white/70" data-testid="placsp-health-live-feed">
              <span className={`font-bold ${data.live_feed.ok ? "text-emerald-700" : "text-rose-700"}`}>
                {data.live_feed.ok ? "✓ Feed live OK" : "✗ Feed live ROTO"}
              </span>
              <span className="text-gray-600">
                {data.live_feed.entries_count ?? 0} entries · HTTP {data.live_feed.http_status ?? "—"}
                {typeof data.live_feed.duration_ms === "number" ? ` · ${data.live_feed.duration_ms} ms` : ""}
              </span>
              {data.live_feed.error && <span className="text-rose-700 ml-1">{data.live_feed.error}</span>}
              <span className="ml-auto text-[10px] text-gray-400">fuente principal del sync</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-400 text-xs py-6 border border-dashed border-gray-200 rounded-lg">
          Sin health-checks aún. Pulsa <b>Comprobar AHORA</b> para lanzar el primero.
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-gray-600 mb-1">Histórico</div>
          <div className="space-y-1">
            {history.slice(0, 8).map(h => (
              <div key={h.id} className="text-[10px] flex items-center gap-2 border border-gray-100 rounded px-2 py-1 bg-gray-50">
                {badge(h.status)}
                <span className="font-mono text-gray-500">{(h.checked_at || "").slice(0, 19).replace("T", " ")}</span>
                <span className="text-gray-600">{h.ym}</span>
                <span className="text-gray-500 ml-auto">HTTP {h.http_status ?? "—"} · {h.entries_sampled ?? 0} entries · {h.duration_ms} ms</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter143 · Panel de histórico de clasificaciones DG (CPV vs Pando)
// ═══════════════════════════════════════════════════════════════════════════
function MercadoClassifyHistoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);   // Iter146 · row seleccionada
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mercado/admin/classify-log?limit=20`);
      setData(r.data);
    } catch { /* no-op */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (row) => {
    setOpenRow(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/mercado/admin/classify-log/${encodeURIComponent(row.created_at)}/docs`);
      setDetail(r.data);
    } catch (e) {
      toast.error("No se pudo cargar detalle: " + (e.response?.data?.detail || e.message));
    } finally { setDetailLoading(false); }
  };

  return (
    <div className="mt-3 border-t border-gray-200 pt-3" data-testid="mercado-classify-history">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1">🏷️ Clasificaciones DG (últimos 30 d)</h4>
          <p className="text-[10px] text-gray-500">Historial de las últimas 20 ejecuciones + agregados por método (CPV rápido vs Pando Claude Haiku).</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/mercado/admin/classify-log/export.xlsx`}
            data-testid="mercado-classify-export-xlsx"
            title="Descargar histórico completo (últimas 1000 clasificaciones) en Excel"
            className="text-[10px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded flex items-center gap-1">
            📥 Excel
          </a>
          <button
            onClick={load}
            data-testid="mercado-classify-history-refresh"
            className="text-[10px] text-blue-600 hover:underline">
            Refrescar
          </button>
        </div>
      </div>

      {loading && <div className="text-[11px] text-gray-500">Cargando…</div>}

      {!loading && data && (
        <>
          {/* Totales 30d */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="bg-slate-50 border border-slate-200 rounded p-2" data-testid="mercado-classify-total-cpv">
              <div className="text-[10px] text-slate-500 uppercase">CPV (sin coste)</div>
              <div className="text-sm font-bold text-slate-800">
                {data.totals_30d?.cpv?.classified ?? 0} <span className="text-[10px] font-normal text-slate-500">clasificados</span>
              </div>
              <div className="text-[10px] text-slate-400">
                {data.totals_30d?.cpv?.runs ?? 0} runs · {data.totals_30d?.cpv?.scanned ?? 0} scanned
              </div>
            </div>
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-2" data-testid="mercado-classify-total-ai">
              <div className="text-[10px] text-fuchsia-600 uppercase">Pando · Claude Haiku</div>
              <div className="text-sm font-bold text-fuchsia-800">
                {data.totals_30d?.ai?.classified ?? 0} <span className="text-[10px] font-normal text-fuchsia-500">clasificados</span>
              </div>
              <div className="text-[10px] text-fuchsia-400">
                {data.totals_30d?.ai?.runs ?? 0} runs · {data.totals_30d?.ai?.scanned ?? 0} scanned · ~${((data.totals_30d?.ai?.classified ?? 0) * 0.0002).toFixed(3)} coste est.
              </div>
            </div>
          </div>

          {/* Tabla últimas ejecuciones */}
          {(!data.recent || data.recent.length === 0) ? (
            <div className="text-[11px] text-slate-400 italic">Sin ejecuciones registradas todavía.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="mercado-classify-history-table">
                <thead className="bg-slate-100 text-slate-600 uppercase text-[9px]">
                  <tr>
                    <th className="text-left px-2 py-1">Fecha</th>
                    <th className="text-left px-2 py-1">Método</th>
                    <th className="text-right px-2 py-1">Scanned</th>
                    <th className="text-right px-2 py-1">OK</th>
                    <th className="text-left px-2 py-1">Usuario</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.recent.map((r, i) => (
                    <tr
                      key={i}
                      onClick={() => openDetail(r)}
                      data-testid={`mercado-classify-row-${i}`}
                      title="Ver docs clasificados en esta ejecución"
                      className="cursor-pointer hover:bg-indigo-50/60">
                      <td className="px-2 py-1 font-mono text-slate-600">{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="px-2 py-1">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${r.kind === "classify_dg_ai" ? "bg-fuchsia-100 text-fuchsia-700" : "bg-slate-100 text-slate-700"}`}>
                          {r.kind === "classify_dg_ai" ? "Pando" : "CPV"}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-slate-600">{r.scanned ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700">{r.classified ?? 0}</td>
                      <td className="px-2 py-1 text-slate-500 truncate max-w-[120px]" title={r.triggered_by}>{r.triggered_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Iter146 · Modal detalle de docs clasificados en la ejecución seleccionada */}
      {openRow && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setOpenRow(null)}
          data-testid="mercado-classify-detail-modal">
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${openRow.kind === "classify_dg_ai" ? "bg-fuchsia-100 text-fuchsia-700" : "bg-slate-100 text-slate-700"}`}>
                    {openRow.kind === "classify_dg_ai" ? "Pando · Claude Haiku" : "CPV (mapa catálogo)"}
                  </span>
                  Ejecución {(openRow.created_at || "").replace("T", " ").slice(0, 19)}
                </h4>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Lanzada por <b>{openRow.triggered_by || "—"}</b> · {openRow.scanned} escaneados · <span className="text-emerald-700 font-bold">{openRow.classified} clasificados</span>
                </div>
              </div>
              <button onClick={() => setOpenRow(null)} data-testid="mercado-classify-detail-close" className="text-2xl text-slate-400 hover:text-slate-700">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {detailLoading && <div className="text-sm text-slate-500 italic">Cargando docs…</div>}
              {!detailLoading && detail && (
                (!detail.docs || detail.docs.length === 0) ? (
                  <div className="text-sm text-slate-500 italic">Sin docs registrados en esta ejecución (histórico antiguo).</div>
                ) : (
                  <table className="w-full text-[11px]" data-testid="mercado-classify-detail-table">
                    <thead className="bg-slate-50 text-[9px] uppercase text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1">Título / Objeto</th>
                        <th className="text-left px-2 py-1">Cliente</th>
                        <th className="text-left px-2 py-1">CCAA</th>
                        <th className="text-left px-2 py-1">DG</th>
                        <th className="text-right px-2 py-1">Presup.</th>
                        <th className="text-center px-2 py-1">Ir</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detail.docs.map((d) => (
                        <tr key={d.id} data-testid={`mercado-classify-detail-row-${d.id}`}>
                          <td className="px-2 py-1 text-slate-800 truncate max-w-[280px]" title={d.titulo_ia || d.objeto_contrato}>
                            {(d.titulo_ia || d.objeto_contrato || "—").slice(0, 100)}
                          </td>
                          <td className="px-2 py-1 text-slate-600 truncate max-w-[160px]">{d.cliente || "—"}</td>
                          <td className="px-2 py-1 text-slate-600">{d.comunidad_autonoma || d.provincia || "—"}</td>
                          <td className="px-2 py-1">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${d.direccion_general === "SERVICIOS" ? "bg-emerald-100 text-emerald-700" : d.direccion_general === "TRATAMIENTO" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                              {d.direccion_general || "OTRO"}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-right font-mono text-slate-600">
                            {d.presupuesto ? `${Math.round(d.presupuesto / 1000)}k€` : "—"}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <a
                              href={`/ofertas-comercial?mercado_id=${d.id}`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`mercado-classify-detail-link-${d.id}`}
                              className="text-indigo-600 hover:underline text-[10px]">
                              MERCADO ↗
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VortalSessionPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [scraping, setScraping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vortal/session/status`);
      setStatus(r.data);
    } catch (e) {
      setStatus({ status: "error", message: e.response?.data?.detail || "Error" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await axios.post(`${API}/vortal/session/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Sesión VORTAL subida correctamente");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const scrapeNow = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/vortal/scrape-now`);
      toast.success("Extracción VORTAL iniciada · se integrará en MERCADO en 1-2 min");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setScraping(false); }
  };

  const deleteSession = async () => {
    if (!window.confirm("¿Borrar la sesión VORTAL guardada?")) return;
    await axios.delete(`${API}/vortal/session`);
    toast.success("Sesión borrada");
    load();
  };

  const downloadScript = async () => {
    try {
      const r = await axios.get(`${API}/vortal/bootstrap-script`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/x-python" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "bootstrap_vortal_session.py";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Script descargado · ejecútalo localmente con `python bootstrap_vortal_session.py`");
    } catch {
      toast.error("Error al descargar");
    }
  };

  const ok     = status?.status === "ok";
  const stale  = status?.status === "stale";
  const miss   = status?.status === "missing";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="vortal-panel">
      <div className="flex items-center gap-2 mb-1">
        <Globe size={16} className="text-purple-700"/>
        <h3 className="font-bold text-gray-900 font-['Outfit']">VORTAL · Sesión RPA</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        VORTAL protege su login con <strong>reCAPTCHA v2</strong>, bloqueando el scraping directo.
        Workaround: el admin hace login manualmente UNA vez con el script que te descargamos,
        y la plataforma reutiliza esa sesión para extraer licitaciones diariamente.
      </p>

      <div className={`rounded-lg border p-3 mb-3 ${
        ok ? "bg-emerald-50 border-emerald-200" :
        stale ? "bg-amber-50 border-amber-200" :
        "bg-gray-50 border-gray-200"
      }`} data-testid="vortal-status">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${ok ? "bg-emerald-500" : stale ? "bg-amber-500" : "bg-gray-400"}`}/>
          <p className="text-sm font-semibold">
            {loading ? "Cargando…" :
             ok ? "Sesión VORTAL activa" :
             stale ? "Sesión antigua — conviene renovar" :
             miss ? "No hay sesión guardada" :
             (status?.message || "Desconocido")}
          </p>
        </div>
        {status?.age_hours != null && (
          <p className="text-xs text-gray-600 mt-1">Edad: {status.age_hours}h (expira típicamente a los ~168h)</p>
        )}
      </div>

      <ol className="space-y-2 text-xs text-gray-700 mb-4">
        <li className="flex gap-2"><span className="font-bold">1.</span>
          <span>Descarga el script bootstrap:
            <button onClick={downloadScript} className="ml-2 text-indigo-600 hover:underline" data-testid="vortal-download-script">bootstrap_vortal_session.py</button>
          </span>
        </li>
        <li className="flex gap-2"><span className="font-bold">2.</span>
          <span>Ejecuta <code className="bg-gray-100 px-1 rounded">python bootstrap_vortal_session.py</code> localmente. Se abre un Chromium: haz login manualmente (incluido el reCAPTCHA). Al terminar, cierra la consola.</span>
        </li>
        <li className="flex gap-2"><span className="font-bold">3.</span>
          <span>Sube el <code className="bg-gray-100 px-1 rounded">vortal_storage_state.json</code> generado:
            <label className="ml-2 inline-block cursor-pointer text-indigo-600 hover:underline">
              {uploading ? "Subiendo…" : "Seleccionar fichero"}
              <input type="file" accept="application/json" className="hidden" onChange={upload} disabled={uploading} data-testid="vortal-upload"/>
            </label>
          </span>
        </li>
      </ol>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={scrapeNow}
          disabled={!ok && !stale || scraping}
          className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-40"
          data-testid="vortal-scrape-btn">
          {scraping ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
          Extraer ahora desde VORTAL
        </button>
        {(ok || stale) && (
          <button onClick={deleteSession} className="text-xs border border-rose-200 text-rose-600 hover:bg-rose-50 px-3 py-1.5 rounded-lg" data-testid="vortal-delete">Borrar sesión</button>
        )}
        <button onClick={load} className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg">Refrescar estado</button>
      </div>
    </div>
  );
}


function AnthropicUsagePanel() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecent, setShowRecent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/anthropic-usage/summary`);
      setData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el uso Pando");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecent = async () => {
    try {
      const r = await axios.get(`${API}/admin/anthropic-usage/recent?limit=20`);
      setRecent(r.data || []);
      setShowRecent(true);
    } catch {
      toast.error("No se pudieron cargar las últimas llamadas");
    }
  };

  // Timeseries semanal (12 semanas por defecto) para el gráfico de evolución
  const [timeseries, setTimeseries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/anthropic-usage/timeseries?weeks=12`)
      .then(r => setTimeseries(r.data?.series || []))
      .catch(() => setTimeseries([]));
  }, []);

  // Top prompts por ahorro (90 días por defecto)
  const [topPrompts, setTopPrompts] = useState([]);
  const [topPromptsLoading, setTopPromptsLoading] = useState(false);
  const [topPromptsDays, setTopPromptsDays] = useState(90);
  const loadTopPrompts = useCallback(async (days = topPromptsDays) => {
    setTopPromptsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/anthropic-usage/top-prompts?days=${days}&limit=10`);
      setTopPrompts(r.data?.rows || []);
    } catch {
      setTopPrompts([]);
    } finally {
      setTopPromptsLoading(false);
    }
  }, [topPromptsDays]);
  useEffect(() => { loadTopPrompts(topPromptsDays); }, [topPromptsDays, loadTopPrompts]);

  useEffect(() => { load(); }, [load]);

  const fmtN = (n) => new Intl.NumberFormat("es-ES").format(n || 0);
  const fmtEur = (n) => `€${(n || 0).toFixed(4)}`;

  const KpiCard = ({ label, value, sub, testId, colorClasses }) => (
    <div className={`rounded-lg border p-3 ${colorClasses}`} data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-0.5 font-['Outfit']">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );

  const s = data || {};
  const today = s.today || {};
  const d7    = s.last_7d  || {};
  const d30   = s.last_30d || {};
  const total = s.total    || {};
  const daily = s.daily    || [];
  const topModels = s.top_models || [];
  const maxDaily = Math.max(1, ...daily.map(d => d.cost_eur || 0));

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="anthropic-usage-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-teal-700"/>
          <h3 className="font-bold text-gray-900 font-['Outfit']">ANTHROPIC · Consumo de tu API Key propia</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
          data-testid="anthropic-usage-refresh">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
          Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Las llamadas a Claude (Score Pando, Análisis competitivo, Resúmenes Comité, etc.) están enrutadas a
        tu <strong>ANTHROPIC_API_KEY</strong> nativa — sin consumir la Universal Key. Coste estimado a precio público × EUR.
        <span className="block mt-1 text-amber-700">⚠ Este panel NO incluye el consumo de análisis IA de Licitaciones
        (checklist, GO/NO-GO, extracción JSON), que usa la Universal Key y se ve en la pestaña <strong>"Historial IA · Licitaciones"</strong>.
        Son dos contadores independientes — no deberían coincidir.</span>
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Hoy"         value={fmtEur(today.cost_eur)} sub={`${fmtN(today.calls)} llamadas · ${fmtN((today.tokens_in||0)+(today.tokens_out||0))} tokens`} testId="kpi-today"  colorClasses="bg-teal-50 border-teal-100"/>
        <KpiCard label="Últimos 7d"  value={fmtEur(d7.cost_eur)}    sub={`${fmtN(d7.calls)} llamadas`} testId="kpi-7d"     colorClasses="bg-indigo-50 border-indigo-100"/>
        <KpiCard label="Últimos 30d" value={fmtEur(d30.cost_eur)}   sub={`${fmtN(d30.calls)} llamadas`} testId="kpi-30d"    colorClasses="bg-violet-50 border-violet-100"/>
        <KpiCard label="Total"       value={fmtEur(total.cost_eur)} sub={`${fmtN(total.calls)} llamadas · ${fmtN((total.tokens_in||0)+(total.tokens_out||0))} tokens`} testId="kpi-total"  colorClasses="bg-slate-50 border-slate-100"/>
      </div>

      {/* Cache savings YTD — destacado en verde */}
      {(total.savings_eur != null) && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-4" data-testid="cache-savings-widget">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-emerald-700"/>
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  Prompt caching · Ahorro YTD
                </p>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-emerald-900" data-testid="cache-savings-eur">
                  {fmtEur(total.savings_eur)}
                </p>
                {total.savings_pct > 0 && (
                  <span className="text-sm font-bold text-emerald-700">
                    -{total.savings_pct?.toFixed?.(1) ?? total.savings_pct}%
                  </span>
                )}
              </div>
              <p className="text-[11px] text-emerald-700 mt-1">
                vs coste nominal {fmtEur((total.cost_eur||0) + (total.savings_eur||0))} sin cache
              </p>
            </div>
            <div className="text-right text-[11px] text-emerald-700 grid grid-cols-2 gap-x-4 gap-y-0.5">
              <span className="font-bold">Cache writes:</span><span>{fmtN(total.cache_creation||0)} tok</span>
              <span className="font-bold">Cache reads:</span><span>{fmtN(total.cache_read||0)} tok</span>
              <span className="font-bold">Hit ratio:</span>
              <span>
                {(total.cache_creation + total.cache_read) > 0
                  ? `${((total.cache_read / (total.cache_creation + total.cache_read)) * 100).toFixed(1)}%`
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de evolución semanal del ahorro */}
      {timeseries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4" data-testid="cache-savings-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-bold text-gray-900 font-['Outfit']">Evolución semanal · Coste vs Ahorro</h4>
              <p className="text-[10px] text-gray-500">Últimas 12 semanas · datos reales del log de Anthropic</p>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-sm bg-slate-700 inline-block"/>Coste real
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block"/>Ahorro cache
              </span>
            </div>
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <ComposedChart data={timeseries} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false}
                  tickFormatter={(v) => `€${v.toFixed(2)}`}/>
                <ChartTooltip
                  contentStyle={{ fontSize: 11, padding: 8, borderRadius: 6, border: "1px solid #E5E7EB" }}
                  formatter={(v, name) => [`€${Number(v).toFixed(4)}`, name]}
                  labelStyle={{ fontWeight: 700, color: "#0F1922" }}
                />
                <Area type="monotone" dataKey="savings_eur" name="Ahorro cache" fill="#10B981" stroke="#10B981" fillOpacity={0.25}/>
                <Line type="monotone" dataKey="cost_eur" name="Coste real" stroke="#0F1922" strokeWidth={2} dot={{ r: 3 }}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {/* Mini-resumen al pie */}
          <div className="flex items-center justify-around mt-2 pt-2 border-t border-gray-100 text-[10px]">
            <div className="text-center">
              <p className="text-gray-500">Coste 12 sem</p>
              <p className="font-bold text-slate-800">€{timeseries.reduce((a, w) => a + (w.cost_eur || 0), 0).toFixed(2)}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500">Ahorro 12 sem</p>
              <p className="font-bold text-emerald-700">€{timeseries.reduce((a, w) => a + (w.savings_eur || 0), 0).toFixed(2)}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500">Llamadas</p>
              <p className="font-bold text-slate-800">{fmtN(timeseries.reduce((a, w) => a + (w.calls || 0), 0))}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500">Última semana</p>
              <p className="font-bold text-slate-800">
                €{(timeseries.at(-1)?.cost_eur || 0).toFixed(4)}
                {(timeseries.at(-1)?.savings_pct > 0) && (
                  <span className="ml-1 text-emerald-700">-{timeseries.at(-1).savings_pct.toFixed(1)}%</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {daily.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Coste diario (últimos 14d)</p>
          <div className="flex items-end gap-1 h-20 bg-gray-50 rounded-lg p-2" data-testid="anthropic-daily-chart">
            {daily.map((d) => {
              const h = Math.max(2, Math.round(((d.cost_eur || 0) / maxDaily) * 64));
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date} · ${fmtEur(d.cost_eur)} · ${d.calls} llamadas`}>
                  <div className="w-full bg-teal-500 hover:bg-teal-600 rounded-sm transition-all" style={{ height: `${h}px` }}/>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-400">
            <span>{daily[0]?.date || ""}</span>
            <span>{daily[daily.length - 1]?.date || ""}</span>
          </div>
        </div>
      )}

      {topModels.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Top modelos (30d)</p>
          <div className="space-y-1" data-testid="anthropic-top-models">
            {topModels.map((m) => (
              <div key={m.model} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                <code className="text-gray-700">{m.model || "—"}</code>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500">{fmtN(m.calls)} llamadas</span>
                  <span className="font-semibold text-teal-700">{fmtEur(m.cost_eur)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top prompts por ahorro (prompt caching) */}
      <div className="mb-4" data-testid="top-prompts-savings-panel">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles size={13} className="text-emerald-600"/>
            <p className="text-xs font-semibold text-gray-700">Top prompts por ahorro · Prompt caching</p>
          </div>
          <div className="flex items-center gap-1">
            {[30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setTopPromptsDays(d)}
                className={`text-[10px] px-2 py-0.5 rounded-md border ${topPromptsDays === d
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                data-testid={`top-prompts-range-${d}`}>
                {d === 365 ? "1a" : `${d}d`}
              </button>
            ))}
            <button
              onClick={() => loadTopPrompts(topPromptsDays)}
              disabled={topPromptsLoading}
              className="text-[10px] border border-gray-200 hover:bg-gray-50 px-2 py-0.5 rounded-md inline-flex items-center gap-1 ml-1"
              data-testid="top-prompts-refresh">
              {topPromptsLoading ? <Loader2 size={10} className="animate-spin"/> : <RefreshCw size={10}/>}
            </button>
          </div>
        </div>

        {topPrompts.length === 0 ? (
          <div className="text-center text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-lg">
            {topPromptsLoading
              ? "Cargando…"
              : "Aún no hay prompts con caché registrados en este rango."}
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-lg" data-testid="top-prompts-table">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500">
                  <th className="px-2 py-1.5 font-semibold">Prompt</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Llamadas</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Cache writes</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Cache reads</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Hit ratio</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Coste</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Ahorro</th>
                  <th className="px-2 py-1.5 font-semibold">Último uso</th>
                </tr>
              </thead>
              <tbody>
                {topPrompts.map((p) => (
                  <tr
                    key={p.prompt_ref}
                    className="border-t border-gray-100 hover:bg-gray-50/60"
                    data-testid={`top-prompt-row-${p.prompt_ref}`}>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate max-w-[220px] font-medium text-gray-800" title={p.name}>
                          {p.name}
                        </span>
                        {p.is_user_defined ? (
                          <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">
                            User
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-100">
                            Preset
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{fmtN(p.calls)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">{fmtN(p.cache_creation)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-semibold">{fmtN(p.cache_read)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        p.hit_ratio >= 70 ? "bg-emerald-100 text-emerald-800"
                        : p.hit_ratio >= 40 ? "bg-amber-100 text-amber-800"
                        : "bg-gray-100 text-gray-600"
                      }`}>
                        {p.hit_ratio?.toFixed?.(1) ?? p.hit_ratio}%
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{fmtEur(p.cost_eur)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-700">
                      {fmtEur(p.savings_eur)}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 text-[10px]">
                      {(p.last_used || "").replace("T", " ").slice(0, 16)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-emerald-50/60 border-t border-emerald-100">
                <tr className="font-semibold text-emerald-900">
                  <td className="px-2 py-1.5">Total ({topPrompts.length} prompts)</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtN(topPrompts.reduce((a, p) => a + (p.calls || 0), 0))}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtN(topPrompts.reduce((a, p) => a + (p.cache_creation || 0), 0))}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtN(topPrompts.reduce((a, p) => a + (p.cache_read || 0), 0))}
                  </td>
                  <td className="px-2 py-1.5"/>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtEur(topPrompts.reduce((a, p) => a + (p.cost_eur || 0), 0))}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums" data-testid="top-prompts-total-savings">
                    {fmtEur(topPrompts.reduce((a, p) => a + (p.savings_eur || 0), 0))}
                  </td>
                  <td className="px-2 py-1.5"/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div>
        <button
          onClick={loadRecent}
          className="text-xs text-indigo-600 hover:underline"
          data-testid="anthropic-show-recent">
          {showRecent ? "Recargar últimas 20 llamadas" : "Ver últimas 20 llamadas"}
        </button>
        {showRecent && recent.length > 0 && (
          <div className="mt-3 max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-gray-500">
                  <th className="px-2 py-1.5 font-semibold">Fecha</th>
                  <th className="px-2 py-1.5 font-semibold">Modelo</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Tok In</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Tok Out</th>
                  <th className="px-2 py-1.5 font-semibold text-right">€</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-2 py-1 text-gray-600">{(r.ts || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-2 py-1"><code className="text-[10px]">{(r.model || "").replace("claude-", "")}</code></td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtN(r.tokens_in)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtN(r.tokens_out)}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-teal-700">{fmtEur(r.cost_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(!data || total.calls === 0) && !loading && (
        <div className="text-center text-xs text-gray-500 py-4 border border-dashed border-gray-200 rounded-lg mt-3">
          Aún no se han registrado llamadas desde la activación del tracking. Lanza un Score Pando en Mercado o Licitaciones y vuelve aquí.
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// R24 (criterios 2026-10-11) · Tablero de precios IA — vista visual del
// catálogo único versionado (core.ai_models.MODEL_CATALOG) y de qué
// modelos realmente usados en la plataforma no están catalogados.
// ════════════════════════════════════════════════════════════════════════════
function AiPricingCatalogPanel() {
  const [catalog, setCatalog] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/ai-usage/pricing-catalog`),
        axios.get(`${API}/admin/ai-usage/pricing-coverage`),
      ]);
      setCatalog(r1.data);
      setCoverage(r2.data);
    } catch (e) {
      toast.error("No se pudo cargar el catálogo de precios IA");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const live = (catalog?.catalog || []).filter(c => c.status === "vivo");
  const legacy = (catalog?.catalog || []).filter(c => c.status === "retirado");
  const [showLegacy, setShowLegacy] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="ai-pricing-catalog-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-emerald-700" />
          <h3 className="font-bold text-gray-900 font-['Outfit']">Catálogo de precios IA · fuente única</h3>
        </div>
        <button onClick={load} disabled={loading} data-testid="ai-pricing-catalog-refresh"
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Un único catálogo versionado (<code>core/ai_models.py</code>) alimenta todos los cálculos de coste de la plataforma —
        antes había 3 copias manuales que había que &quot;recordar sincronizar&quot;. Un modelo sin precio catalogado nunca cae
        silenciosamente al precio de otro modelo: se marca explícitamente como N/D.
      </p>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : (
        <>
          {coverage?.has_uncovered && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-xs text-red-700 flex items-start gap-2" data-testid="ai-pricing-uncovered-banner">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Modelos usados sin precio catalogado (coste N/D)</p>
                <p className="mt-0.5">{coverage.uncovered_nd.map(m => `${m.model} (${m.calls} llamadas)`).join(", ")}</p>
              </div>
            </div>
          )}

          <table className="w-full text-xs mb-4" data-testid="ai-pricing-catalog-table">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100">
                <th className="py-2 font-semibold">Modelo</th>
                <th className="py-2 font-semibold">Proveedor</th>
                <th className="py-2 font-semibold text-right">Precio IN / Mtok</th>
                <th className="py-2 font-semibold text-right">Precio OUT / Mtok</th>
                <th className="py-2 font-semibold">Versión</th>
                <th className="py-2 font-semibold">Vigente desde</th>
                <th className="py-2 font-semibold">Fuente</th>
                <th className="py-2 font-semibold text-right">Llamadas reales</th>
              </tr>
            </thead>
            <tbody>
              {live.map(m => {
                const usage = coverage?.covered?.find(c => c.model === m.model || c.model.startsWith(m.model));
                return (
                  <tr key={m.model} className="border-b border-gray-50" data-testid={`ai-pricing-row-${m.model}`}>
                    <td className="py-2">
                      <p className="font-semibold text-gray-800">{m.label}</p>
                      <p className="text-gray-400 font-mono text-[10px]">{m.model}{m.default && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold">default</span>}</p>
                    </td>
                    <td className="py-2 text-gray-600">{m.provider}</td>
                    <td className="py-2 text-right font-mono text-gray-700">${m.in_usd_per_mtok.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono text-gray-700">${m.out_usd_per_mtok.toFixed(2)}</td>
                    <td className="py-2 text-gray-500">v{m.pricing_version}</td>
                    <td className="py-2 text-gray-500">{m.effective_from}</td>
                    <td className="py-2 text-gray-400 text-[10px] max-w-[180px] truncate" title={m.pricing_source}>{m.pricing_source}</td>
                    <td className="py-2 text-right font-semibold text-gray-700">{usage ? new Intl.NumberFormat("es-ES").format(usage.calls) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <button onClick={() => setShowLegacy(v => !v)} data-testid="ai-pricing-toggle-legacy"
            className="text-[11px] text-gray-400 hover:text-gray-600 mb-2">
            {showLegacy ? "Ocultar" : "Mostrar"} modelos retirados ({legacy.length})
          </button>
          {showLegacy && (
            <table className="w-full text-xs opacity-70" data-testid="ai-pricing-legacy-table">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="py-2 font-semibold">Modelo</th>
                  <th className="py-2 font-semibold">Proveedor</th>
                  <th className="py-2 font-semibold text-right">Precio IN / Mtok</th>
                  <th className="py-2 font-semibold text-right">Precio OUT / Mtok</th>
                  <th className="py-2 font-semibold" colSpan={2}>Vigente desde</th>
                  <th className="py-2 font-semibold">Fuente</th>
                  <th className="py-2 font-semibold text-right">Llamadas reales</th>
                </tr>
              </thead>
              <tbody>
                {legacy.map(m => {
                  const usage = coverage?.covered?.find(c => c.model === m.model || c.model.startsWith(m.model));
                  return (
                    <tr key={m.model} className="border-b border-gray-50" data-testid={`ai-pricing-row-${m.model}`}>
                      <td className="py-2">
                        <p className="font-medium text-gray-600">{m.label}</p>
                        <p className="text-gray-400 font-mono text-[10px]">{m.model}</p>
                      </td>
                      <td className="py-2 text-gray-500">{m.provider}</td>
                      <td className="py-2 text-right font-mono text-gray-500">${m.in_usd_per_mtok.toFixed(2)}</td>
                      <td className="py-2 text-right font-mono text-gray-500">${m.out_usd_per_mtok.toFixed(2)}</td>
                      <td className="py-2 text-gray-400" colSpan={2}>—</td>
                      <td className="py-2 text-gray-400 text-[10px] max-w-[180px] truncate" title={m.pricing_source}>{m.pricing_source}</td>
                      <td className="py-2 text-right font-medium text-gray-500">{usage ? new Intl.NumberFormat("es-ES").format(usage.calls) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function InternalHoursRatesPanel() {
  const [defaultRate, setDefaultRate] = useState(65);
  const [overrides, setOverrides] = useState([]); // [{provider, rate}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/offers/internal-hours-rates`);
      setDefaultRate(Number(data.default_rate || 65));
      const arr = Object.entries(data.provider_overrides || {}).map(([provider, rate]) => ({ provider, rate: Number(rate) }));
      setOverrides(arr.length > 0 ? arr : [
        { provider: "MOBA", rate: 65 },
        { provider: "Distromel", rate: 75 },
      ]);
    } catch (e) {
      toast.error("No se pudieron cargar las tasas IT: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateOverride = (idx, field, value) => {
    setOverrides(prev => prev.map((o, i) => i === idx ? { ...o, [field]: field === "rate" ? Number(value) : value } : o));
  };
  const addOverride = () => setOverrides(prev => [...prev, { provider: "", rate: 65 }]);
  const removeOverride = (idx) => setOverrides(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    const provider_overrides = {};
    for (const o of overrides) {
      if (o.provider && o.provider.trim() && o.rate > 0) {
        provider_overrides[o.provider.trim()] = Number(o.rate);
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/offers/internal-hours-rates`, {
        default_rate: Number(defaultRate),
        provider_overrides,
      });
      toast.success("Tasas IT guardadas correctamente");
      load();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="internal-hours-rates-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <Clock size={18} className="text-indigo-600"/>
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 font-['Outfit'] text-base">Tasas horarias internas IT · Análisis de Ofertas TIC</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Tarifa €/h que se aplica automáticamente al inyectar las secciones <strong>Horas internas IT</strong> tras un análisis Pando.
            Puedes fijar una tarifa por defecto y overrides por proveedor (ej. MOBA 65 €/h, Distromel 75 €/h).
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide min-w-[150px]">Tarifa por defecto</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min="0" step="1"
                value={defaultRate}
                onChange={e => setDefaultRate(e.target.value)}
                className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                data-testid="internal-hours-default-rate"
              />
              <span className="text-sm text-gray-500">€/h</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Overrides por proveedor</p>
              <button
                onClick={addOverride}
                className="text-xs text-indigo-600 hover:underline font-semibold"
                data-testid="internal-hours-add-override">
                + Añadir proveedor
              </button>
            </div>
            <div className="space-y-2">
              {overrides.length === 0 && (
                <p className="text-xs text-gray-400 italic">No hay overrides. Se usará la tarifa por defecto para todos.</p>
              )}
              {overrides.map((o, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`internal-hours-override-${i}`}>
                  <input
                    type="text"
                    placeholder="Proveedor (ej. MOBA, Distromel)"
                    value={o.provider}
                    onChange={e => updateOverride(i, "provider", e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                  />
                  <input
                    type="number" min="0" step="1"
                    value={o.rate}
                    onChange={e => updateOverride(i, "rate", e.target.value)}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                  />
                  <span className="text-sm text-gray-500">€/h</span>
                  <button
                    onClick={() => removeOverride(i)}
                    className="text-gray-400 hover:text-red-500"
                    title="Eliminar este override">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#006027] disabled:opacity-50 flex items-center gap-2"
              data-testid="internal-hours-save">
              {saving ? <><Loader2 size={14} className="animate-spin"/> Guardando…</> : <><CheckCircle size={14}/> Guardar tasas</>}
            </button>
            <p className="text-[11px] text-gray-400">Se aplica en el próximo análisis Pando. Las secciones auto-creadas se regeneran al regenerar el análisis.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// WOW Display · público con token embebido para pantallas Valoriza
// ════════════════════════════════════════════════════════════════════════════
function WowDisplayPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/wow/config`);
      setCfg(data);
    } catch (e) {
      toast.error("WOW config: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/wow/config`, {
        title: cfg.title, rotation_seconds: cfg.rotation_seconds,
      });
      setCfg(data);
      toast.success("Config WOW guardada");
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const rotateToken = async () => {
    if (!window.confirm("¿Rotar el token? Las pantallas embebidas dejarán de funcionar hasta que las actualices con el nuevo token.")) return;
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/admin/wow/rotate-token`);
      setCfg(c => ({ ...c, token: data.token }));
      toast.success("Nuevo token generado");
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setRotating(false); }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = cfg?.token ? `${origin}/wow?token=${encodeURIComponent(cfg.token)}` : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado al portapapeles"));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="wow-display-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-50 to-amber-50 flex items-center justify-center flex-shrink-0">
          <Sparkles size={18} className="text-[#007934]"/>
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 font-['Outfit'] text-base">WOW Display · Adjudicaciones para pantallas Valoriza</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            URL pública con token embebido para mostrar en pantallas de oficinas. Modo carrusel automático con KPIs, mapa nacional, sectores y últimas adjudicaciones.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
      ) : cfg ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Título del display</label>
              <input
                value={cfg.title || ""}
                onChange={e => setCfg({ ...cfg, title: e.target.value })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                data-testid="wow-title"
                maxLength={80}/>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Rotación entre slides (segundos)</label>
              <input
                type="number" min="5" max="120"
                value={cfg.rotation_seconds || 18}
                onChange={e => setCfg({ ...cfg, rotation_seconds: Number(e.target.value) })}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                data-testid="wow-rotation"/>
            </div>
          </div>

          {/* URL embebida */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">URL pública (compartir con TVs / pantallas)</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 truncate" title={fullUrl}>
                {fullUrl}
              </code>
              <button
                onClick={() => copy(fullUrl)}
                className="text-xs border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 font-semibold"
                data-testid="wow-copy-url">
                Copiar
              </button>
              <a
                href={fullUrl}
                target="_blank" rel="noopener noreferrer"
                className="text-xs bg-[#007934] text-white px-3 py-2 rounded-lg hover:bg-[#006027] font-semibold"
                data-testid="wow-preview">
                Vista previa
              </a>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Abre la URL en una pantalla en modo kiosko (Chrome --kiosk) o como pestaña pantalla completa (F11).
              Los datos refrescan automáticamente cada 5 minutos.
            </p>
          </div>

          {/* Token + acciones */}
          <div className="flex items-center justify-between gap-2 bg-amber-50/40 border border-amber-100 rounded-lg p-3">
            <div className="text-xs text-amber-900">
              <strong>Token actual:</strong> <code className="font-mono ml-1">{(cfg.token || "").slice(0, 6)}…{(cfg.token || "").slice(-6)}</code>
              <p className="text-[10px] mt-0.5">Si rotas el token, las pantallas embebidas dejan de funcionar hasta que las actualices.</p>
            </div>
            <button
              onClick={rotateToken}
              disabled={rotating}
              className="text-xs border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 px-3 py-2 rounded-lg font-semibold disabled:opacity-50"
              data-testid="wow-rotate-token">
              {rotating ? <Loader2 size={11} className="animate-spin inline"/> : "Rotar token"}
            </button>
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#006027] disabled:opacity-50 flex items-center gap-2"
            data-testid="wow-save">
            {saving ? <><Loader2 size={14} className="animate-spin"/> Guardando…</> : <><CheckCircle size={14}/> Guardar configuración</>}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// AvatarUploader — sube avatar para usuarios (uso en lista admin)
// ════════════════════════════════════════════════════════════════════════════
function AvatarUploader({ user, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("La imagen supera 1 MB. Redúcela antes de subir.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/users/${user.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Avatar de ${user.name} actualizado`);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className="relative w-9 h-9 group cursor-pointer flex-shrink-0"
      onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
      title="Pulsa para cambiar avatar"
      data-testid={`avatar-uploader-${user.id}`}>
      {user.avatar ? (
        <img src={user.avatar} alt={user.name}
             className="w-9 h-9 rounded-full object-cover ring-1 ring-gray-200"/>
      ) : (
        <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-xs font-bold ring-1 ring-gray-200">
          {user.name?.slice(0, 2).toUpperCase() || "??"}
        </div>
      )}
      <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
        {uploading ? <Loader2 size={12} className="animate-spin text-white"/> : <UserCheck size={12} className="text-white"/>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        className="hidden"
        onChange={onChange}/>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// NameAliasesEditor — gestiona aliases para matching VSM → user.email
// ════════════════════════════════════════════════════════════════════════════
function NameAliasesEditor({ user, onSaved }) {
  const [aliases, setAliases] = useState(user.name_aliases || []);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAliases(user.name_aliases || []); }, [user.name_aliases]);

  const add = () => {
    const v = draft.trim();
    if (!v || aliases.includes(v)) return;
    setAliases([...aliases, v]);
    setDraft("");
  };

  const remove = (i) => setAliases(prev => prev.filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${user.id}/name-aliases`, { aliases });
      toast.success(`Aliases de ${user.name} actualizados (${aliases.length})`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100" data-testid={`aliases-editor-${user.id}`}>
      <div className="flex-shrink-0 w-32">
        <p className="text-xs text-gray-500 font-semibold mt-1.5">Aliases de nombre</p>
        <p className="text-[10px] text-gray-400 mt-0.5">Para mapear VSM → email en Hall of Fame.</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {aliases.length === 0 && <span className="text-[11px] text-gray-400 italic">Sin aliases. Se usa solo el nombre exacto.</span>}
          {aliases.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-[#E8F5E9] text-[#007934] border border-[#007934]/20 rounded-md px-2 py-0.5 text-xs font-medium">
              {a}
              <button onClick={(e) => { e.stopPropagation(); remove(i); }} className="hover:text-red-500" title="Quitar">
                <X size={11}/>
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-1.5">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="ej. T. Brage · Tere Brage"
            className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#007934]"
          />
          <button
            onClick={(e) => { e.stopPropagation(); add(); }}
            className="text-xs border border-gray-200 px-2 py-1 rounded hover:bg-gray-50 font-semibold">
            + Añadir
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); save(); }}
            disabled={saving}
            className="text-xs bg-[#007934] text-white px-2 py-1 rounded font-semibold hover:bg-[#006027] disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin"/> : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenAICompatQuotasPanel() {
  const [quotas, setQuotas] = useState([]);
  const [daily, setDaily] = useState({ users: [], series: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {email, monthly_token_limit, rpm_limit, notes}
  const [creating, setCreating] = useState(false);

  const [bucket, setBucket] = useState("day"); // Iter160 · toggle día/hora

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const days = bucket === "hour" ? 7 : 30;
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/openai-compat/quotas`),
        axios.get(`${API}/admin/openai-compat/daily-usage?bucket=${bucket}&days=${days}`),
      ]);
      setQuotas(r1.data || []);
      setDaily(r2.data || { users: [], series: [] });
    } catch (e) {
      toast.error("No se pudieron cargar las cuotas");
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      await axios.put(`${API}/admin/openai-compat/quotas`, form);
      toast.success("Cuota guardada");
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (email) => {
    if (!confirm(`¿Borrar la cuota personalizada de ${email}? Volverá a los defaults globales (1M tokens/mes, 60 rpm)`)) return;
    await axios.delete(`${API}/admin/openai-compat/quotas/${encodeURIComponent(email)}`);
    toast.success("Cuota borrada");
    load();
  };

  const fmtN = (n) => new Intl.NumberFormat("es-ES").format(n || 0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="openai-quotas-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-indigo-700"/>
          <h3 className="font-bold text-gray-900 font-['Outfit']">OPENAI-COMPAT · Cuotas y consumo por cuenta</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading}
            className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
            data-testid="quotas-refresh">
            {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            Refrescar
          </button>
          <button onClick={() => { setCreating(true); setEditing({ email: "", monthly_token_limit: 1000000, rpm_limit: 60, notes: "" }); }}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
            data-testid="quotas-add">
            <Plus size={12}/> Nueva cuenta
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Cuotas mensuales de tokens y rate limit (RPM) para el endpoint <code>/api/openai-compat/v1/chat/completions</code>.
        Sin cuota personalizada se aplican defaults: <strong>1M tokens/mes · 60 rpm</strong>. La barra muestra % consumido del mes en curso.
      </p>

      <DailyUsageChart daily={daily} bucket={bucket} onBucketChange={setBucket} />

      {quotas.length === 0 && !loading && (
        <div className="text-center text-xs text-gray-500 py-6 border border-dashed border-gray-200 rounded-lg">
          Aún no hay cuotas personalizadas configuradas. Crea una cuenta para tu proveedor de middleware.
        </div>
      )}

      {quotas.length > 0 && (
        <div className="space-y-2" data-testid="quotas-list">
          {quotas.map((q) => {
            const pct = q.pct_used || 0;
            const colorBar = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-indigo-500";
            return (
              <div key={q.email} className="border border-gray-100 rounded-lg p-3 hover:border-indigo-200 transition" data-testid={`quota-${q.email}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-sm text-gray-900">{q.email}</p>
                    {q.notes && <p className="text-[11px] text-gray-500">{q.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(q)}
                      className="text-xs border border-gray-200 hover:bg-gray-50 px-2 py-1 rounded inline-flex items-center gap-1"
                      data-testid={`edit-${q.email}`}>
                      <Edit2 size={11}/> Editar
                    </button>
                    <button onClick={() => remove(q.email)}
                      className="text-xs border border-rose-200 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded inline-flex items-center gap-1"
                      data-testid={`delete-${q.email}`}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex justify-between text-[11px] text-gray-600 mb-1">
                    <span>{fmtN(q.consumption?.tokens_total)} / {fmtN(q.monthly_token_limit)} tokens</span>
                    <span className="font-bold">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded overflow-hidden">
                    <div className={`h-full ${colorBar} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }}/>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] mt-2">
                  <div>
                    <p className="text-gray-500 uppercase tracking-wider text-[10px]">Llamadas mes</p>
                    <p className="font-bold text-gray-900">{fmtN(q.consumption?.calls)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wider text-[10px]">Tokens IN</p>
                    <p className="font-bold text-gray-900">{fmtN(q.consumption?.tokens_in)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wider text-[10px]">Tokens OUT</p>
                    <p className="font-bold text-gray-900">{fmtN(q.consumption?.tokens_out)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wider text-[10px]">Coste mes</p>
                    <p className="font-bold text-indigo-700">€{(q.consumption?.cost_eur || 0).toFixed(4)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">RPM límite: {q.rpm_limit}/min</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal edit/create */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="quota-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 font-['Outfit']">
                {creating ? "Nueva cuenta de servicio" : "Editar cuota"}
              </h3>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="text-gray-400 hover:text-gray-700">
                <X size={18}/>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email cuenta de servicio</label>
                <input type="email" value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  disabled={!creating}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 disabled:bg-gray-100"
                  placeholder="middleware@partner.com"
                  data-testid="quota-email"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Cuota mensual de tokens</label>
                <input type="number" value={editing.monthly_token_limit} min="0"
                  onChange={(e) => setEditing({ ...editing, monthly_token_limit: parseInt(e.target.value) || 0 })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                  data-testid="quota-monthly"/>
                <p className="text-[10px] text-gray-500 mt-0.5">Suma de tokens IN+OUT del mes en curso. Ej: 1.000.000</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Rate limit (peticiones/min)</label>
                <input type="number" value={editing.rpm_limit} min="1" max="10000"
                  onChange={(e) => setEditing({ ...editing, rpm_limit: parseInt(e.target.value) || 60 })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                  data-testid="quota-rpm"/>
                <p className="text-[10px] text-gray-500 mt-0.5">Sliding window 60s. Ej: 60</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Notas internas</label>
                <input type="text" value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="Proveedor middleware Pando · contrato 2026..."
                  data-testid="quota-notes"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setEditing(null); setCreating(false); }}
                className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg">
                Cancelar
              </button>
              <button onClick={() => save(editing)}
                disabled={!editing.email}
                className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="quota-save">
                Guardar cuota
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function DailyUsageChart({ daily, bucket, onBucketChange }) {
  const { users = [], series = [] } = daily || {};
  const isHour = bucket === "hour";
  // Paleta para hasta 8 cuentas (luego se repite con desfase de tono)
  const PALETTE = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#3B82F6", "#A855F7", "#14B8A6", "#F97316"];
  const colorFor = (i) => PALETTE[i % PALETTE.length];

  const bucketToggle = (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="chart-bucket-toggle">
      <button
        onClick={() => onBucketChange("day")}
        className={`px-2 py-1 text-[10px] font-semibold ${bucket === "day" ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        data-testid="chart-bucket-day">Diario · 30d</button>
      <button
        onClick={() => onBucketChange("hour")}
        className={`px-2 py-1 text-[10px] font-semibold ${bucket === "hour" ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        data-testid="chart-bucket-hour">Horario · 7d</button>
    </div>
  );

  if (users.length === 0 || series.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4" data-testid="daily-usage-chart">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-700">Consumo por cuenta</p>
          {bucketToggle}
        </div>
        <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center text-xs text-gray-400" data-testid="daily-chart-empty">
          Sin datos de consumo en el rango seleccionado.
        </div>
      </div>
    );
  }

  // Dimensiones SVG
  const W = 800, H = 220, PAD_L = 50, PAD_R = 12, PAD_T = 14, PAD_B = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const allValues = [];
  series.forEach(s => users.forEach(u => allValues.push(s.values?.[u] || 0)));
  const maxY = Math.max(1, ...allValues);
  const xStep = series.length > 1 ? innerW / (series.length - 1) : innerW;

  const fmtAxisY = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M`
                       : n >= 1_000     ? `${Math.round(n/1_000)}k`
                       : `${n}`;

  // Etiqueta X: para día = "MM-DD", para hora = "DD HH:00"
  const fmtX = (key) => {
    if (!key) return "";
    if (isHour) {
      // key = "2026-07-24T14:00" → "24 14h"
      const [d, h] = key.split("T");
      const day = d?.slice(8) || "";
      const hh = (h || "00").slice(0, 2);
      return `${day} ${hh}h`;
    }
    return key.slice(5); // "MM-DD"
  };

  // Líneas Y de referencia (5)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => ({
    y: PAD_T + innerH * (1 - p),
    label: fmtAxisY(maxY * p),
  }));

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4" data-testid="daily-usage-chart">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs font-semibold text-gray-700">
          Consumo por cuenta · {isHour ? `últimos ${daily.days || 7} días por hora (${series.length} pts)` : `últimos ${daily.days || 30} días (tokens)`}
        </p>
        <div className="flex items-center gap-3">
          {bucketToggle}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]" data-testid="chart-legend">
            {users.map((u, i) => (
              <span key={u} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: colorFor(i) }}/>
                <span className="text-gray-700 font-mono">{u}</span>
              </span>
          ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-52" data-testid="chart-svg">
          {/* Grid horizontal */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={t.y} y2={t.y} stroke="#E5E7EB" strokeDasharray={i === 0 ? "0" : "3 3"}/>
              <text x={PAD_L - 6} y={t.y + 3} fontSize="9" textAnchor="end" fill="#9CA3AF">{t.label}</text>
            </g>
          ))}

          {/* X axis labels */}
          {series.map((s, i) => {
            const targetTicks = isHour ? 8 : 6;
            if (series.length <= targetTicks || i % Math.ceil(series.length / targetTicks) === 0 || i === series.length - 1) {
              const x = PAD_L + i * xStep;
              return (
                <text key={i} x={x} y={H - PAD_B + 14} fontSize="9" textAnchor="middle" fill="#9CA3AF">
                  {fmtX(s.date)}
                </text>
              );
            }
            return null;
          })}

          {/* Líneas por usuario */}
          {users.map((u, idx) => {
            const linePath = series.map((s, i) => {
              const v = s.values?.[u] || 0;
              const x = PAD_L + i * xStep;
              const y = PAD_T + innerH * (1 - v / maxY);
              return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
            }).join(" ");
            // Iter160 · Área bajo la línea para mejor lectura visual
            const areaPath = linePath +
              ` L ${(PAD_L + (series.length - 1) * xStep).toFixed(1)} ${(PAD_T + innerH).toFixed(1)}` +
              ` L ${PAD_L.toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;
            const c = colorFor(idx);
            return (
              <g key={u}>
                <path d={areaPath} fill={c} fillOpacity="0.10"/>
                <path d={linePath} stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                {series.map((s, i) => {
                  const v = s.values?.[u] || 0;
                  if (v === 0) return null;
                  const x = PAD_L + i * xStep;
                  const y = PAD_T + innerH * (1 - v / maxY);
                  return (
                    <circle key={i} cx={x} cy={y} r="2.2" fill={c}>
                      <title>{`${u}\n${fmtX(s.date)}: ${new Intl.NumberFormat("es-ES").format(v)} tokens`}</title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}


function SyncEnvPanel() {
  const [whitelist, setWhitelist] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastApplyResult, setLastApplyResult] = useState(null);
  const [identity, setIdentity] = useState(null);
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/admin/sync/whitelist`),
        axios.get(`${API}/admin/sync/applied-history?limit=10`),
        axios.get(`${API}/admin/sync/environment-identity`),
      ]);
      setWhitelist(r1.data.whitelist || {});
      setHistory(r2.data || []);
      setIdentity(r3.data || null);
    } catch (e) {
      // Silent; el panel sigue siendo usable
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadDump = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/sync/dump`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `valoriza_sync_${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Dump descargado · ${Object.values(r.data.counts || {}).reduce((a, b) => a + (b > 0 ? b : 0), 0)} docs`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando dump");
    } finally { setDownloading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!confirm(`¿Aplicar el contenido de "${f.name}" en este entorno?\n\nLas colecciones whitelisted se actualizarán con upsert (no se borra nada).`)) {
      e.target.value = "";
      return;
    }
    setApplying(true);
    setLastApplyResult(null);
    try {
      const text = await f.text();
      const json = JSON.parse(text);
      json.confirm = true;
      try {
        const r = await axios.post(`${API}/admin/sync/apply`, json);
        setLastApplyResult(r.data);
        toast.success("Sincronización aplicada con éxito");
        load();
      } catch (err) {
        // R22 (criterios 2026-10-11) · el backend bloquea si el entorno de
        // destino no está verificado o coincide con el de origen. Se ofrece
        // forzar explícitamente en vez de fallar en silencio.
        const detail = err.response?.data?.detail || "";
        if (err.response?.status === 409 && confirm(`${detail}\n\n¿Forzar de todos modos?`)) {
          json.force = true;
          const r2 = await axios.post(`${API}/admin/sync/apply`, json);
          setLastApplyResult(r2.data);
          toast.success("Sincronización aplicada (forzada)");
          load();
        } else {
          throw err;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Error: ${err.message}`);
    } finally {
      setApplying(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const collKeys = Object.keys(whitelist);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="sync-env-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ArrowRightLeft size={16} className="text-purple-700"/>
          <h3 className="font-bold text-gray-900 font-['Outfit']">SYNC · Replicar datos entre Preview y Producción</h3>
        </div>
        <button onClick={load} disabled={loading}
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
          Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Patrón <strong>dump → apply</strong> seguro: en el entorno origen descargas un JSON, en el destino lo subes.
        Sólo se aplican colecciones whitelisted (no se borra nada · upsert idempotente). Las apps_settings y los IDs específicos están preconfigurados.
      </p>

      {/* R22 (criterios 2026-10-11) · identidad del ENTORNO ACTUAL (este
          servidor), calculada en servidor — no un texto fijo de frontend. */}
      {identity && (
        <div className={`mb-4 rounded-lg p-3 text-xs flex items-center gap-2 flex-wrap ${identity.verified ? "bg-blue-50 border border-blue-200" : "bg-amber-50 border border-amber-200"}`} data-testid="sync-env-identity-banner">
          <span className={`font-bold px-2 py-0.5 rounded-full ${identity.verified ? "bg-blue-600 text-white" : "bg-amber-500 text-white"}`}>
            Este entorno: {identity.entorno === "preview" ? "Preview" : "Sin señal de preview"}
          </span>
          {identity.dataset_identity && (
            <span className="text-gray-500 font-mono">BD: {identity.dataset_identity}</span>
          )}
          {/* P02 (roadmap-valoriza-revision-despliegue-2026-10-12, E13) ·
              revisión git realmente corriendo en este proceso, para que
              cada ticket cerrado pueda citar la revisión desplegada. */}
          {identity.commit_sha_short && (
            <span className="text-gray-500 font-mono" data-testid="deployed-revision" title={identity.commit_sha}>
              rev: {identity.commit_sha_short} · {identity.commit_date ? new Date(identity.commit_date).toLocaleString("es-ES") : "—"}
            </span>
          )}
          {identity.explicacion && (
            <span className={identity.verified ? "text-blue-700" : "text-amber-700"}>{identity.explicacion}</span>
          )}
          {!identity.verified && (
            <span className="text-amber-700">El apply de sync se bloqueará salvo que se fuerce explícitamente.</span>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="border border-purple-200 bg-purple-50/40 rounded-lg p-4">
          <p className="text-xs font-bold text-purple-900 uppercase tracking-wider mb-1">1. Exportar (origen)</p>
          <p className="text-xs text-gray-600 mb-3">
            Descarga un JSON con todas las colecciones whitelisted del entorno actual.
          </p>
          <button onClick={downloadDump} disabled={downloading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="sync-download-btn">
            {downloading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            {downloading ? "Generando dump..." : "Descargar dump JSON"}
          </button>
        </div>

        <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-4">
          <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider mb-1">2. Importar (destino)</p>
          <p className="text-xs text-gray-600 mb-3">
            Sube el JSON descargado en otro entorno. Se aplicará con confirmación.
          </p>
          <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={onFile} data-testid="sync-file-input"/>
          <button onClick={() => fileInputRef.current?.click()} disabled={applying}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="sync-upload-btn">
            {applying ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {applying ? "Aplicando..." : "Subir y aplicar JSON"}
          </button>
        </div>
      </div>

      {/* Resultado del último apply */}
      {lastApplyResult && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50 rounded-lg p-3">
          <p className="text-xs font-bold text-emerald-900 mb-2">✓ Sync aplicado · {(lastApplyResult.applied_at || "").slice(0, 19)}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {Object.entries(lastApplyResult.summary || {}).map(([coll, info]) => (
              <div key={coll} className="bg-white border border-gray-100 rounded px-2 py-1.5">
                <p className="font-mono text-gray-700">{coll}</p>
                {info.skipped !== undefined ? (
                  <p className="text-gray-400">{info.skipped} skipped ({info.reason})</p>
                ) : (
                  <p className="text-gray-500">
                    <span className="text-emerald-700 font-semibold">{info.upserted || 0}</span> nuevos ·
                    <span className="text-blue-700 font-semibold ml-1">{info.modified || 0}</span> modificados
                    {info.errors ? <span className="text-rose-600 ml-1">· {info.errors} errores</span> : null}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Colecciones whitelisted */}
      <details className="mb-4">
        <summary className="text-xs font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
          Ver colecciones whitelisted ({collKeys.length})
        </summary>
        <div className="mt-2 space-y-1 text-xs" data-testid="sync-whitelist-list">
          {collKeys.map(k => (
            <div key={k} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1">
              <code className="text-gray-700">{k}</code>
              <span className="text-gray-400">→</span>
              <span className="text-gray-600">
                {whitelist[k] === null ? "todos los docs" : `${(whitelist[k] || []).length} ids específicos`}
              </span>
            </div>
          ))}
        </div>
      </details>

      {/* Historial */}
      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-2">Histórico de syncs aplicados (últimos {history.length})</p>
          <div className="space-y-1 text-xs max-h-40 overflow-y-auto" data-testid="sync-history">
            {history.map((h, i) => (
              <div key={i} className="bg-gray-50 rounded p-2 border border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{(h.ts || "").replace("T", " ").slice(0, 19)}</span>
                  <span className="text-gray-500">por {h.user_email}</span>
                </div>
                <p className="text-gray-500 text-[10px] mt-0.5">
                  {Object.entries(h.summary || {}).map(([k, v]) =>
                    `${k}: ${v.upserted || 0}+${v.modified || 0}`).join(" · ")}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// TENDER KEYWORDS PANEL — gestión de palabras clave de detección de licitaciones
// ═══════════════════════════════════════════════════════════════════════════
function TenderKeywordsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [showBase, setShowBase] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/tender-keywords`);
      setData(d);
    } catch (e) {
      toast.error("Error cargando keywords");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const keywords = input.split(/[,;\n]+/).map(k => k.trim()).filter(Boolean);
    if (!keywords.length) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tender-keywords`, { keywords });
      toast.success(`${keywords.length} keyword(s) añadida(s)`);
      setInput("");
      load();
    } catch (e) {
      toast.error("Error añadiendo keywords");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (kw) => {
    try {
      await axios.delete(`${API}/admin/tender-keywords/${encodeURIComponent(kw)}`);
      toast.success(`"${kw}" eliminada`);
      load();
    } catch (e) {
      toast.error("Error eliminando keyword");
    }
  };

  return (
    <div data-testid="tender-keywords-panel" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Tag size={18} color="#7c3aed" />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
          Palabras clave de detección de licitaciones
        </h3>
        {data && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: 99 }}>
            {data.total} total
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
        Estas palabras se usan como <strong>segunda red</strong> en el scraper diario PLACSP: si una licitación tiene un CPV incorrecto o sin CPV, pero su título contiene alguna de estas palabras, se incluye igualmente en la detección.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><Loader2 size={20} className="animate-spin" /></div>
      ) : (
        <>
          {/* Custom keywords */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Palabras personalizadas ({data?.custom?.length || 0})
            </p>
            {data?.custom?.length === 0 ? (
              <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>Sin palabras personalizadas todavía.</p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(data?.custom || []).map(kw => (
                  <span key={kw} data-testid={`keyword-custom-${kw}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#ede9fe", color: "#5b21b6", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                    {kw}
                    <button onClick={() => handleDelete(kw)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 0, color: "#7c3aed" }} title="Eliminar">
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Add new */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <input
              data-testid="tender-keywords-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="Añadir keyword (comas para múltiples: SAIH, SAICA, BIOMETANIZACIÓN)"
              style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }}
            />
            <button
              data-testid="tender-keywords-add-btn"
              onClick={handleAdd}
              disabled={saving || !input.trim()}
              style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, opacity: saving || !input.trim() ? 0.6 : 1 }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Añadir
            </button>
          </div>

          {/* Base keywords (collapsible) */}
          <button
            onClick={() => setShowBase(v => !v)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4, padding: 0, marginBottom: showBase ? 8 : 0 }}>
            <ChevronDown size={14} style={{ transform: showBase ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            {showBase ? "Ocultar" : "Ver"} palabras clave base predefinidas ({data?.base?.length || 0})
          </button>
          {showBase && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {(data?.base || []).map(kw => (
                <span key={kw} style={{ background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 99, fontSize: 11 }}>
                  {kw}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Iter217 · TenderCpvsPanel · CPVs personalizados (extra sobre TARGET_CPV_CODES)
// ════════════════════════════════════════════════════════════════════════════
function TenderCpvsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [showBase, setShowBase] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/tender-cpvs`);
      setData(d);
    } catch (e) {
      toast.error("Error cargando CPVs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const cpvs = input.split(/[,;\s\n]+/).map(k => k.trim()).filter(Boolean);
    if (!cpvs.length) return;
    setSaving(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/tender-cpvs`, { cpvs });
      if ((r.added || []).length) toast.success(`${r.added.length} CPV(s) añadido(s)`);
      if ((r.invalid || []).length) toast.error(`CPVs inválidos ignorados: ${r.invalid.join(", ")}`);
      setInput("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error añadiendo CPVs");
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (cpv) => {
    try {
      await axios.delete(`${API}/admin/tender-cpvs/${encodeURIComponent(cpv)}`);
      toast.success(`${cpv} eliminado`);
      load();
    } catch (e) {
      toast.error("Error eliminando CPV");
    }
  };

  return (
    <div data-testid="tender-cpvs-panel" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Tag size={18} color="#0ea5e9" />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
          Códigos CPV monitorizados
        </h3>
        {data && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: 99 }}>
            {data.total} total
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
        CPVs base + personalizados que el scraper diario PLACSP filtra. Cada oferta con al menos <strong>1 CPV coincidente</strong> se incluye. Añade nuevos si un cliente publica con un CPV atípico (ej. Coslada 45316210).
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><Loader2 size={20} className="animate-spin" /></div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              CPVs personalizados ({data?.custom?.length || 0})
              {(data?.custom_stats || []).some(s => s.stale_30d) && (
                <span style={{ marginLeft: 8, fontSize: 10, color: "#dc2626", background: "#fee2e2", padding: "2px 8px", borderRadius: 99, fontWeight: 700 }}
                      data-testid="cpv-stale-warning">
                  ⚠ {(data.custom_stats).filter(s => s.stale_30d).length} sin hits en 30 días
                </span>
              )}
            </p>
            {data?.custom?.length === 0 ? (
              <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>Sin CPVs personalizados todavía.</p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(data?.custom_stats || []).map(s => {
                  const stale = s.stale_30d;
                  const lastLabel = s.last_hit_at
                    ? new Date(s.last_hit_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" })
                    : "nunca";
                  return (
                    <span
                      key={s.cpv}
                      data-testid={`cpv-custom-${s.cpv}`}
                      title={stale
                        ? `⚠ Sin hits en los últimos 30 días. Último hit: ${lastLabel}. Posible CPV mal informado o extinto.`
                        : `${s.hits} hits · último ${lastLabel}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: stale ? "#fee2e2" : "#e0f2fe",
                        color: stale ? "#991b1b" : "#075985",
                        border: stale ? "1px solid #fca5a5" : "1px solid transparent",
                        padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600, fontFamily: "monospace"
                      }}>
                      {stale && <AlertTriangle size={11} />}
                      {s.cpv}
                      <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 500 }}>· {s.hits} hits</span>
                      <button onClick={() => handleDelete(s.cpv)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 0, color: stale ? "#991b1b" : "#0369a1" }} title="Eliminar">
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <input
              data-testid="tender-cpvs-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="8 dígitos (ej. 45316210, 90513100). Separa con comas para múltiples."
              style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", fontFamily: "monospace" }}
            />
            <button
              data-testid="tender-cpvs-add-btn"
              onClick={handleAdd}
              disabled={saving || !input.trim()}
              style={{ background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, opacity: saving || !input.trim() ? 0.6 : 1 }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Añadir
            </button>
          </div>
          <button
            onClick={() => setShowBase(v => !v)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4, padding: 0, marginBottom: showBase ? 8 : 0 }}>
            <ChevronDown size={14} style={{ transform: showBase ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            {showBase ? "Ocultar" : "Ver"} CPVs base predefinidos ({data?.base?.length || 0})
          </button>
          {showBase && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {(data?.base || []).map(c => (
                <span key={c} style={{ background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontFamily: "monospace" }}>{c}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Iter217 · PlacspSyncStatsPanel · histórico ZIP vs Live feed
// ════════════════════════════════════════════════════════════════════════════
function PlacspSyncStatsPanel() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [catching, setCatching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/placsp-sync-stats?limit=15`);
      setRuns(data.runs || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleManualSync = async () => {
    if (!window.confirm(
      "Vas a lanzar un sync PLACSP AHORA (fuera del cron 07:00 UTC). " +
      "Tarda ~1-3 minutos. Se descargará el ZIP mensual + feed live y se " +
      "aplicará la watchlist ampliada. ¿Continuar?"
    )) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/tender360/m0/integrations/placsp/sync?months_back=1`);
      toast.success(`Sync lanzado (${data.triggered_by}). Consulta la tabla en 1-3 min.`);
      // Recargar en 15s para captar el nuevo run
      setTimeout(load, 15000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error lanzando sync");
    } finally { setSyncing(false); }
  };

  const handleRetroactive = async () => {
    if (!window.confirm(
      "El cazador retroactivo re-analizará las oportunidades auto-descartadas " +
      "de los últimos 180 días con la watchlist CPV actual. Las que ahora " +
      "SÍ mapean a Valoriza se reactivarán como PROPUESTA_OPORTUNIDAD. ¿Continuar?"
    )) return;
    setCatching(true);
    try {
      // Primero dry-run para mostrar preview
      const { data: preview } = await axios.post(`${API}/mercado/admin/retroactive-catch?days_back=180&dry_run=true`);
      if (preview.reactivated === 0) {
        toast.info(`Sin candidatos: ${preview.checked} auto-descartadas revisadas, ninguna coincide con la watchlist actual.`);
        return;
      }
      if (!window.confirm(`Se reactivarán ${preview.reactivated} oportunidades (de ${preview.checked} revisadas). ¿Confirmas?`)) return;
      const { data } = await axios.post(`${API}/mercado/admin/retroactive-catch?days_back=180&dry_run=false`);
      toast.success(`✓ ${data.reactivated} oportunidades reactivadas. Aparecerán en Mercado > Propuestas.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en cazador retroactivo");
    } finally { setCatching(false); }
  };

  const totalZip  = runs.reduce((s, r) => s + (r.zip_relevant || 0), 0);
  const totalLive = runs.reduce((s, r) => s + (r.live_added_after_dedupe || 0), 0);
  const totalNew  = runs.reduce((s, r) => s + (r.total_persisted_new || 0), 0);

  return (
    <div data-testid="placsp-sync-stats-panel" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <RefreshCw size={18} color="#059669" />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
          Sync PLACSP · ZIP mensual vs Live feed
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button
            onClick={handleManualSync}
            disabled={syncing}
            data-testid="placsp-sync-now-btn"
            style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: syncing ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: syncing ? 0.7 : 1 }}
            title="Ejecuta el sync PLACSP en el momento con la watchlist actual (sin esperar al cron 07:00 UTC)">
            {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {syncing ? "Sincronizando…" : "Sync ahora"}
          </button>
          <button
            onClick={handleRetroactive}
            disabled={catching}
            data-testid="mercado-retroactive-btn"
            style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: catching ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: catching ? 0.7 : 1 }}
            title="Re-analiza auto-descartadas de los últimos 180 días con la watchlist actual y reactiva las que ahora sí encajan">
            {catching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            {catching ? "Cazando…" : "Cazador retroactivo"}
          </button>
          <button onClick={load} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer", color: "#374151" }}>
            Recargar
          </button>
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
        Cada ejecución del cron 07:00 UTC registra cuántas ofertas relevantes vinieron del <strong>ZIP mensual</strong> vs cuántas se añadieron desde el <strong>Live feed</strong> (después de deduplicar). Usa <strong>Sync ahora</strong> para forzar una ejecución fuera del cron y <strong>Cazador retroactivo</strong> para reactivar auto-descartadas históricas con la watchlist ampliada.
      </p>

      {/* Totales agregados */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#166534", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>ZIP mensual (∑)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#166534", fontFamily: "monospace" }}>{totalZip}</div>
        </div>
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#1e40af", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Live feed añadió (∑)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1e40af", fontFamily: "monospace" }}>{totalLive}</div>
        </div>
        <div style={{ background: "#fdf4ff", border: "1px solid #f5d0fe", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#86198f", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Persistidas nuevas (∑)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#86198f", fontFamily: "monospace" }}>{totalNew}</div>
        </div>
      </div>

      {/* Tabla de ejecuciones */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><Loader2 size={20} className="animate-spin" /></div>
      ) : runs.length === 0 ? (
        <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", textAlign: "center", padding: 20 }}>
          Sin ejecuciones registradas todavía. El cron corre a las 07:00 UTC diariamente.
        </p>
      ) : (
        <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Fecha</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#166534" }}>ZIP</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#1e40af" }}>Live+ded</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#86198f" }}>Nuevas</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#6b7280" }}>Trigger</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} data-testid={`placsp-run-${r.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "6px 10px", color: "#374151", fontFamily: "monospace" }}>{String(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#166534", fontFamily: "monospace" }}>{r.zip_relevant ?? 0}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#1e40af", fontFamily: "monospace" }}>{r.live_added_after_dedupe ?? 0}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#86198f", fontFamily: "monospace", fontWeight: 600 }}>{r.total_persisted_new ?? 0}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#9ca3af", fontSize: 11 }}>{String(r.triggered_by || "").slice(0, 24)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Iter237 · MongoHealthPanel · latencia Atlas p50/p95/p99 + tamaño colecciones
// ════════════════════════════════════════════════════════════════════════════
function MongoHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [samples, setSamples] = useState(10);

  const load = async (n = samples) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mongo-health?samples=${n}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cargando mongo-health");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(samples); /* eslint-disable-next-line */ }, []);

  const latencyColor = (ms) => {
    if (ms == null || ms < 0) return "text-gray-400";
    if (ms < 30)  return "text-emerald-600";
    if (ms < 100) return "text-amber-600";
    return "text-rose-600";
  };

  const fmtCount = (n) => (n || 0).toLocaleString("es-ES");

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4" data-testid="mongo-health-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold font-['Outfit'] text-gray-900">MongoDB Atlas · Health</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Latencia en tiempo real + tamaño de colecciones clave</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={samples} onChange={e => { const n = parseInt(e.target.value); setSamples(n); load(n); }}
                  className="border rounded px-2 py-1 text-xs" data-testid="mongo-health-samples">
            <option value="5">5 pings</option>
            <option value="10">10 pings</option>
            <option value="20">20 pings</option>
            <option value="30">30 pings</option>
          </select>
          <button onClick={() => load()} className="bg-[#007934] text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-[#006528]"
                  disabled={loading} data-testid="mongo-health-refresh">
            {loading ? "..." : "Recargar"}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center text-gray-400 text-sm py-6">Midiendo latencia…</div>
      ) : !data ? (
        <div className="text-center text-gray-400 text-sm py-6">Sin datos</div>
      ) : (
        <>
          {/* Latencia grid */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {[
              { k: "p50_ms",  label: "P50" },
              { k: "p95_ms",  label: "P95" },
              { k: "p99_ms",  label: "P99" },
              { k: "avg_ms",  label: "Media" },
              { k: "min_ms",  label: "Min" },
              { k: "max_ms",  label: "Max" },
            ].map(({ k, label }) => (
              <div key={k} className="border border-gray-100 rounded-lg p-2 text-center" data-testid={`mongo-health-${k}`}>
                <div className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
                <div className={`font-['Outfit'] font-bold text-lg mt-0.5 ${latencyColor(data[k])}`}>
                  {data[k] == null || data[k] < 0 ? "—" : `${data[k]} ms`}
                </div>
              </div>
            ))}
          </div>

          {/* Success rate */}
          <div className="flex items-center gap-3 text-[11px] text-gray-600 mb-4 flex-wrap">
            <span data-testid="mongo-health-success">
              <b className={data.failed > 0 ? "text-rose-600" : "text-emerald-600"}>
                {data.success}/{data.samples}
              </b> pings OK
            </span>
            {data.server_info?.setName && (
              <span>Replica: <b>{data.server_info.setName}</b></span>
            )}
            {data.server_info?.primary && (
              <span>Primary: <code className="text-[10px] bg-gray-100 rounded px-1 py-0.5">{data.server_info.primary}</code></span>
            )}
          </div>

          {/* Collection sizes */}
          {data.collections_size && Object.keys(data.collections_size).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Tamaño de colecciones (estimado)</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                {Object.entries(data.collections_size).sort((a,b)=>b[1]-a[1]).map(([name, cnt]) => (
                  <div key={name} className="border border-gray-100 rounded p-1.5 flex items-baseline justify-between" data-testid={`mongo-coll-${name}`}>
                    <span className="text-[10px] text-gray-600 truncate mr-1" title={name}>{name}</span>
                    <span className="text-[11px] font-bold text-gray-800 tabular-nums">{fmtCount(cnt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Iter218 · WatchlistAuditLogPanel · historial de cambios en keywords + CPVs
// ════════════════════════════════════════════════════════════════════════════
function WatchlistAuditLogPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | cpv | keyword

  const load = async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? "" : `&kind=${filter}`;
      const { data } = await axios.get(`${API}/admin/watchlist-audit-log?limit=100${qs}`);
      setEntries(data.entries || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const fmt = ts => {
    try {
      const d = new Date(ts);
      return d.toLocaleString("es-ES", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return String(ts || "").slice(0, 16).replace("T", " "); }
  };

  return (
    <div data-testid="watchlist-audit-panel" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <Clock size={18} color="#6366f1" />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
          Historial de cambios · watchlist
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 8, padding: 2 }}>
          {[["all", "Todos"], ["cpv", "CPVs"], ["keyword", "Keywords"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`audit-filter-${k}`}
              style={{
                background: filter === k ? "#fff" : "transparent",
                color: filter === k ? "#111827" : "#6b7280",
                border: "none", borderRadius: 6, padding: "4px 10px",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                boxShadow: filter === k ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
        Cada alta/baja de keyword o CPV queda auditada con usuario y timestamp. Útil para revisar en comité mensual por qué se añadió/quitó cada término.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><Loader2 size={20} className="animate-spin" /></div>
      ) : entries.length === 0 ? (
        <p style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", textAlign: "center", padding: 20 }}>
          Sin cambios registrados con este filtro.
        </p>
      ) : (
        <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Fecha</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Usuario</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Tipo</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Acción</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} data-testid={`audit-entry-${e.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "6px 10px", color: "#374151", fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmt(e.ts)}</td>
                  <td style={{ padding: "6px 10px", color: "#4b5563" }}>{e.user}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <span style={{
                      background: e.kind === "cpv" ? "#e0f2fe" : "#f3e8ff",
                      color:      e.kind === "cpv" ? "#075985" : "#6b21a8",
                      padding: "1px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, textTransform: "uppercase"
                    }}>{e.kind}</span>
                  </td>
                  <td style={{ padding: "6px 10px" }}>
                    <span style={{
                      color: e.action === "add" ? "#166534" : "#991b1b",
                      fontWeight: 600, fontSize: 11
                    }}>
                      {e.action === "add" ? "+ Añadió" : "− Eliminó"}
                    </span>
                  </td>
                  <td style={{ padding: "6px 10px", fontFamily: "monospace", color: "#111827", fontWeight: 600 }}>{e.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SyncHealthPanel · estado en vivo de los crons del backend
// ════════════════════════════════════════════════════════════════════════════
function SyncHealthPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);
  const [logs, setLogs] = React.useState(null);
  const [showAlertCfg, setShowAlertCfg] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sync-health`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cargando sync-health");
    } finally {
      setLoading(false);
    }
  };
  const loadLogs = async (source) => {
    if (expanded === source) {
      setExpanded(null); setLogs(null); return;
    }
    try {
      const r = await axios.get(`${API}/admin/sync-health/logs/${source}?limit=10`);
      setLogs(r.data.items);
      setExpanded(source);
    } catch (e) {
      toast.error("Error cargando logs");
    }
  };
  // Iter199 · Disparar un cron manualmente (recuperar RED/YELLOW sin esperar)
  const runJobNow = async (jobId) => {
    if (!window.confirm(`Ejecutar el cron "${jobId}" AHORA en background?`)) return;
    try {
      await axios.post(`${API}/admin/sync-health/run-job/${jobId}`);
      toast.success(`▶ Cron "${jobId}" lanzado. Refresca en 1-2 min para ver el resultado.`, { duration: 8000 });
      setTimeout(load, 15000); // auto-refresh en 15s
    } catch (e) {
      toast.error(e.response?.data?.detail || `Error lanzando "${jobId}"`);
    }
  };
  React.useEffect(() => { load(); }, []);

  if (!data && loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-500">
        Cargando estado de los crons…
      </div>
    );
  }
  if (!data) return null;

  const HEALTH_CLS = {
    green:   "bg-emerald-100 text-emerald-700 border-emerald-200",
    yellow:  "bg-amber-100 text-amber-700 border-amber-200",
    red:     "bg-rose-100 text-rose-700 border-rose-200",
    unknown: "bg-gray-100 text-gray-500 border-gray-200",
    watchdog: "bg-sky-50 text-sky-600 border-sky-200",
  };
  const HEALTH_LABEL = { green: "OK", yellow: "RETRASO", red: "CRÍTICO", unknown: "NO VERIFICABLE", watchdog: "DISPARADOR" };
  const watchdogCount = (data.jobs || []).filter(j => j.role === "watchdog").length;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-sync-health-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Estado de los crons (Sync Health)</h3>
          <p className="text-[10px] text-gray-500 mt-0.5">
            Estado en vivo de las tareas programadas. RED = sin ejecutar en su cadencia · YELLOW = a punto de vencer ·
            OK = correcto · NO VERIFICABLE = nunca ha registrado una ejecución en el log — no implica que falle ni que
            esté bien, solo que hoy no se puede comprobar (ver N09, roadmap-valoriza-jev-2026-10-11.md).
          </p>
        </div>
        <button
          onClick={load}
          className="text-[10px] text-gray-500 hover:text-gray-700 border border-gray-200 px-2 py-1 rounded"
          data-testid="sync-health-refresh">
          ↻ Refrescar
        </button>
        <button
          onClick={() => setShowAlertCfg(true)}
          className="text-[10px] text-rose-700 hover:text-rose-900 border border-rose-200 bg-rose-50 px-2 py-1 rounded font-semibold ml-1"
          data-testid="sync-health-alert-btn"
          title="Configurar alerta diaria por email cuando algún cron esté en CRÍTICO">
          🔔 Alertas
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-bold" data-testid="sync-health-green-count">
          ✓ {data.summary.green} OK
        </span>
        <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold" data-testid="sync-health-yellow-count">
          ▲ {data.summary.yellow} retraso
        </span>
        <span className="bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full font-bold" data-testid="sync-health-red-count">
          ✗ {data.summary.red} crítico
        </span>
        <span className="bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full font-bold" data-testid="sync-health-unknown-count" title="Nunca registró una ejecución en external_sync_log — no implica que el job esté roto, solo que su estado hoy NO es verificable con los datos disponibles">
          ? {data.summary.unknown} no verificable{data.summary.unknown === 1 ? "" : "s"}
        </span>
        {watchdogCount > 0 && (
          <span className="bg-sky-50 text-sky-600 border border-sky-200 px-2 py-0.5 rounded-full font-bold" data-testid="sync-health-watchdog-count" title="Jobs que solo comprueban si el job principal está desactualizado y lo relanzan — no hacen el trabajo real, por eso no cuentan como OK/retraso/crítico/no verificable">
            ⟲ {watchdogCount} disparador{watchdogCount === 1 ? "" : "es"}
          </span>
        )}
        <span className="ml-auto text-[10px] text-gray-400">Comprobado: {fmtDate(data.checked_at)}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-2 font-semibold">Estado</th>
              <th className="text-left p-2 font-semibold">Job</th>
              <th className="text-left p-2 font-semibold">Próxima ejecución</th>
              <th className="text-left p-2 font-semibold">Último OK</th>
              <th className="text-left p-2 font-semibold">Estado log</th>
              <th className="text-left p-2 font-semibold">Disparado por</th>
              <th className="text-right p-2 font-semibold">Acción</th>
            </tr>
          </thead>
          <tbody>
            {data.jobs.map(j => (
              <React.Fragment key={j.id}>
                <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`sync-row-${j.id}`}>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${HEALTH_CLS[j.health]}`}>
                      {HEALTH_LABEL[j.health]}
                    </span>
                  </td>
                  <td className="p-2 font-mono text-[10px] text-gray-700">{j.id}</td>
                  <td className="p-2 text-gray-700">{fmtDate(j.next_run)}</td>
                  <td className="p-2 text-gray-700">{fmtDate(j.last_finished_at)}</td>
                  <td className="p-2">
                    {j.last_status === "ok" && <span className="text-emerald-700">✓ ok</span>}
                    {j.last_status === "error" && <span className="text-rose-700 font-semibold" title={j.last_error}>✗ error</span>}
                    {!j.last_status && <span className="text-gray-400">—</span>}
                  </td>
                  <td className="p-2 text-gray-500 text-[10px] truncate max-w-[140px]" title={j.last_trigger}>
                    {j.last_trigger || "—"}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex justify-end gap-2">
                      {j.tracked && (
                        <button
                          onClick={() => loadLogs(j.source)}
                          className="text-blue-600 hover:text-blue-800 text-[10px]"
                          data-testid={`sync-drill-${j.id}`}>
                          {expanded === j.source ? "ocultar" : "ver historial"}
                        </button>
                      )}
                      <button
                        onClick={() => runJobNow(j.id)}
                        className="text-emerald-700 hover:text-emerald-900 text-[10px] font-semibold border border-emerald-200 hover:bg-emerald-50 px-1.5 py-0.5 rounded"
                        data-testid={`sync-run-now-${j.id}`}
                        title="Ejecutar este cron AHORA en background. Útil para recuperar jobs en CRÍTICO/RETRASO.">
                        ▶ Ejecutar
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === j.source && logs && (
                  <tr><td colSpan={7} className="bg-gray-50 p-2">
                    <div className="text-[10px] text-gray-600 mb-1 font-semibold">Últimos {logs.length} runs · {j.source}</div>
                    <div className="space-y-0.5 max-h-40 overflow-auto">
                      {logs.length === 0 && <div className="text-gray-400 italic">Sin registros.</div>}
                      {logs.map((l, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 text-[10px] py-0.5 border-b border-gray-100 last:border-0">
                          <div className="col-span-3 text-gray-700">{fmtDate(l.finished_at)}</div>
                          <div className="col-span-1">
                            {l.status === "ok" ? <span className="text-emerald-700">✓</span> : <span className="text-rose-700">✗</span>}
                          </div>
                          <div className="col-span-3 truncate text-gray-500" title={l.triggered_by}>{l.triggered_by}</div>
                          <div className="col-span-5 font-mono text-gray-600 truncate">
                            {l.error ? <span className="text-rose-600">{l.error}</span> : JSON.stringify(l.stats || {})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {showAlertCfg && <SyncHealthAlertModal onClose={() => setShowAlertCfg(false)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SyncHealthAlertModal · configura email diario si hay críticos
// ════════════════════════════════════════════════════════════════════════════
function SyncHealthAlertModal({ onClose }) {
  const [s, setS] = React.useState(null);
  const [recInput, setRecInput] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/sync-health/alert-settings`);
      setS(r.data);
      setRecInput((r.data.recipients || []).join("\n"));
    } catch (e) {
      toast.error("No se pudieron cargar los ajustes");
    }
  };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recInput.split(/[\n,;]/).map(x => x.trim()).filter(Boolean);
      await axios.put(`${API}/admin/sync-health/alert-settings`, {
        enabled: s.enabled,
        recipients,
        include_yellow: s.include_yellow,
        subject_template: s.subject_template,
      });
      toast.success("Ajustes guardados");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("¿Lanzar comprobación ahora y enviar email si hay críticos?")) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/sync-health/send-alert-now`);
      if (r.data.skipped) {
        toast.success(`No hay críticos (${r.data.red} red · ${r.data.yellow} yellow). Email no enviado.`);
      } else if (r.data.ok) {
        toast.success(`Email enviado a ${(r.data.sent_to || []).length} destinatario(s). ${r.data.red} críticos.`);
      } else {
        toast.error("No hay destinatarios configurados");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  if (!s) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-xl p-6 text-xs text-gray-500">Cargando…</div>
      </div>
    );
  }
  const validCount = recInput.split(/[\n,;]/).map(x => x.trim()).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" data-testid="sync-health-alert-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-4">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <h3 className="text-sm font-bold text-gray-900">🔔 Alertas automáticas por email</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Cada día a las 07:55 UTC, si hay 1+ crons en estado CRÍTICO, recibirás un email con el detalle.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">✕</button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={s.enabled}
              onChange={e => setS({ ...s, enabled: e.target.checked })}
              className="w-4 h-4" data-testid="sync-alert-enabled" />
            <span className="font-semibold text-gray-800">Alerta automática activada</span>
            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${s.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
              {s.enabled ? "ACTIVA" : "PAUSADA"}
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={s.include_yellow}
              onChange={e => setS({ ...s, include_yellow: e.target.checked })}
              className="w-4 h-4" data-testid="sync-alert-include-yellow" />
            <span className="text-gray-700">Avisarme también de los YELLOW (retraso, no solo críticos)</span>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Asunto (placeholder: {"{count}"})</span>
            <input value={s.subject_template || ""}
              onChange={e => setS({ ...s, subject_template: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 mt-1"
              data-testid="sync-alert-subject" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Destinatarios (uno por línea o separados por coma)</span>
            <textarea value={recInput} onChange={e => setRecInput(e.target.value)}
              rows={4}
              placeholder={`dredondo@valorizasm.com\nit@valorizasm.com`}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 mt-1 font-mono text-[11px]"
              data-testid="sync-alert-recipients" />
          </label>
          <p className="text-[10px] text-gray-500 -mt-2">{validCount} email(s) detectados</p>
          {s.last_sent && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-[11px] text-gray-700">
              Último envío: <span className="font-semibold">{new Date(s.last_sent).toLocaleString()}</span>
              {s.last_status && (
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {s.last_status.triggered_by} · {s.last_status.red} críticos · {s.last_status.sent} envíos · {s.last_status.errors} errores
                </div>
              )}
            </div>
          )}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-800">
            <strong>Programación:</strong> Día 1 de cada mes NO; <strong>todos los días a las 07:55 UTC</strong> (justo antes del sync diario de PLACSP). El email solo se envía si hay al menos 1 cron en CRÍTICO (o YELLOW si lo activas arriba).
          </div>
        </div>
        <div className="flex justify-between gap-2 p-3 border-t bg-gray-50">
          <button onClick={sendNow} disabled={sending}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50"
            data-testid="sync-alert-send-now">
            {sending ? "Comprobando…" : "Comprobar y enviar ahora"}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cerrar</button>
            <button onClick={save} disabled={saving}
              className="text-xs bg-[#007934] hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50"
              data-testid="sync-alert-save">
              {saving ? "…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Iter161 · Búsqueda global de Admin. Cada término está mapeado al tab en el
// que vive esa sección. Escribir "cron" salta a Sincronización & Salud;
// escribir "pando" salta a Pando & IA, etc. Coincidencia por substring
// case-insensitive; la primera regla que casa gana.
const ADMIN_SEARCH_CATALOG = [
  // Usuarios & Permisos
  { tab: "users", label: "Usuarios pendientes",  keywords: ["usuario", "pendiente", "aprobar", "aprob", "rol", "role", "delegado", "regional"] },
  { tab: "users", label: "Permisos por módulo", keywords: ["permiso", "módulo", "modulo", "access", "acceso"] },
  // Automatizaciones
  { tab: "automations", label: "Informe semanal",  keywords: ["informe", "semanal", "weekly", "email", "digest", "wow", "pantalla", "tender", "keyword", "palabra"] },
  // Sincronización & Salud
  { tab: "sync", label: "Salud de crons + errores", keywords: ["cron", "sync", "sincron", "placsp", "salud", "health", "job", "scheduler", "backfill", "mercado", "error", "alerta", "alert"] },
  // Pando & IA
  { tab: "pando", label: "Consumo y cuotas Pando", keywords: ["pando", "ia", "ai", "anthropic", "openai", "claude", "haiku", "sonnet", "token", "consumo", "cuota", "quota", "tarifa", "rate"] },
  // Datos externos
  { tab: "data", label: "VORTAL RPA", keywords: ["vortal", "rpa", "scrap", "competit"] },
  // Sistema
  { tab: "system", label: "Diagnóstico + Info", keywords: ["env", "variable", "jwt", "mongo", "sistema", "diagn", "version", "producci"] },
];

function AdminSearchBar({ onJump, activeTab }) {
  const [q, setQ] = React.useState("");
  const [flash, setFlash] = React.useState(null);
  const q_lc = q.trim().toLowerCase();
  const matches = React.useMemo(() => {
    if (!q_lc) return [];
    return ADMIN_SEARCH_CATALOG.filter(row =>
      row.keywords.some(k => k.includes(q_lc) || q_lc.includes(k))
    );
  }, [q_lc]);

  const jump = (tab, label) => {
    onJump(tab);
    setFlash(label);
    setQ("");
    setTimeout(() => setFlash(null), 2000);
  };

  // Auto-jump al primer resultado tras un pequeño debounce (evita saltar por
  // cada tecla). Solo si hay 1 match único.
  React.useEffect(() => {
    if (!q_lc || matches.length === 0) return;
    const uniqueTabs = [...new Set(matches.map(m => m.tab))];
    if (uniqueTabs.length !== 1) return;  // ambigüedad → esperar click
    if (uniqueTabs[0] === activeTab) return;  // ya estamos
    const t = setTimeout(() => jump(uniqueTabs[0], matches[0].label), 400);
    return () => clearTimeout(t);
  }, [q_lc, matches, activeTab]);

  return (
    <div className="relative" data-testid="admin-search-bar">
      <div className="relative">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar sección…  (ej: cron, pando, jwt, vortal)"
          className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          data-testid="admin-search-input"/>
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔎</span>
        {flash && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="admin-search-flash">
            → {flash}
          </span>
        )}
      </div>
      {q_lc && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="admin-search-results">
          {matches.slice(0, 8).map((m, i) => (
            <button
              key={i}
              onClick={() => jump(m.tab, m.label)}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-xs flex items-center justify-between border-b border-gray-100 last:border-b-0"
              data-testid={`admin-search-result-${i}`}>
              <span className="text-gray-800">{m.label}</span>
              <span className="text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">{m.tab}</span>
            </button>
          ))}
        </div>
      )}
      {q_lc && matches.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs text-gray-400" data-testid="admin-search-empty">
          Sin coincidencias.
        </div>
      )}
    </div>
  );
}



// Iter168 · Migración MERCADO → PROPUESTA_OPORTUNIDAD (útil tras redeploy
// a producción que aún tiene estados legacy EN_CARTERA/PUBLICADA).
function MercadoMigratePanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runMigrate = async () => {
    if (!window.confirm(
      "Reclasificar a PROPUESTA_OPORTUNIDAD:\n" +
      " • Opps con estado legacy (EN_CARTERA / vacío)\n" +
      " • PUBLICADA del sync antiguo SIN decisión humana (sin historial, " +
      "sin responsable y sin oferta vinculada)\n\n" +
      "Es idempotente: no toca opps con decisión manual, responsable asignado " +
      "o vinculadas a Licitaciones.\n\n" +
      "Ejecutar tras redespliegue a producción si los números no cuadran. ¿Continuar?"
    )) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/mercado/reclassify-all`);
      setResult(r.data);
      const bd = r.data.breakdown || {};
      toast.success(
        `Migradas ${r.data.total} opps → PROPUESTA_OPORTUNIDAD ` +
        `(Publicadas: ${bd.PUBLICADA ?? 0} · En cartera: ${bd.EN_CARTERA ?? 0})`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la migración");
    } finally { setRunning(false); }
  };

  const runApplySuggestions = async () => {
    if (!window.confirm(
      "Aplicar sugerencias automáticas del classifier sobre las opps en PROPUESTA_OPORTUNIDAD. " +
      "Sólo mueve las que el classifier marca como NO_ESTRATEGICA con motivos claros " +
      "(las demás quedan pendientes de decisión humana). ¿Continuar?"
    )) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/mercado/apply-suggestions`);
      toast.success(`${r.data.NO_ESTRATEGICA ?? 0} clasificadas · ${r.data.sin_cambio ?? 0} sin cambio`);
      setResult({ ...r.data, applied: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error aplicando sugerencias");
    } finally { setRunning(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-4"
         data-testid="admin-mercado-migrate-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">Mercado · Migración de estados</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Reclasifica opps con estado legacy (EN_CARTERA, PUBLICADA sin decisión humana) a
            <b> PROPUESTA_OPORTUNIDAD</b> para que el equipo comercial decida manualmente.
            Ejecutar tras cada redespliegue a un entorno con BD legacy.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={runMigrate}
          disabled={running}
          data-testid="admin-mercado-migrate-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {running ? "Migrando…" : "Migrar a Propuestas"}
        </button>
        <button
          onClick={runApplySuggestions}
          disabled={running}
          data-testid="admin-mercado-apply-suggestions-btn"
          className="bg-zinc-600 hover:bg-zinc-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {running ? "Aplicando…" : "Auto-marcar No Estratégicas"}
        </button>
      </div>
      {result && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <pre className="whitespace-pre-wrap font-mono text-emerald-900">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}


// Iter179.7 · Auto-clasificador DG por CPV + keywords
function MercadoAutoClassifyDGPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedDone, setAppliedDone] = useState(false);

  const preview = async () => {
    setLoading(true);
    setAppliedDone(false);
    try {
      const r = await axios.post(`${API}/mercado/admin/auto-classify-dg?apply=false`);
      setStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al obtener preview");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (!stats) { toast.error("Ejecuta primero el preview"); return; }
    const n = (stats.would_set_TRATAMIENTO || 0) + (stats.would_set_SERVICIOS || 0);
    if (!window.confirm(
      `Se asignará DG a ${n} oportunidades (${stats.would_set_TRATAMIENTO} Residuos · ${stats.would_set_SERVICIOS} Servicios Urbanos).\n` +
      `Las ${stats.would_leave_unclassified} ambiguas quedan sin tocar. ¿Continuar?`
    )) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/mercado/admin/auto-classify-dg?apply=true`);
      setStats(r.data);
      setAppliedDone(true);
      toast.success(`${r.data.applied} oportunidades clasificadas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar");
    } finally { setApplying(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-3"
         data-testid="admin-auto-classify-dg-panel">
      <div>
        <h3 className="font-semibold text-gray-900">Mercado · Auto-clasificador Dirección General</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl">
          Escanea las oportunidades <b>Sin DG</b> (o marcadas como <code className="text-[10px] bg-gray-100 rounded px-1">OTRO</code>) y les asigna
          <b> Residuos</b> o <b> Servicios Urbanos</b> aplicando:
          <br />
          1) <b>CPV</b> prefijos de alta confianza (<code>905→Residuos</code>, <code>906/907/773/774/909→Servicios</code>).
          <br />
          2) <b>Keywords</b> en <code className="text-[10px] bg-gray-100 rounded px-1">objeto_contrato</code> (residuo · basura · reciclaje ↔ jardín · poda · zonas verdes · limpieza viaria).
          <br />
          <b>Conservador:</b> las ambiguas o sin señal se dejan sin tocar. Nunca sobrescribe una DG existente distinta de OTRO/vacío.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={preview}
          disabled={loading || applying}
          data-testid="admin-autoclass-preview-btn"
          className="text-xs font-semibold px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 disabled:opacity-50">
          {loading ? "Escaneando…" : "1️⃣ Ver preview"}
        </button>
        <button
          onClick={apply}
          disabled={!stats || applying || appliedDone}
          data-testid="admin-autoclass-apply-btn"
          className="text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
          {applying ? "Aplicando…" : appliedDone ? "✓ Aplicado" : "2️⃣ Aplicar cambios"}
        </button>
        {stats && (
          <span className="text-[11px] text-gray-500">
            Escaneadas <b>{stats.scanned}</b> · <b className="text-emerald-700">{stats.would_set_TRATAMIENTO}</b> Residuos · <b className="text-blue-700">{stats.would_set_SERVICIOS}</b> Serv. Urbanos · <b className="text-amber-700">{stats.would_leave_unclassified}</b> ambiguas
          </span>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2" data-testid="admin-autoclass-samples">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-emerald-800 mb-1">Muestra Residuos ({stats.would_set_TRATAMIENTO})</div>
            <ul className="space-y-1 text-[11px] text-emerald-900">
              {(stats.samples?.TRATAMIENTO || []).slice(0, 5).map((s, i) => (
                <li key={s.id || i} className="border-b border-emerald-100 pb-1 last:border-0">
                  <div className="font-semibold truncate" title={s.cliente}>{s.cliente || "—"}</div>
                  <div className="text-[10px] text-gray-600 line-clamp-2">{s.objeto}</div>
                  <div className="text-[9px] font-mono text-emerald-700 mt-0.5">{s.reason}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-blue-800 mb-1">Muestra Servicios Urbanos ({stats.would_set_SERVICIOS})</div>
            <ul className="space-y-1 text-[11px] text-blue-900">
              {(stats.samples?.SERVICIOS || []).slice(0, 5).map((s, i) => (
                <li key={s.id || i} className="border-b border-blue-100 pb-1 last:border-0">
                  <div className="font-semibold truncate" title={s.cliente}>{s.cliente || "—"}</div>
                  <div className="text-[10px] text-gray-600 line-clamp-2">{s.objeto}</div>
                  <div className="text-[9px] font-mono text-blue-700 mt-0.5">{s.reason}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-[10px] uppercase font-bold text-amber-800 mb-1">Ambiguas / sin señal ({stats.would_leave_unclassified})</div>
            <ul className="space-y-1 text-[11px] text-amber-900">
              {(stats.samples?.unclassified || []).slice(0, 5).map((s, i) => (
                <li key={s.id || i} className="border-b border-amber-100 pb-1 last:border-0">
                  <div className="font-semibold truncate" title={s.cliente}>{s.cliente || "—"}</div>
                  <div className="text-[10px] text-gray-600 line-clamp-2">{s.objeto}</div>
                  <div className="text-[9px] font-mono text-amber-700 mt-0.5">{s.reason}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}


function MotivoNoPresentacionJevPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mercado/motivos-sin-clasificar`);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la vista previa");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const classify = async () => {
    setClassifying(true);
    try {
      const { data } = await axios.post(`${API}/mercado/motivos-clasificar`);
      toast.success(`${data.clasificados} motivo(s) clasificado(s) por Jev`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo clasificar");
    } finally { setClassifying(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-3" data-testid="motivo-no-presentacion-jev-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles size={15} className="text-indigo-600" /> Mercado · Clasificador de motivo de &quot;No presentada&quot;</h3>
        <button onClick={load} disabled={loading} className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="motivo-jev-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 max-w-3xl">
        El desglose estratégica/no estratégica de &quot;No presentadas&quot; solo reconoce hoy la variante exacta
        &quot;Non-strategic&quot;. Para cualquier otra redacción (en español o distinto formato), Jev clasifica el
        motivo una vez y lo cachea — solo se aplica para pasar a &quot;no estratégica&quot; con confianza alta
        (≥70%); por debajo de ese umbral se mantiene el comportamiento histórico (&quot;estratégica&quot; por
        defecto) para no contradecir una lectura de negocio ya acordada.
      </p>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : (
        <>
          {preview?.pendientes?.length ? (
            <>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="motivo-jev-pending">
                {preview.pendientes.length} motivo(s) sin clasificar: {preview.pendientes.join(", ")}
              </div>
              <button onClick={classify} disabled={classifying} data-testid="motivo-jev-classify-btn"
                className="text-xs font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: "#007934" }}>
                {classifying ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null} Clasificar con Jev
              </button>
            </>
          ) : (
            <p className="text-xs text-gray-400">Sin motivos pendientes — todos ya tienen clasificación en caché ({preview?.total_clasificados_en_cache ?? 0} en total).</p>
          )}
        </>
      )}
    </div>
  );
}


// Iter179 · Enrichment masivo multi-campo (background, con polling)
function MercadoEnrichScrapePanel() {
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const fetchStatus = async (jobId) => {
    try {
      const r = await axios.get(`${API}/mercado/admin/enrich-scrape/${jobId}`);
      setJob(r.data);
      if (r.data.status !== "running") stopPolling();
    } catch { /* silent */ }
  };

  const loadLatest = async () => {
    setLoadingLatest(true);
    try {
      const r = await axios.get(`${API}/mercado/admin/enrich-scrape/latest`);
      const doc = r.data && r.data.id ? r.data : null;
      setJob(doc);
      if (doc && doc.status === "running") {
        pollRef.current = setInterval(() => fetchStatus(doc.id), 3000);
      }
    } catch { setJob(null); }
    finally { setLoadingLatest(false); }
  };

  useEffect(() => { loadLatest(); return () => stopPolling(); // eslint-disable-next-line
  }, []);

  const start = async (limit = 0) => {
    const msg = limit
      ? `Iniciar prueba con las primeras ${limit} opps (rápido, ~${Math.ceil(limit/4)*4}s).\nSolo rellena huecos vacíos. ¿Continuar?`
      : "Iniciar enriquecimiento masivo por scraping del pliego online (PLACSP).\n" +
        "Se procesarán TODAS las opps con enlace y algún campo vacío. Solo se rellenan " +
        "huecos (nunca sobrescribe datos existentes). El proceso corre en segundo plano " +
        "y puede tardar varios minutos según el volumen. ¿Continuar?";
    if (!window.confirm(msg)) return;
    setStarting(true);
    stopPolling();
    try {
      const qs = limit ? `?concurrency=4&limit=${limit}` : `?concurrency=4`;
      const r = await axios.post(`${API}/mercado/admin/enrich-scrape${qs}`);
      toast.success(`Job iniciado: ${r.data.job_id.slice(0, 8)}…`);
      await fetchStatus(r.data.job_id);
      pollRef.current = setInterval(() => fetchStatus(r.data.job_id), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el job");
    } finally { setStarting(false); }
  };

  const pct = job && job.total ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;
  const running = job && job.status === "running";
  const done = job && job.status === "done";
  const hits = (job && job.fields_hit) || {};
  const fieldsOrder = ["fecha_presentacion", "presupuesto", "duracion_anios",
    "tipo_contrato", "procedimiento", "cpv", "provincia", "objeto_contrato"];

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-3"
         data-testid="admin-enrich-scrape-panel">
      <div>
        <h3 className="font-semibold text-gray-900">Mercado · Enriquecimiento masivo por scraping</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl">
          Lanza en <b>segundo plano</b> un scrape del pliego online (PLACSP) para completar
          campos vacíos: <code className="text-[10px] bg-gray-100 rounded px-1">fecha_presentacion</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">presupuesto</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">duracion_anios</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">tipo_contrato</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">procedimiento</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">cpv</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">provincia</code>,
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">objeto_contrato</code>.
          <br />
          <b>Conservador:</b> solo rellena huecos, nunca reemplaza datos existentes. Puede tardar
          según el volumen y la disponibilidad de PLACSP.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => start(20)}
          disabled={starting || running}
          data-testid="admin-enrich-scrape-test-btn"
          className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50 border border-indigo-200">
          Probar con 20
        </button>
        <button
          onClick={() => start(0)}
          disabled={starting || running}
          data-testid="admin-enrich-scrape-start-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {starting ? "Iniciando…" : running ? "Job en curso…" : "Iniciar enriquecimiento (todas)"}
        </button>
        <button
          onClick={loadLatest}
          disabled={loadingLatest}
          data-testid="admin-enrich-scrape-refresh-btn"
          className="text-xs px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
          {loadingLatest ? "Cargando…" : "Actualizar estado"}
        </button>
        {job && (
          <span className="text-[11px] text-gray-500" data-testid="admin-enrich-scrape-jobid">
            job <code>{String(job.id || "").slice(0, 8)}</code> · estado <b>{job.status}</b>
          </span>
        )}
      </div>

      {job && (
        <div className="space-y-2" data-testid="admin-enrich-scrape-progress">
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div className={`h-2 ${done ? "bg-emerald-500" : "bg-indigo-500"} transition-all`}
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[11px] text-gray-600 flex flex-wrap gap-3">
            <span data-testid="enrich-progress-processed">Procesadas <b>{job.processed || 0}</b> / {job.total || 0} ({pct}%)</span>
            <span className="text-emerald-700" data-testid="enrich-progress-updated">Actualizadas <b>{job.updated || 0}</b></span>
            <span className="text-amber-700" data-testid="enrich-progress-nomatch">Sin match <b>{job.no_match || 0}</b></span>
            <span className="text-rose-700" data-testid="enrich-progress-failed">Fallos <b>{job.failed || 0}</b></span>
          </div>

          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="admin-enrich-scrape-fieldshit">
            {fieldsOrder.map(f => (
              <div key={f} className="text-[10px] bg-gray-50 border border-gray-100 rounded px-2 py-1 flex items-center justify-between">
                <span className="text-gray-500 truncate">{f}</span>
                <span className="font-mono font-semibold text-gray-800">{hits[f] || 0}</span>
              </div>
            ))}
          </div>

          {done && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                 data-testid="admin-enrich-scrape-done">
              Completado a las {job.finished_at ? new Date(job.finished_at).toLocaleString("es-ES") : "—"}.
              Puedes lanzarlo de nuevo cuando quieras: solo actuará sobre huecos que sigan vacíos.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Iter175 · Backfill de fecha_presentacion vía scrape del pliego (PLACSP)
function FechaPresentacionBackfillPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Rellenar fecha_presentacion escrapeando el pliego online (PLACSP) para las opps con enlace pero sin fecha.\n" +
      "Se procesarán hasta 200 en esta llamada. Idempotente: solo toca las que estén vacías. ¿Continuar?"
    )) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/mercado/backfill-fechas-presentacion?limit=200&concurrency=5`);
      setResult(r.data);
      toast.success(`Actualizadas ${r.data.updated} · sin match ${r.data.no_match} · fallos ${r.data.failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en el backfill");
    } finally { setRunning(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-3"
         data-testid="admin-fecha-backfill-panel">
      <div>
        <h3 className="font-semibold text-gray-900">Mercado · Rellenar fechas de presentación</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-2xl">
          Scrape del pliego online para las opps con <code className="text-[10px] bg-gray-100 rounded px-1">url_externa</code> pero
          sin <code className="text-[10px] bg-gray-100 rounded px-1">fecha_presentacion</code>. Corre automáticamente tras cada
          sync PLACSP (07:00). Pulsa el botón para forzar una ejecución adicional. Procesa 200 por llamada, 5 URLs a la vez.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={run}
          disabled={running}
          data-testid="admin-fecha-backfill-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {running ? "Rellenando…" : "Rellenar fechas ahora"}
        </button>
        {result && (
          <span className="text-[11px] text-gray-600" data-testid="admin-fecha-backfill-result">
            Procesadas <b>{result.processed}</b> · <span className="text-emerald-700">Actualizadas <b>{result.updated}</b></span> ·
            <span className="text-amber-700 ml-1">Sin match <b>{result.no_match}</b></span> ·
            <span className="text-rose-700 ml-1">Fallos <b>{result.failed}</b></span>
          </span>
        )}
      </div>
    </div>
  );
}


// Iter174 · Diccionario manual de alias de operadores de competencia.
// Permite unificar variantes de nombres que el normalizador automático
// (upper + trim + colapso de espacios) no detecta por sí solo.
function CompetitionAliasesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alias, setAlias] = useState("");
  const [target, setTarget] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  // Iter174.2 · Sugerencias automáticas por similitud/contención
  const [autoSuggestions, setAutoSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mercado/aliases`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Error cargando alias");
    } finally { setLoading(false); }
  };

  // Sugerencias de target: operadores que ya aparecen en competition-map
  const loadSuggestions = async () => {
    try {
      const r = await axios.get(`${API}/mercado/competition-map`);
      const set = new Set();
      const collect = (byYear) => {
        Object.values(byYear || {}).forEach(rows => {
          rows.forEach(({ name, norm }) => {
            if (norm && norm !== "__OTROS__" && norm !== "__PENDIENTE__") set.add(name);
          });
        });
      };
      collect(r.data.resolved_by_year);
      collect(r.data.presented_by_year);
      collect(r.data.incumbent_by_year);
      setSuggestions([...set].sort());
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadSuggestions(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!alias.trim() || !target.trim()) {
      toast.error("Alias y target son obligatorios");
      return;
    }
    if (alias.trim().toUpperCase() === target.trim().toUpperCase()) {
      toast.error("Alias y target no pueden ser iguales");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/mercado/aliases`, { alias: alias.trim(), target: target.trim() });
      toast.success(r.data.action === "created" ? "Alias creado" : "Alias actualizado");
      setAlias(""); setTarget("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando alias");
    } finally { setSaving(false); }
  };

  const remove = async (id, aliasName) => {
    if (!window.confirm(`Eliminar el alias "${aliasName}"?`)) return;
    try {
      await axios.delete(`${API}/mercado/aliases/${id}`);
      toast.success("Alias eliminado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  // Iter174.2 · Cargar sugerencias automáticas (similitud + contención)
  const loadAutoSuggestions = async () => {
    setSuggestLoading(true);
    try {
      const r = await axios.get(`${API}/mercado/aliases/suggestions`);
      setAutoSuggestions(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error obteniendo sugerencias");
    } finally { setSuggestLoading(false); }
  };

  // Aplica una fusión concreta (alias A → target B)
  const applyMerge = async (aliasName, targetName) => {
    try {
      await axios.post(`${API}/mercado/aliases`, { alias: aliasName, target: targetName });
      toast.success(`Fusionado: ${aliasName} → ${targetName}`);
      // Marcar el par como dismissed en la misma sesión
      setDismissed(prev => new Set([...prev, `${aliasName}::${targetName}`, `${targetName}::${aliasName}`]));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando alias");
    }
  };

  const dismissPair = (a, b) => {
    setDismissed(prev => new Set([...prev, `${a}::${b}`, `${b}::${a}`]));
  };

  const visibleSuggestions = (autoSuggestions?.items || []).filter(
    s => !dismissed.has(`${s.a}::${s.b}`)
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-4"
         data-testid="admin-competition-aliases-panel">
      <div>
        <h3 className="font-semibold text-gray-900">Competencia · Diccionario de operadores</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-2xl">
          Unifica variantes de nombres que el normalizador automático (mayúsculas + espacios)
          no detecta. Por ejemplo <code className="text-[10px] bg-gray-100 rounded px-1">FCC MA</code> →
          <code className="text-[10px] bg-gray-100 rounded px-1 ml-1">FCC Medio Ambiente</code>.
          Los alias se aplican al ranking anual (Resuelto / Presentado / Incumbents) y al mapa provincial.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2" data-testid="admin-alias-form">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Alias (variante)</label>
          <input
            type="text"
            value={alias}
            onChange={e => setAlias(e.target.value)}
            placeholder="Ej: FCC MA"
            data-testid="admin-alias-input"
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 mt-0.5" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Target (nombre canónico)</label>
          <input
            type="text"
            value={target}
            onChange={e => setTarget(e.target.value)}
            list="alias-target-suggestions"
            placeholder="Ej: FCC Medio Ambiente"
            data-testid="admin-alias-target-input"
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 mt-0.5" />
          <datalist id="alias-target-suggestions">
            {suggestions.map(s => <option key={s} value={s}/>)}
          </datalist>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-alias-submit-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {saving ? "Guardando…" : "Añadir / Actualizar"}
        </button>
        <button
          type="button"
          onClick={loadAutoSuggestions}
          disabled={suggestLoading}
          data-testid="admin-alias-suggest-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {suggestLoading ? "Analizando…" : "Sugerir alias"}
        </button>
      </form>

      {/* Iter174.2 · Sugerencias automáticas (similitud + contención) */}
      {autoSuggestions && (
        <div className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-3 space-y-2"
             data-testid="admin-alias-suggestions">
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-indigo-900">
              <b>{autoSuggestions.count}</b> sugerencia{autoSuggestions.count === 1 ? "" : "s"} encontrada{autoSuggestions.count === 1 ? "" : "s"}
              {autoSuggestions.count > visibleSuggestions.length &&
                <span className="text-indigo-500 ml-1">· {autoSuggestions.count - visibleSuggestions.length} descartadas</span>}
              <span className="text-indigo-400 ml-2">· umbral {(autoSuggestions.threshold * 100).toFixed(0)}%</span>
            </div>
            <button
              onClick={() => setAutoSuggestions(null)}
              className="text-indigo-500 hover:text-indigo-700 text-[11px] font-semibold"
              data-testid="admin-alias-suggestions-close">Cerrar</button>
          </div>
          {visibleSuggestions.length === 0 ? (
            <div className="text-xs text-gray-500 italic p-2">
              No hay sugerencias pendientes. Los pares se pueden refinar bajando el umbral en el endpoint.
            </div>
          ) : (
            <div className="space-y-1.5">
              {visibleSuggestions.map((s, i) => (
                <div key={`${s.a}::${s.b}`}
                     className="flex flex-wrap items-center gap-2 bg-white border border-gray-100 rounded-md px-2 py-1.5"
                     data-testid={`admin-alias-suggestion-${i}`}>
                  <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                    s.reason === "contiene" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                  }`}>
                    {s.reason === "contiene" ? "Contiene" : `${Math.round(s.similarity * 100)}%`}
                  </span>
                  <div className="flex-1 min-w-[240px] text-xs">
                    <span className="font-mono" title={`${s.a_count} ofertas`}>{s.a}</span>
                    <span className="text-gray-400 mx-1.5">({s.a_count})</span>
                    <span className="text-gray-400">↔</span>
                    <span className="font-mono ml-1.5" title={`${s.b_count} ofertas`}>{s.b}</span>
                    <span className="text-gray-400 ml-1.5">({s.b_count})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => applyMerge(s.a, s.b)}
                      className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`admin-alias-suggestion-merge-ab-${i}`}
                      title={`Alias: ${s.a} → ${s.b}`}>
                      {s.a.length > 12 ? `${s.a.slice(0, 10)}…` : s.a} → {s.b.length > 12 ? `${s.b.slice(0, 10)}…` : s.b}
                    </button>
                    <button
                      onClick={() => applyMerge(s.b, s.a)}
                      className="text-[10px] font-semibold px-2 py-1 rounded bg-slate-600 hover:bg-slate-700 text-white"
                      data-testid={`admin-alias-suggestion-merge-ba-${i}`}
                      title={`Alias: ${s.b} → ${s.a}`}>
                      {s.b.length > 12 ? `${s.b.slice(0, 10)}…` : s.b} → {s.a.length > 12 ? `${s.a.slice(0, 10)}…` : s.a}
                    </button>
                    <button
                      onClick={() => dismissPair(s.a, s.b)}
                      className="text-[10px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 text-gray-600"
                      data-testid={`admin-alias-suggestion-dismiss-${i}`}>
                      Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border border-gray-100 rounded-lg overflow-hidden" data-testid="admin-alias-table">
        {loading ? (
          <div className="p-4 text-center text-xs text-gray-400">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-400">
            Sin alias definidos. Añade una fila para unificar variantes.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-600">
                <th className="px-3 py-2">Alias</th>
                <th className="px-3 py-2">→</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Actualizado</th>
                <th className="px-3 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map(row => (
                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50"
                    data-testid={`admin-alias-row-${row.id}`}>
                  <td className="px-3 py-2 font-mono">{row.alias}</td>
                  <td className="px-3 py-2 text-gray-400">→</td>
                  <td className="px-3 py-2 font-semibold text-emerald-800">{row.target}</td>
                  <td className="px-3 py-2 text-gray-400 text-[10px]">
                    {(row.updated_at || row.created_at || "").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => remove(row.id, row.alias)}
                      data-testid={`admin-alias-delete-${row.id}`}
                      className="text-rose-600 hover:text-rose-700 text-[11px] font-semibold">
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// Iter170 · Distribución de opps activas por comercial + link a su cartera
function MercadoOwnershipPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const fmtCompactEUR = (n) => (n == null || n === 0)
    ? "0 €"
    : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(n);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/mercado/ownership-stats`);
        if (alive) setStats(r.data);
      } catch (_) { /* silent */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !stats) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100" data-testid="admin-ownership-panel">
        <h3 className="font-semibold text-gray-900 mb-2">Mercado · Reparto por comercial</h3>
        <p className="text-xs text-gray-400">{loading ? "Cargando…" : "Sin datos"}</p>
      </div>
    );
  }

  const maxCount = Math.max(
    ...stats.assigned.map(s => s.count),
    stats.unassigned.count,
    1,
  );

  const openOwnerView = (email) => {
    nav(`/ofertas-comercial?tab=mercado&owner=${encodeURIComponent(email)}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 space-y-4"
         data-testid="admin-ownership-panel">
      <div>
        <h3 className="font-semibold text-gray-900">Mercado · Reparto por comercial</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {stats.total_active} opps activas en Strategic Market
          {stats.propuestas_pending > 0 && ` · ${stats.propuestas_pending} Propuestas pendientes de clasificar`}
        </p>
      </div>

      {stats.unassigned.count > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between"
             data-testid="admin-ownership-unassigned">
          <div>
            <div className="text-xs font-semibold text-orange-800">Sin responsable asignado</div>
            <div className="text-lg font-bold text-orange-900">
              {stats.unassigned.count} <span className="text-xs font-normal text-orange-600 ml-1">· {fmtCompactEUR(stats.unassigned.importe_total)}</span>
            </div>
          </div>
          <button
            onClick={() => openOwnerView("__unassigned__")}
            data-testid="admin-ownership-unassigned-goto"
            className="text-xs font-semibold px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">
            Ver y repartir →
          </button>
        </div>
      )}

      <div className="space-y-2" data-testid="admin-ownership-list">
        {stats.assigned.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Nadie tiene opps asignadas todavía.</p>
        ) : stats.assigned.map((s) => {
          const pct = Math.round((s.count / maxCount) * 100);
          return (
            <div key={s.owner} className="flex items-center gap-3"
                 data-testid={`admin-ownership-row-${s.owner.replace(/[^a-z0-9]/gi, '_')}`}>
              <div className="w-32 text-xs font-medium text-gray-700 truncate" title={s.owner}>
                {s.owner.split("@")[0]}
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full rounded-full flex items-center justify-end px-2 text-white text-[10px] font-bold"
                     style={{ width: `${pct}%` }}>
                  {s.count}
                </div>
              </div>
              <div className="w-20 text-[10px] text-gray-500 font-mono text-right">
                {fmtCompactEUR(s.importe_total)}
              </div>
              <button
                onClick={() => openOwnerView(s.owner)}
                data-testid={`admin-ownership-goto-${s.owner.replace(/[^a-z0-9]/gi, '_')}`}
                className="text-[10px] font-semibold px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50">
                Ver
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// QualityQueuePanel · Iter247 · Cola de Calidad: TEST/limbo/duplicados/
// huérfanas/sobreasignación. Solo la purga de TEST escribe en BD, y siempre
// requiere selección manual de ids + confirmación explícita (nunca borrado
// automático o por regex ciego desde el cliente).
// ════════════════════════════════════════════════════════════════════════════
const QUALITY_TEST_COLLECTION_LABELS = {
  licitaciones: "Licitaciones",
  projects: "Proyectos",
  it_peticiones: "Peticiones IT/Pando",
};

function CatalogNormalizationPanel() {
  const [provincias, setProvincias] = useState(null);
  const [actividad, setActividad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null); // "provincias" | "actividad"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/catalog/provincias-preview`),
        axios.get(`${API}/admin/catalog/actividad-comercial-preview`),
      ]);
      setProvincias(r1.data);
      setActividad(r2.data);
    } catch (e) {
      toast.error("No se pudo cargar la vista previa de catálogos");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyExact = async (kind) => {
    setApplying(kind);
    try {
      const { data } = await axios.post(`${API}/admin/catalog/${kind === "provincias" ? "provincias" : "actividad-comercial"}-normalize`, {});
      toast.success(`${data.total_documentos_actualizados} documento(s) normalizado(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo normalizar");
    } finally { setApplying(null); }
  };

  const applySuggestion = async (kind, raw, suggested) => {
    setApplying(kind + raw);
    try {
      const endpoint = kind === "provincias" ? "provincias" : "actividad-comercial";
      const { data } = await axios.post(`${API}/admin/catalog/${endpoint}-normalize`, { aliases: { [raw]: suggested } });
      toast.success(`"${raw}" → "${suggested}" aplicado (${data.total_documentos_actualizados} documento(s))`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aplicar la sugerencia");
    } finally { setApplying(null); }
  };

  const renderBlock = (kind, label, d) => {
    if (!d) return null;
    const suggestions = Object.entries(d.sugerencias_ia || {});
    const stillUnresolved = (d.sin_resolver || []).filter(r => !d.sugerencias_ia?.[r]);
    return (
      <div className="border border-gray-100 rounded-lg p-4 mb-3" data-testid={`catalog-norm-block-${kind}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-800">{label}</h4>
          {d.cambios_propuestos?.length > 0 && (
            <button onClick={() => applyExact(kind)} disabled={applying === kind}
              data-testid={`catalog-norm-apply-exact-${kind}`}
              className="text-xs font-medium text-white px-3 py-1 rounded-lg disabled:opacity-60" style={{ background: "#007934" }}>
              {applying === kind ? <Loader2 size={11} className="animate-spin inline" /> : `Aplicar ${d.cambios_propuestos.length} coincidencia(s) exacta(s)`}
            </button>
          )}
        </div>
        {!d.cambios_propuestos?.length && !suggestions.length && !stillUnresolved.length && (
          <p className="text-xs text-gray-400">Catálogo ya normalizado — sin cambios pendientes.</p>
        )}
        {suggestions.length > 0 && (
          <div className="space-y-1.5 mb-2">
            <p className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1"><Sparkles size={11} /> Sugerencias de Jev (alias/abreviaturas, revisar antes de aplicar)</p>
            {suggestions.map(([raw, s]) => (
              <div key={raw} className="flex items-center justify-between text-xs bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-1.5" data-testid={`catalog-norm-suggestion-${kind}-${raw}`}>
                <span><code className="bg-white px-1 rounded">{raw}</code> → <strong>{s.suggested}</strong> <span className="text-gray-400">({Math.round(s.confidence * 100)}%)</span></span>
                <button onClick={() => applySuggestion(kind, raw, s.suggested)} disabled={applying === kind + raw}
                  data-testid={`catalog-norm-apply-suggestion-${kind}-${raw}`}
                  className="font-semibold text-indigo-700 hover:underline shrink-0 ml-2">
                  {applying === kind + raw ? <Loader2 size={10} className="animate-spin inline" /> : "Aplicar"}
                </button>
              </div>
            ))}
          </div>
        )}
        {stillUnresolved.length > 0 && (
          <p className="text-[11px] text-gray-400">Sin ninguna sugerencia fiable: {stillUnresolved.join(", ")}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4" data-testid="catalog-normalization-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-indigo-700" />
          <h3 className="font-bold text-gray-900 font-['Outfit']">Normalización de catálogos (con sugerencia de Jev)</h3>
        </div>
        <button onClick={load} disabled={loading} data-testid="catalog-norm-refresh"
          className="text-xs border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refrescar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Variantes exactas de mayúsculas/acentos se normalizan sin IA. Para el resto (abreviaturas, alias, otro idioma —
        ej. &quot;ZONE_1&quot; vs &quot;Zona 1&quot;), Jev sugiere la forma canónica más probable — nunca se aplica sin que
        un admin la confirme aquí.
      </p>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : (
        <>
          {renderBlock("provincias", "Provincia (ofertas_comercial)", provincias)}
          {renderBlock("actividad", "Actividad comercial (ofertas_comercial)", actividad)}
        </>
      )}
    </div>
  );
}

// N07 (roadmap-valoriza-jev-2026-10-11, Ola 3) · "Finanzas conserva la
// aprobación de límites y fórmulas económicas" — panel de revisión de
// tarifas versionadas (coste/hora, ROI, UTE). Ser admin NO basta para
// aprobar: requiere el permiso granular `tariff_finance_approver`.
function TarifasApprovalPanel() {
  const [tarifas, setTarifas] = useState([]);
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [cobertura, setCobertura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ tipo: "coste_hora", entidad_ref: "", valor: "", moneda: "EUR", unidad: "EUR/hora", fuente: "" });
  // P0-04 (plan cierre POC/Comité 12/10/2026) · "Mi Trabajo" sólo abría la
  // pestaña correcta, nunca aterrizaba en la fila exacta — ahora lee
  // "?open={id}" y hace scroll + resalta la tarifa concreta.
  const openIdRef = useRef(new URLSearchParams(window.location.search).get("open"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u, res, cob] = await Promise.all([
        axios.get(`${API}/tarifas`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/tarifas/cobertura`),
      ]);
      setTarifas((r.data || []).filter(t => t.vigencia_hasta === null));
      setUsers(u.data || []);
      setResources(res.data || []);
      setCobertura(cob.data);
    } catch (e) {
      toast.error("No se pudieron cargar las tarifas: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = openIdRef.current;
    if (!id || loading) return;
    const el = document.getElementById(`tarifa-row-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-500");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 4000);
    }
  }, [loading, tarifas]);

  const decide = async (tid, estado) => {
    let motivo = null;
    if (estado === "rechazada") {
      motivo = window.prompt("Motivo del rechazo (obligatorio):");
      if (!motivo || motivo.trim().length < 3) return;
    }
    setActing(tid);
    try {
      await axios.put(`${API}/tarifas/${tid}/aprobar`, { estado_aprobacion: estado, motivo });
      toast.success(estado === "aprobada" ? "Tarifa aprobada" : "Tarifa rechazada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar la decisión");
    } finally { setActing(null); }
  };

  const toggleApprover = async (u) => {
    try {
      await axios.put(`${API}/admin/users/${u.id}/tariff-finance-approver`, { tariff_finance_approver: !u.tariff_finance_approver });
      toast.success(!u.tariff_finance_approver ? `${u.email} ahora puede aprobar tarifas de Finanzas` : `Permiso retirado a ${u.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar el permiso");
    }
  };

  // P03 (roadmap-valoriza-revision-despliegue-2026-10-12, E05) · "no se
  // encontró allí una acción visible para crear/importar una tarifa" — el
  // backend ya soportaba POST /tarifas, sólo faltaba este formulario.
  const createTarifa = async () => {
    const entidad = resources.find(r => r.id === form.entidad_ref);
    const payload = {
      tipo: form.tipo,
      entidad_ref: form.tipo === "coste_hora" ? (form.entidad_ref || null) : null,
      entidad_label: form.tipo === "coste_hora" ? (entidad?.name || null) : (form.entidad_ref || null),
      valor: form.valor !== "" ? parseFloat(form.valor) : null,
      moneda: form.moneda, unidad: form.unidad, fuente: form.fuente,
    };
    if (payload.valor === null) { toast.error("El valor es obligatorio"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/tarifas`, payload);
      toast.success("Tarifa creada en borrador — pendiente de aprobación de Finanzas");
      setShowCreate(false);
      setForm({ tipo: "coste_hora", entidad_ref: "", valor: "", moneda: "EUR", unidad: "EUR/hora", fuente: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la tarifa");
    } finally { setCreating(false); }
  };

  const pendientes = tarifas.filter(t => t.estado_aprobacion === "pendiente_aprobacion_finanzas");
  const aprobadas = tarifas.filter(t => t.estado_aprobacion === "aprobada");

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="tarifas-approval-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-800">Tarifas y fórmulas económicas (Finanzas)</h3>
        <button onClick={() => setShowCreate(s => !s)} className="text-xs font-semibold text-white bg-[#007934] hover:bg-[#006528] px-3 py-1.5 rounded-lg" data-testid="tarifa-crear-toggle">
          {showCreate ? "Cancelar" : "+ Crear / importar tarifa"}
        </button>
      </div>

      {showCreate && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 space-y-2" data-testid="tarifa-crear-form">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="tarifa-form-tipo">
              <option value="coste_hora">Coste/hora (recurso)</option>
              <option value="roi_formula">Fórmula ROI</option>
              <option value="ute_participacion">Participación UTE</option>
            </select>
            {form.tipo === "coste_hora" ? (
              <select value={form.entidad_ref} onChange={e => setForm({ ...form, entidad_ref: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="tarifa-form-recurso">
                <option value="">Selecciona recurso…</option>
                {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            ) : (
              <input placeholder="Entidad/ámbito (ej. UTE Zaragoza)" value={form.entidad_ref} onChange={e => setForm({ ...form, entidad_ref: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="tarifa-form-entidad" />
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" placeholder="Valor" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="tarifa-form-valor" />
            <input placeholder="Unidad (EUR/hora, %...)" value={form.unidad} onChange={e => setForm({ ...form, unidad: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
            <input placeholder="Fuente (ej. contrato UTE 2026)" value={form.fuente} onChange={e => setForm({ ...form, fuente: e.target.value })} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
          </div>
          <p className="text-[10px] text-gray-500">Se crea como <strong>borrador pendiente de aprobación de Finanzas</strong> — no sustituye la versión vigente hasta que se apruebe. No inventes valores: usa la cifra real aportada por Finanzas/Comercial.</p>
          <button onClick={createTarifa} disabled={creating} className="text-xs font-semibold text-white bg-[#007934] hover:bg-[#006528] px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid="tarifa-form-submit">
            {creating ? "Creando…" : "Crear versión"}
          </button>
        </div>
      )}

      {/* P03 · cobertura real de tarifas versionadas vs legacy, para no dar
          por aceptado "Finanzas sólo debe introducir números" sin ver el
          estado real de cada recurso. */}
      {cobertura && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="tarifas-cobertura">
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-green-700">{cobertura.con_tarifa_aprobada}</p>
            <p className="text-[10px] text-green-600">con tarifa aprobada</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-amber-700">{cobertura.con_tarifa_pendiente_aprobacion}</p>
            <p className="text-[10px] text-amber-600">versión pendiente</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-red-700">{cobertura.sin_tarifa_versionada}</p>
            <p className="text-[10px] text-red-600">sin versionar (legacy/65€·h default)</p>
          </div>
        </div>
      )}

      {loading ? <p className="text-xs text-gray-400">Cargando…</p> : (
        <>
          <p className="text-xs text-gray-500 mb-3">{pendientes.length} de {tarifas.length} versiones vigentes pendientes de aprobación de Finanzas.</p>
          <div className="space-y-2 mb-4">
            {pendientes.map(t => (
              <div key={t.id} id={`tarifa-row-${t.id}`} className="flex items-center justify-between bg-amber-50 rounded-lg p-2.5 text-xs transition-all" data-testid={`tarifa-pendiente-${t.id}`}>
                <div>
                  <span className="font-semibold text-gray-700">{t.tipo}</span>
                  <span className="text-gray-400 ml-2">{t.entidad_label || t.entidad_ref || "global"} · {t.valor ?? t.formula} {t.unidad}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => decide(t.id, "aprobada")} disabled={acting === t.id} data-testid={`tarifa-aprobar-${t.id}`} className="px-2.5 py-1 rounded bg-green-600 text-white font-semibold disabled:opacity-50">Aprobar</button>
                  <button onClick={() => decide(t.id, "rechazada")} disabled={acting === t.id} data-testid={`tarifa-rechazar-${t.id}`} className="px-2.5 py-1 rounded border border-red-200 text-red-600 font-semibold disabled:opacity-50">Rechazar</button>
                </div>
              </div>
            ))}
            {pendientes.length === 0 && <p className="text-[11px] text-gray-400">Sin tarifas pendientes de aprobación.</p>}
          </div>
          {aprobadas.length > 0 && (
            <details className="mb-3">
              <summary className="text-xs font-semibold text-[#007934] cursor-pointer">Ver {aprobadas.length} tarifas vigentes aprobadas</summary>
              <div className="mt-2 space-y-1">
                {aprobadas.map(t => (
                  <div key={t.id} className="flex items-center justify-between text-xs bg-green-50/60 rounded px-2 py-1" data-testid={`tarifa-aprobada-${t.id}`}>
                    <span>{t.tipo} · {t.entidad_label || t.entidad_ref || "global"}</span>
                    <span className="font-semibold text-green-700">{t.valor ?? t.formula} {t.unidad}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          <details>
            <summary className="text-xs font-semibold text-[#007934] cursor-pointer">Gestionar aprobadores financieros</summary>
            <div className="mt-2 space-y-1">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between text-xs py-1">
                  <span>{u.email}</span>
                  <button onClick={() => toggleApprover(u)} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${u.tariff_finance_approver ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {u.tariff_finance_approver ? "Aprobador" : "Sin permiso"}
                  </button>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
    </div>
  );
}

// Reglas de negocio v1 (valoriza-reglas-negocio-iniciales-v1-2026-10-12.md,
// sección 16): lista/filtra las claves vigentes, permite crear una nueva
// VERSIÓN (nunca editar in-place) con motivo obligatorio, y ver el
// historial completo de cada clave. Cada valor muestra su origen
// (operativo/hipotesis/documentado) y la sección del documento de la que
// procede — "ver regla aplicada" exigido por el punto 16.2.
const ORIGEN_BADGE = {
  operativo: "bg-blue-50 text-blue-700 border-blue-200",
  hipotesis: "bg-amber-50 text-amber-700 border-amber-200",
  documentado: "bg-green-50 text-green-700 border-green-200",
};

function BusinessRulesPanel() {
  const [rules, setRules] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [filtroSeccion, setFiltroSeccion] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // clave siendo editada
  const [editValor, setEditValor] = useState("");
  const [editMotivo, setEditMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [historial, setHistorial] = useState(null); // {clave, historial:[]}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroSeccion) params.seccion = filtroSeccion;
      if (q) params.q = q;
      const [r, s] = await Promise.all([
        axios.get(`${API}/business-rules`, { params }),
        axios.get(`${API}/business-rules/secciones`),
      ]);
      setRules(r.data.items || []);
      setSecciones(s.data.secciones || []);
    } catch (e) {
      toast.error("No se pudieron cargar las reglas de negocio: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [filtroSeccion, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const startEdit = (r) => {
    setEditing(r.clave);
    setEditValor(typeof r.valor === "object" ? JSON.stringify(r.valor) : String(r.valor));
    setEditMotivo("");
  };

  const saveEdit = async (r) => {
    if (!editMotivo || editMotivo.trim().length < 3) { toast.error("El motivo es obligatorio (mínimo 3 caracteres)"); return; }
    let valor = editValor;
    if (r.tipo === "number") valor = parseFloat(editValor);
    else if (r.tipo === "json") { try { valor = JSON.parse(editValor); } catch { toast.error("JSON inválido"); return; } }
    setSaving(true);
    try {
      await axios.put(`${API}/business-rules/${encodeURIComponent(r.clave)}/nueva-version`, { valor, motivo: editMotivo, ambito: r.ambito });
      toast.success(`Nueva versión creada para "${r.clave}" (v${r.version + 1})`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la nueva versión");
    } finally { setSaving(false); }
  };

  const viewHistorial = async (r) => {
    try {
      const { data } = await axios.get(`${API}/business-rules/${encodeURIComponent(r.clave)}/historial`, { params: { ambito: r.ambito } });
      setHistorial(data);
    } catch (e) { toast.error("No se pudo cargar el historial"); }
  };

  // T05 (tanda integral 12/10/2026) · "restaurar mediante otra versión" —
  // el backend ya soporta esto de forma nativa: restaurar = crear una
  // nueva versión con el valor antiguo (nunca se edita in-place ni se
  // borra historial). Antes sólo existía como lectura; ahora hay un botón
  // explícito en el propio historial.
  const [restoringVersion, setRestoringVersion] = useState(null);
  const restoreVersion = async (h) => {
    if (!historial) return;
    const motivo = window.prompt(
      `Restaurar v${h.version} (valor: ${typeof h.valor === "object" ? JSON.stringify(h.valor) : h.valor}) como nueva versión vigente. Indica el motivo:`,
      `Restauración de v${h.version}`
    );
    if (!motivo || motivo.trim().length < 3) { if (motivo !== null) toast.error("El motivo es obligatorio (mínimo 3 caracteres)"); return; }
    setRestoringVersion(h.id);
    try {
      const resp = await axios.put(`${API}/business-rules/${encodeURIComponent(historial.clave)}/nueva-version`, {
        valor: h.valor, motivo, ambito: historial.ambito,
      });
      toast.success(`v${h.version} restaurada como nueva versión v${resp.data.version}`);
      setHistorial(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar esa versión");
    } finally { setRestoringVersion(null); }
  };

  const [showCreate, setShowCreate] = useState(false);
  const [newRule, setNewRule] = useState({ clave: "", valor: "", unidad: "M€", origen: "operativo", descripcion: "", motivo: "" });
  const [creating, setCreating] = useState(false);

  const createRule = async () => {
    if (!newRule.clave.trim()) { toast.error("La clave es obligatoria"); return; }
    if (!newRule.motivo || newRule.motivo.trim().length < 3) { toast.error("El motivo es obligatorio (mínimo 3 caracteres)"); return; }
    const valor = parseFloat(newRule.valor);
    if (Number.isNaN(valor)) { toast.error("El valor debe ser numérico"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/business-rules`, { ...newRule, valor, tipo: "number" });
      toast.success(`Regla "${newRule.clave}" creada`);
      setShowCreate(false);
      setNewRule({ clave: "", valor: "", unidad: "M€", origen: "operativo", descripcion: "", motivo: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la regla");
    } finally { setCreating(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="business-rules-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-gray-800">Reglas de negocio (v1, 12/10/2026)</h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{rules.length} claves vigentes</span>
          <button onClick={() => setShowCreate((v) => !v)} className="text-[10px] font-semibold text-white bg-gray-900 px-2.5 py-1 rounded-lg hover:bg-gray-700" data-testid="business-rules-toggle-create">
            {showCreate ? "Cancelar" : "+ Nueva clave (ej. presupuesto mensual)"}
          </button>
        </div>
      </div>
      {showCreate && (
        <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50 space-y-2" data-testid="business-rules-create-form">
          <p className="text-[10px] text-gray-500">
            Ej. para cargar el presupuesto de un mes/dirección para el Business Review:{" "}
            <code className="bg-white px-1 rounded">presupuesto_mensual.2026.10.US</code> (US/WT/TOTAL)
          </p>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Clave (ej. presupuesto_mensual.2026.10.US)" value={newRule.clave} onChange={(e) => setNewRule((p) => ({ ...p, clave: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="business-rules-create-clave" />
            <input placeholder="Valor (número, ej. 5.2)" value={newRule.valor} onChange={(e) => setNewRule((p) => ({ ...p, valor: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="business-rules-create-valor" />
            <input placeholder="Unidad (ej. M€)" value={newRule.unidad} onChange={(e) => setNewRule((p) => ({ ...p, unidad: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="business-rules-create-unidad" />
            <input placeholder="Descripción breve" value={newRule.descripcion} onChange={(e) => setNewRule((p) => ({ ...p, descripcion: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="business-rules-create-descripcion" />
            <input placeholder="Motivo (obligatorio)" value={newRule.motivo} onChange={(e) => setNewRule((p) => ({ ...p, motivo: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5 col-span-2" data-testid="business-rules-create-motivo" />
          </div>
          <button onClick={createRule} disabled={creating} className="text-[10px] font-semibold text-white bg-[#007934] px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid="business-rules-create-submit">
            {creating ? "Creando…" : "Crear"}
          </button>
        </div>
      )}
      <p className="text-[11px] text-gray-500 mb-3">
        Configuración versionada y editable — nunca se edita in-place, cada cambio crea una nueva versión con motivo
        auditado. "Documentado" = acreditado por fuente real; "operativo"/"hipótesis" = decisión de diseño v1, no un
        promedio de mercado ni una política corporativa preexistente.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <input placeholder="Buscar clave…" value={q} onChange={e => setQ(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1.5 flex-1 min-w-[160px]" data-testid="business-rules-search" />
        <select value={filtroSeccion} onChange={e => setFiltroSeccion(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1.5" data-testid="business-rules-seccion-filter">
          <option value="">Todas las secciones</option>
          {secciones.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {loading ? <p className="text-xs text-gray-400">Cargando…</p> : (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
          {rules.map(r => (
            <div key={`${r.clave}:${r.ambito}`} className="border border-gray-100 rounded-lg p-2.5" data-testid={`business-rule-${r.clave}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono font-semibold text-gray-700 truncate">{r.clave}</p>
                  <p className="text-[10px] text-gray-400 truncate">{r.descripcion}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${ORIGEN_BADGE[r.origen] || ORIGEN_BADGE.operativo}`}>{r.origen}</span>
                    <span className="text-[9px] text-gray-400">{r.seccion_documento}</span>
                    <span className="text-[9px] text-gray-400">v{r.version} · ámbito: {r.ambito}</span>
                  </div>
                </div>
                {editing === r.clave ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <input value={editValor} onChange={e => setEditValor(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 w-24" data-testid={`business-rule-edit-valor-${r.clave}`} />
                    <input placeholder="Motivo" value={editMotivo} onChange={e => setEditMotivo(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 w-28" data-testid={`business-rule-edit-motivo-${r.clave}`} />
                    <button onClick={() => saveEdit(r)} disabled={saving} className="text-[10px] font-semibold text-white bg-[#007934] px-2 py-1 rounded disabled:opacity-50" data-testid={`business-rule-save-${r.clave}`}>Guardar</button>
                    <button onClick={() => setEditing(null)} className="text-[10px] text-gray-400 px-1">Cancelar</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-800" data-testid={`business-rule-valor-${r.clave}`}>
                      {typeof r.valor === "object" ? JSON.stringify(r.valor) : String(r.valor)}{r.unidad ? ` ${r.unidad}` : ""}
                    </span>
                    <button onClick={() => viewHistorial(r)} className="text-[10px] text-gray-400 hover:text-[#007934]" data-testid={`business-rule-historial-${r.clave}`}>Historial</button>
                    <button onClick={() => startEdit(r)} className="text-[10px] font-semibold text-[#007934]" data-testid={`business-rule-edit-${r.clave}`}>Editar</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {rules.length === 0 && <p className="text-[11px] text-gray-400">Sin reglas para este filtro.</p>}
        </div>
      )}
      {historial && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setHistorial(null)}>
          <div className="bg-white rounded-xl p-4 max-w-md w-full max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="business-rule-historial-modal">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-mono font-bold">{historial.clave}</p>
              <button onClick={() => setHistorial(null)} className="text-gray-400 text-xs">✕</button>
            </div>
            <div className="space-y-2">
              {historial.historial.map((h, idx) => (
                <div key={h.id} className="text-[11px] border-l-2 border-gray-200 pl-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold">v{h.version}: {typeof h.valor === "object" ? JSON.stringify(h.valor) : String(h.valor)} {h.unidad}</p>
                    {idx !== 0 && (
                      <button
                        onClick={() => restoreVersion(h)}
                        disabled={restoringVersion === h.id}
                        className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded hover:bg-amber-100 disabled:opacity-50 shrink-0"
                        data-testid={`business-rule-restore-v${h.version}`}
                      >
                        {restoringVersion === h.id ? "..." : "Restaurar"}
                      </button>
                    )}
                  </div>
                  <p className="text-gray-400">{h.editor} · {h.vigente_desde?.slice(0, 10)}{h.vigente_hasta ? ` → ${h.vigente_hasta.slice(0, 10)}` : " (vigente)"}</p>
                  {h.motivo && <p className="text-gray-500 italic">"{h.motivo}"</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function JevObservabilityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalls, setShowCalls] = useState(false);
  const [showMotivos, setShowMotivos] = useState(false);
  const [acting, setActing] = useState(null); // motivo_key siendo revisado/aprobado
  const [approvers, setApprovers] = useState([]);
  const [showApprovers, setShowApprovers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        axios.get(`${API}/admin/jev/observability`),
        axios.get(`${API}/admin/users`),
      ]);
      setData(r.data);
      setApprovers(u.data || []);
    } catch (e) {
      toast.error("No se pudo cargar la observabilidad de Jev: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reviewMotivo = async (key) => {
    const reason = window.prompt("Motivo de la revisión (mínimo 3 caracteres):");
    if (!reason || reason.trim().length < 3) return;
    setActing(key);
    try {
      await axios.post(`${API}/mercado/motivos-clasificar/${encodeURIComponent(key)}/revisar`, { reason });
      toast.success("Marcado como revisada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo revisar");
    } finally { setActing(null); }
  };

  const decideMotivo = async (key, decision) => {
    const reason = window.prompt(`Motivo de la ${decision === "aprobada" ? "aprobación" : "rechazo"} (mínimo 3 caracteres):`);
    if (!reason || reason.trim().length < 3) return;
    setActing(key);
    try {
      await axios.post(`${API}/mercado/motivos-clasificar/${encodeURIComponent(key)}/aprobar`, { decision, reason });
      toast.success(decision === "aprobada" ? "Clasificación aprobada" : "Clasificación rechazada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar la decisión");
    } finally { setActing(null); }
  };

  const toggleApprover = async (u) => {
    try {
      await axios.put(`${API}/admin/users/${u.id}/jev-commercial-approver`, { jev_commercial_approver: !u.jev_commercial_approver });
      toast.success(!u.jev_commercial_approver ? `${u.email} ahora puede aprobar clasificaciones comerciales de Jev` : `Permiso retirado a ${u.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar el permiso");
    }
  };

  const REVIEW_BADGE = {
    pendiente_revision: "bg-amber-100 text-amber-700",
    revisada: "bg-sky-100 text-sky-700",
    aprobada: "bg-emerald-100 text-emerald-700",
    rechazada: "bg-rose-100 text-rose-700",
  };

  return (
    <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4" data-testid="jev-observability-panel">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-bold text-gray-800 flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-violet-600" /> Observabilidad de Jev (N02)
        </h4>
        <button onClick={load} data-testid="jev-observability-refresh" className="text-[10px] text-blue-600 hover:underline">Refrescar</button>
      </div>
      <p className="text-[10px] text-gray-500 mb-2">
        Cada llamada a Jev (TypeSafe) queda registrada: tarea, caché o llamada real, duración, confianza y un
        <code className="mx-1 bg-gray-100 px-1 rounded">request_id</code> correlacionable. Jev abstrae el modelo/proveedor
        concreto por diseño — este panel lo declara como N/D en vez de inventarlo.
      </p>
      {loading && <div className="text-[11px] text-gray-500">Cargando…</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="bg-slate-50 border border-slate-200 rounded p-2" data-testid="jev-obs-total">
              <div className="text-[10px] text-slate-500 uppercase">Llamadas registradas</div>
              <div className="text-base font-bold text-slate-800">{data.total_calls_logged}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <div className="text-[10px] text-emerald-600 uppercase">Desde caché</div>
              <div className="text-base font-bold text-emerald-800">{data.cache_hits}</div>
            </div>
            <div className="bg-violet-50 border border-violet-200 rounded p-2">
              <div className="text-[10px] text-violet-600 uppercase">Llamadas reales</div>
              <div className="text-base font-bold text-violet-800">{data.live_calls}</div>
            </div>
            <div className={`rounded p-2 border ${data.errors > 0 ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
              <div className={`text-[10px] uppercase ${data.errors > 0 ? "text-rose-600" : "text-slate-500"}`}>Errores</div>
              <div className={`text-base font-bold ${data.errors > 0 ? "text-rose-800" : "text-slate-800"}`}>{data.errors}</div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-[10px] font-semibold text-gray-600 mb-1">Por tarea</div>
            <table className="w-full text-[11px]" data-testid="jev-obs-by-task-table">
              <thead><tr className="text-left text-gray-400">
                <th className="py-1">Tarea</th><th>Llamadas</th><th>Duración media</th><th>Confianza media</th><th>Errores</th>
              </tr></thead>
              <tbody>
                {(data.by_task || []).map((t) => (
                  <tr key={t.task} className="border-t border-gray-100">
                    <td className="py-1 font-mono text-gray-700">{t.task}</td>
                    <td>{t.calls}</td>
                    <td>{t.avg_duration_ms} ms</td>
                    <td>{t.avg_confidence ?? "—"}</td>
                    <td className={t.errors > 0 ? "text-rose-600 font-semibold" : ""}>{t.errors}</td>
                  </tr>
                ))}
                {(!data.by_task || data.by_task.length === 0) && (
                  <tr><td colSpan={5} className="py-2 text-gray-400">Sin llamadas registradas todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            onClick={() => setShowCalls((v) => !v)}
            data-testid="jev-obs-toggle-last-calls"
            className="text-[10px] text-blue-600 hover:underline mb-2 block">
            {showCalls ? "Ocultar" : "Ver"} últimas {data.last_calls?.length || 0} llamadas
          </button>
          {showCalls && (
            <div className="max-h-56 overflow-y-auto border border-gray-100 rounded mb-3" data-testid="jev-obs-last-calls-list">
              {(data.last_calls || []).map((c, i) => (
                <div key={i} className="text-[10px] px-2 py-1 border-b border-gray-50 flex items-center justify-between gap-2">
                  <span className="font-mono text-gray-600 truncate flex-1">{c.task}</span>
                  <span className={c.cache_hit ? "text-emerald-600" : "text-violet-600"}>{c.cache_hit ? "caché" : "real"}</span>
                  <span className="text-gray-400">{c.duration_ms} ms</span>
                  <span className="text-gray-400">conf. {c.confidence ?? "—"}</span>
                  <span className={c.status === "error" ? "text-rose-600 font-semibold" : "text-gray-400"}>{c.status}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowMotivos((v) => !v)}
            data-testid="jev-obs-toggle-motivos"
            className="text-[10px] text-blue-600 hover:underline mb-2 block">
            {showMotivos ? "Ocultar" : "Ver"} tabla de revisión · motivos &quot;no presentada&quot; ({data.revision_motivos_no_presentada?.length || 0})
          </button>
          {showMotivos && (
            <table className="w-full text-[11px]" data-testid="jev-obs-motivos-table">
              <thead><tr className="text-left text-gray-400">
                <th className="py-1">Motivo original</th><th>Categoría propuesta</th><th>Confianza</th><th>Versión prompt</th><th>Estado revisión</th><th>Trazabilidad</th><th>Acciones</th>
              </tr></thead>
              <tbody>
                {(data.revision_motivos_no_presentada || []).map((m) => (
                  <tr key={m.motivo_key} className="border-t border-gray-100">
                    <td className="py-1">{m.motivo_original}</td>
                    <td>{m.categoria_propuesta}</td>
                    <td>{m.confidence}</td>
                    <td className="font-mono text-gray-500">{m.prompt_version}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${REVIEW_BADGE[m.review_status] || "bg-gray-100 text-gray-600"}`} data-testid={`jev-obs-motivo-status-${m.motivo_key}`}>
                        {m.review_status}
                      </span>
                    </td>
                    <td className="text-[9px] text-gray-400">
                      {m.reviewed_by && <div>Revisada por {m.reviewed_by}</div>}
                      {m.approved_by && <div>{m.review_status === "rechazada" ? "Rechazada" : "Aprobada"} por {m.approved_by}</div>}
                    </td>
                    <td className="flex gap-1">
                      {m.review_status === "pendiente_revision" && (
                        <button onClick={() => reviewMotivo(m.motivo_key)} disabled={acting === m.motivo_key}
                                data-testid={`jev-obs-motivo-revisar-${m.motivo_key}`}
                                className="text-[9px] font-semibold bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded hover:bg-sky-100 disabled:opacity-40">
                          Revisar
                        </button>
                      )}
                      {m.review_status === "revisada" && data.current_user_can_approve && (
                        <>
                          <button onClick={() => decideMotivo(m.motivo_key, "aprobada")} disabled={acting === m.motivo_key}
                                  data-testid={`jev-obs-motivo-aprobar-${m.motivo_key}`}
                                  className="text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded hover:bg-emerald-100 disabled:opacity-40">
                            Aprobar
                          </button>
                          <button onClick={() => decideMotivo(m.motivo_key, "rechazada")} disabled={acting === m.motivo_key}
                                  data-testid={`jev-obs-motivo-rechazar-${m.motivo_key}`}
                                  className="text-[9px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded hover:bg-rose-100 disabled:opacity-40">
                            Rechazar
                          </button>
                        </>
                      )}
                      {m.review_status === "revisada" && !data.current_user_can_approve && (
                        <span className="text-[9px] text-gray-400" title="Requiere el permiso específico de aprobador comercial de Jev">Esperando aprobador comercial</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <button
            onClick={() => setShowApprovers((v) => !v)}
            data-testid="jev-obs-toggle-approvers"
            className="text-[10px] text-blue-600 hover:underline mt-3 block">
            {showApprovers ? "Ocultar" : "Gestionar"} aprobadores comerciales de Jev
          </button>
          {showApprovers && (
            <div className="mt-2 border border-gray-100 rounded p-2" data-testid="jev-obs-approvers-list">
              <p className="text-[10px] text-gray-500 mb-2">
                Ser administrador NO concede este permiso automáticamente (decisión de negocio N02) —
                debe concederse explícitamente a la persona designada por Comercial.
              </p>
              {approvers.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-[11px] py-1 border-t border-gray-50">
                  <span>{u.name || u.email} <span className="text-gray-400">({u.email})</span></span>
                  <button onClick={() => toggleApprover(u)}
                          data-testid={`jev-obs-toggle-approver-${u.id}`}
                          className={`text-[9px] font-semibold px-2 py-0.5 rounded border ${u.jev_commercial_approver ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                    {u.jev_commercial_approver ? "Aprobador ✓" : "Conceder permiso"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function QualityQueuePanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // collection -> Set(ids)
  const [purging, setPurging] = useState(null); // collection en curso
  const [linkOffer, setLinkOffer] = useState(null); // Iter252 · picker manual oferta↔Tender360
  const [dismissedDups, setDismissedDups] = useState(new Set()); // Iter257 · "Rechazar" propuesta (solo oculta, no borra)
  const [mergingDup, setMergingDup] = useState(null); // expediente|cliente en curso
  const [mergingPoc, setMergingPoc] = useState(null); // poc_id_a|poc_id_b en curso
  const [togglingUser, setTogglingUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quality-queue`);
      setData(data);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cargando la cola de calidad");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleSel = (col, id) => {
    setSelected(prev => {
      const set = new Set(prev[col] || []);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...prev, [col]: set };
    });
  };
  const toggleSelAll = (col, ids) => {
    setSelected(prev => {
      const cur = prev[col] || new Set();
      const allSelected = ids.every(id => cur.has(id));
      return { ...prev, [col]: new Set(allSelected ? [] : ids) };
    });
  };

  const purgeSelected = async (col) => {
    const ids = Array.from(selected[col] || []);
    if (ids.length === 0) { toast.error("Selecciona al menos un registro"); return; }
    if (!window.confirm(
      `⚠ Vas a ELIMINAR PERMANENTEMENTE ${ids.length} registro(s) TEST de "${QUALITY_TEST_COLLECTION_LABELS[col] || col}".\n` +
      `Esta acción no se puede deshacer. ¿Confirmas la purga?`
    )) return;
    setPurging(col);
    try {
      const { data: res } = await axios.post(`${API}/admin/quality-queue/purge-test`, { collection: col, ids });
      toast.success(`✓ ${res.deleted} registro(s) TEST eliminados de ${QUALITY_TEST_COLLECTION_LABELS[col] || col}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al purgar registros TEST");
    } finally { setPurging(null); }
  };

  const approveMerge = async (dup) => {
    const p = dup.proposal;
    if (!p) return;
    if (!window.confirm(
      `⚠ Vas a fusionar "${dup.expediente} · ${dup.cliente}".\n` +
      `Se conservará el registro más reciente/completo y se ELIMINARÁN ${p.discard_ids.length} registro(s).\n` +
      `Esta acción no se puede deshacer. ¿Confirmas?`
    )) return;
    setMergingDup(dup.expediente + "|" + dup.cliente);
    try {
      const { data: res } = await axios.post(`${API}/admin/quality-queue/merge-comercial`, {
        keep_id: p.keep_id, discard_ids: p.discard_ids,
      });
      toast.success(`✓ Fusionado: se eliminaron ${res.deleted} registro(s) duplicado(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al fusionar duplicado");
    } finally { setMergingDup(null); }
  };

  const mergePocs = async (dup) => {
    if (!window.confirm(
      `⚠ Vas a fusionar estos 2 PoCs:\n"${dup.title_a}"\n"${dup.title_b}"\n\n` +
      `Se conservará "${dup.title_a}" y se ELIMINARÁ "${dup.title_b}" (el historial de estado queda registrado en el que se conserva).\n` +
      `Esta acción no se puede deshacer. ¿Confirmas la fusión?`
    )) return;
    setMergingPoc(dup.poc_id_a + "|" + dup.poc_id_b);
    try {
      await axios.post(`${API}/poc-ai/merge`, { keep_id: dup.poc_id_a, delete_id: dup.poc_id_b });
      toast.success(`✓ Fusionado: se conservó "${dup.title_a}"`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al fusionar PoCs");
    } finally { setMergingPoc(null); }
  };

  // SU05 (H04) · antes sólo ocultaba en memoria (`dismissedDups`, se
  // perdía al recargar). Ahora persiste en backend por firma de grupo —
  // sobrevive a recarga, nueva sesión y reejecución del detector. Si el
  // grupo cambia de composición, su firma cambia y deja de estar cubierto
  // por este descarte (evidencia material nueva, no se fuerza a ocultar).
  const dismissDup = async (dup) => {
    setDismissedDups(prev => new Set(prev).add(dup.firma_grupo)); // ocultación optimista inmediata
    try {
      await axios.post(`${API}/admin/quality-queue/dismiss-comercial`, {
        firma_grupo: dup.firma_grupo, expediente: dup.expediente, cliente: dup.cliente,
      });
      toast.success("Descarte guardado — no volverá a aparecer para este grupo");
    } catch (e) {
      setDismissedDups(prev => { const s = new Set(prev); s.delete(dup.firma_grupo); return s; });
      toast.error(e.response?.data?.detail || "No se pudo guardar el descarte");
    }
  };

  const toggleUserActive = async (u) => {
    setTogglingUser(u.id);
    try {
      if (u.activo === false) {
        await axios.post(`${API}/admin/users/${u.id}/reactivate`);
        toast.success(`✓ ${u.name || u.email} reactivado`);
      } else {
        if (!window.confirm(`¿Desactivar la cuenta de ${u.name || u.email}? No podrá iniciar sesión hasta reactivarla.`)) { setTogglingUser(null); return; }
        await axios.post(`${API}/admin/users/${u.id}/deactivate`);
        toast.success(`✓ ${u.name || u.email} desactivado`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar estado del usuario");
    } finally { setTogglingUser(null); }
  };


  if (loading && !data) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm" data-testid="quality-queue-panel">
        <Loader2 className="animate-spin mx-auto mb-2" size={20} /> Cargando cola de calidad…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm" data-testid="quality-queue-panel">
        Sin datos disponibles.
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="space-y-5" data-testid="quality-queue-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold font-['Outfit'] text-gray-900 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" /> Cola de Calidad
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Registros TEST, ofertas sin vínculo/huérfanas, sobreasignación y duplicados. Tender360 es la fuente canónica.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="quality-queue-refresh"
                className="bg-[#007934] text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-[#006528] flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Recargar
        </button>
      </div>

      {/* SU09 · estas detecciones vivas son la ENTRADA del inventario único
          de calidad. Al sincronizar detectores (desde Calidad) entran al
          inventario con la línea de vida detectado→revisión→confirmado/
          falso positivo/resuelto, compartida con Mercado y Calidad. */}
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-[#007934]/20 bg-[#007934]/5 px-4 py-3" data-testid="quality-queue-inventario-banner">
        <div className="flex items-start gap-2 min-w-0">
          <Layers size={16} className="text-[#007934] mt-0.5 shrink-0" />
          <p className="text-[11px] text-slate-600">
            Estas detecciones alimentan el <b>Inventario único de calidad</b> (Admin · Mercado · Calidad).
            Allí cada hallazgo se gestiona con una sola línea de vida: <b>detectado → revisión → confirmado / falso positivo / resuelto</b>,
            con responsable y evidencia de cierre.
          </p>
        </div>
        <button type="button" onClick={() => navigate("/calidad")} data-testid="quality-queue-open-inventario"
                className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-[#007934]/30 text-[#007934] rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#007934]/10">
          Abrir inventario único <ArrowRight size={13} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          { k: "test_total", label: "Registros TEST", color: "text-rose-600" },
          { k: "sin_vinculo", label: "Ofertas TIC sin vínculo", color: "text-amber-600" },
          { k: "huerfanas", label: "Ofertas TIC huérfanas", color: "text-rose-600" },
          { k: "limbo_offers", label: "Ofertas TIC en limbo (borrador)", color: "text-amber-600" },
          { k: "licitaciones_en_limbo", label: "Licitaciones en limbo (>7d)", color: "text-amber-600" },
          { k: "sobreasignacion", label: "Sobreasignación", color: "text-rose-600" },
          { k: "duplicados_comerciales", label: "Duplicados comerciales (accionables)", color: "text-amber-600" },
          { k: "poc_duplicados", label: "PoCs duplicados", color: "text-indigo-600" },
          { k: "usuarios_sin_modulos", label: "Usuarios sin módulos", color: "text-gray-500" },
        ].map(({ k, label, color }) => (
          <div key={k} className="border border-gray-100 rounded-lg p-2.5 text-center" data-testid={`quality-summary-${k}`}>
            <div className={`font-['Outfit'] font-bold text-xl ${color}`}>{s[k] ?? 0}</div>
            <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-400">
        "Ofertas TIC" (borradores IT/CAPEX-OPEX) y "Licitaciones" (pipeline comercial Tender360) son colecciones y reglas distintas —
        por eso "en limbo" tiene dos tarjetas separadas, no un único número.
      </p>

      {/* TEST records — purga manual con selección explícita */}
      <div className="border border-rose-100 bg-rose-50/40 rounded-xl p-4">
        <h4 className="text-xs font-bold text-rose-800 mb-1">Registros TEST (candidatos a purga manual)</h4>
        <p className="text-[11px] text-rose-600 mb-3">
          Coincidencia "test" (sin distinguir mayúsculas) en el título o el expediente. Debes seleccionar y confirmar cada purga; nada se borra automáticamente.
          {data.test_records?.workitems_excluded_count > 0 && (
            <> · Workitems excluido a propósito ({data.test_records.workitems_excluded_count} coincidencias, alto riesgo de falso positivo).</>
          )}
        </p>
        {Object.entries(QUALITY_TEST_COLLECTION_LABELS).map(([col, label]) => {
          const rows = data.test_records?.[col] || [];
          const ids = rows.map(r => r.id);
          const sel = selected[col] || new Set();
          if (rows.length === 0) {
            return (
              <div key={col} className="text-[11px] text-gray-400 py-1" data-testid={`quality-test-${col}-empty`}>
                {label}: sin registros TEST.
              </div>
            );
          }
          return (
            <div key={col} className="mb-3 bg-white rounded-lg border border-gray-100 p-3" data-testid={`quality-test-group-${col}`}>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={ids.every(id => sel.has(id))} onChange={() => toggleSelAll(col, ids)}
                         data-testid={`quality-test-${col}-select-all`} />
                  {label} ({rows.length})
                </label>
                <button onClick={() => purgeSelected(col)} disabled={sel.size === 0 || purging === col}
                        data-testid={`quality-test-${col}-purge-btn`}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 flex items-center gap-1.5">
                  {purging === col ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  Purgar seleccionados ({sel.size})
                </button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {rows.map(r => (
                  <label key={r.id} className="flex items-center gap-2 text-[11px] text-gray-700 py-0.5 cursor-pointer"
                         data-testid={`quality-test-row-${col}-${r.id}`}>
                    <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(col, r.id)} />
                    <span className="truncate">{r.title || "(sin título)"}</span>
                    {r.matched_field && r.matched_field !== "titulo" && r.matched_field !== "title" && (
                      <span className="text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-[9px] font-semibold shrink-0"
                            title={`Coincide "test" en ${r.matched_field}`}>
                        {r.matched_field}: {r.matched_value}
                      </span>
                    )}
                    <span className="text-gray-400 font-mono text-[10px]">#{r.id.slice(0, 8)}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Ofertas sin vínculo */}
      <QualityReadOnlyTable
        testId="quality-sin-vinculo"
        title="Ofertas TIC sin vínculo a licitación"
        hint="Vincula manualmente cada oferta con su expediente en Tender360 (candidatos rankeados + confirmación explícita, sin fusiones automáticas). 'Sugerencia' = mejor candidata calculada por el cron diario, nunca autovinculada."
        rows={data.sin_vinculo}
        columns={[
          { k: "title", label: "Oferta" },
          { k: "status", label: "Estado" },
          { k: "suggested_titulo", label: "Sugerencia Tender360 (cron)" },
          { k: "suggested_score_pct", label: "Match %" },
        ]}
        renderActions={(r) => (
          <button
            onClick={() => setLinkOffer({ id: r.id, title: r.title })}
            className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            data-testid={`quality-sin-vinculo-link-${r.id}`}
          >
            Vincular
          </button>
        )}
      />

      {/* Ofertas huérfanas */}
      <QualityReadOnlyTable
        testId="quality-huerfanas"
        title="Ofertas huérfanas (vínculo roto)"
        hint="El linked_tender360_id no existe en Tender360 (p.ej. la licitación se borró). Re-vincula manualmente o desvincula."
        rows={data.huerfanas}
        columns={[{ k: "title", label: "Oferta" }, { k: "linked_tender360_id", label: "ID licitación (no encontrada)" }]}
        renderActions={(r) => (
          <button
            onClick={() => setLinkOffer({ id: r.id, title: r.title, linked_tender360_id: r.linked_tender360_id })}
            className="text-[10px] font-semibold px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            data-testid={`quality-huerfanas-link-${r.id}`}
          >
            Revisar vínculo
          </button>
        )}
      />

      {/* Ofertas TIC en limbo */}
      <QualityReadOnlyTable
        testId="quality-limbo"
        title="Ofertas TIC en limbo (borrador &gt;30 días sin avanzar)"
        hint="db.offers (borradores IT/CAPEX-OPEX) sin movimiento desde hace más de 30 días."
        rows={data.limbo_offers}
        columns={[{ k: "title", label: "Oferta" }, { k: "status", label: "Estado" }, { k: "updated_at", label: "Última actualización" }]}
      />

      {/* Iter254 · Licitaciones comerciales en limbo estratégico — mismo
          cálculo que /ofertas-comercial (alerts/stagnation), añadido aquí
          para que Cola de Calidad sea el panel completo (no se fusiona con
          "Ofertas TIC en limbo" de arriba porque son colecciones distintas). */}
      <QualityReadOnlyTable
        testId="quality-licitaciones-limbo"
        title="Licitaciones en limbo estratégico (&gt;7 días sin decisión)"
        hint="ofertas_comercial en POR DECIDIR / BORRADOR / PUBLICADA — mismo dato que la pantalla Ofertas Comerciales."
        rows={data.licitaciones_en_limbo}
        columns={[
          { k: "title", label: "Objeto" }, { k: "cliente", label: "Cliente" },
          { k: "estado", label: "Estado" }, { k: "dias_en_limbo", label: "Días en limbo" },
        ]}
      />

      {/* Sobreasignación */}
      <div className="border border-gray-100 rounded-xl p-4" data-testid="quality-sobreasignacion">
        <h4 className="text-xs font-bold text-gray-800 mb-1">Sobreasignación de recursos (&gt;100%)</h4>
        {(!data.sobreasignacion || data.sobreasignacion.length === 0) ? (
          <p className="text-[11px] text-gray-400">Sin sobreasignaciones detectadas.</p>
        ) : (
          <div className="space-y-2">
            {data.sobreasignacion.map(r => (
              <div key={r.resource_name} className="text-[11px] border border-amber-100 bg-amber-50/50 rounded p-2" data-testid={`quality-sobreasig-${r.resource_name}`}>
                <div className="font-semibold text-amber-800">{r.resource_name} · {r.total_pct}%</div>
                <div className="text-gray-500 mt-0.5">
                  {r.detail.map((d, i) => (<span key={i} className="mr-2">{d.project} ({d.pct}%)</span>))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Duplicados comerciales · Iter257 · Clasificación real: solo se
          propone fusión cuando el objeto_contrato es (casi) idéntico o
          basura en uno de los registros. La mayoría son LOTES distintos
          del mismo expediente (falso positivo del agrupado expediente+cliente)
          y se muestran como "sin acción". */}
      <div className="border border-gray-100 rounded-xl p-4" data-testid="quality-duplicados">
        <h4 className="text-xs font-bold text-gray-800 mb-1">Duplicados comerciales (mismo expediente+cliente)</h4>
        <p className="text-[11px] text-gray-400 mb-2">
          Revisado 1 a 1: la mayoría son <strong>lotes distintos</strong> del mismo expediente (Lote 1/2, zonas, subsectores) — no duplicados reales.
          Solo se propone fusión cuando el objeto es idéntico o basura. Ninguna fusión se aplica sin tu aprobación explícita.
        </p>
        {(!data.duplicados_comerciales || data.duplicados_comerciales.length === 0) ? (
          <p className="text-[11px] text-gray-400">Sin duplicados detectados.</p>
        ) : (
          <div className="space-y-2">
            {data.duplicados_comerciales.map((d, i) => {
              const dupKey = d.firma_grupo;
              if (dismissedDups.has(dupKey) || d.descartado) return null;
              const isReal = d.classification === "duplicado_real" || d.classification === "duplicado_sucio";
              return (
                <div key={i}
                     className={`text-[11px] border rounded p-2 ${isReal ? "border-amber-200 bg-amber-50/50" : "border-gray-100"}`}
                     data-testid={`quality-dup-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <span className="font-semibold text-gray-700">{d.expediente || "(sin expediente)"} · {d.cliente || "(sin cliente)"}</span>
                      <span className="text-gray-400"> — {d.count} registros</span>
                      {d.classification === "lotes_distintos" && (
                        <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`quality-dup-lotes-${i}`}>
                          Lotes distintos{d.lotes_detectados?.length > 1 ? ` (${d.lotes_detectados.map(l => `Lote ${l}`).join(", ")})` : ""} · sin acción
                        </span>
                      )}
                      {d.classification === "duplicado_real" && (
                        <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                          Duplicado real
                        </span>
                      )}
                      {d.classification === "duplicado_sucio" && (
                        <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                          Posible duplicado (dato basura)
                        </span>
                      )}
                    </div>
                    {isReal && d.proposal && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => approveMerge(d)} disabled={mergingDup === dupKey}
                                data-testid={`quality-dup-approve-${i}`}
                                className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                          {mergingDup === dupKey ? <Loader2 size={11} className="animate-spin" /> : null} Aprobar fusión
                        </button>
                        <button onClick={() => dismissDup(d)} data-testid={`quality-dup-reject-${i}`}
                                className="text-[10px] font-semibold px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
                          Rechazar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-gray-500 mt-0.5 truncate">{d.objeto}</div>
                  {isReal && d.proposal && (
                    <div className="text-[10px] text-amber-700 mt-1">
                      Propuesta: conservar #{d.proposal.keep_id.slice(0, 8)} · {d.proposal.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Jev (revisión 2026-10-11, caso 4 + extensión roadmap Jev) · PoCs
          Pando con título repetido o muy similar bajo el mismo responsable
          y estado — fusión REAL con confirmación explícita (nunca
          automática), vía POST /poc-ai/merge. */}
      <div className="border border-gray-100 rounded-xl p-4 mt-4" data-testid="quality-poc-duplicados">
        <h4 className="text-xs font-bold text-gray-800 mb-1 flex items-center gap-1"><Sparkles size={12} className="text-indigo-600" /> Posibles PoCs Pando duplicados</h4>
        <p className="text-[11px] text-gray-400 mb-2">
          Mismo responsable y mismo estado — título idéntico o, según Jev, la misma iniciativa redactada de forma distinta.
          &quot;Fusionar&quot; conserva el primero y borra el segundo (con confirmación) — también puedes revisarlos a mano en <a href="/poc-ai" className="underline text-indigo-600">/poc-ai</a>.
        </p>
        {(!data.poc_duplicados || data.poc_duplicados.length === 0) ? (
          <p className="text-[11px] text-gray-400">Sin duplicados detectados.</p>
        ) : (
          <div className="space-y-1.5">
            {data.poc_duplicados.map((d, i) => {
              const dupKey = d.poc_id_a + "|" + d.poc_id_b;
              return (
                <div key={i} className="text-[11px] border border-indigo-200 bg-indigo-50/50 rounded p-2 flex items-center justify-between gap-2" data-testid={`quality-poc-dup-${i}`}>
                  <div>
                    <span className="font-semibold text-gray-700">{d.title_a}</span>
                    <span className="text-gray-400"> ≈ </span>
                    <span className="font-semibold text-gray-700">{d.title_b}</span>
                    <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-white border border-indigo-200 text-indigo-700">
                      {d.match_basis === "titulo_exacto" ? "Título idéntico" : `Jev · ${Math.round(d.confidence * 100)}%`}
                    </span>
                    <div className="text-gray-400 mt-0.5">{d.owner} · {d.status}</div>
                  </div>
                  <button onClick={() => mergePocs(d)} disabled={mergingPoc === dupKey}
                    data-testid={`quality-poc-dup-merge-${i}`}
                    className="text-[10px] font-semibold text-white px-2.5 py-1 rounded-lg shrink-0 disabled:opacity-60" style={{ background: "#007934" }}>
                    {mergingPoc === dupKey ? <Loader2 size={10} className="animate-spin inline" /> : "Fusionar"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter304 · D02 Fase 2 — Identidad canónica Expediente/Lote (modo sombra) */}
      <CanonicalExpedientesPanel />

      {/* Usuarios sin módulos */}
      <QualityReadOnlyTable
        testId="quality-usuarios-sin-modulos"
        title="Usuarios sin ningún módulo activo"
        hint="No admin, sin permisos en module_scopes ni module_permissions."
        rows={data.usuarios_sin_modulos}
        columns={[{ k: "email", label: "Email" }, { k: "name", label: "Nombre" }, { k: "role", label: "Rol" },
                  { k: "activo", label: "Estado", render: (v) => (v === false ? "Desactivado" : "Activo") }]}
        renderActions={(r) => (
          <button onClick={() => toggleUserActive(r)} disabled={togglingUser === r.id}
                  data-testid={`quality-usuarios-sin-modulos-toggle-${r.id}`}
                  className={`text-[10px] font-semibold px-2 py-1 rounded border disabled:opacity-40 ${
                    r.activo === false
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                      : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                  }`}>
            {togglingUser === r.id ? "…" : (r.activo === false ? "Reactivar" : "Desactivar")}
          </button>
        )}
      />

      {/* Iter252 · Picker manual oferta↔expediente Tender360, reutilizado desde
          Ofertas TIC. Un solo vínculo por acción (sin bulk), requiere
          confirmación explícita del usuario (window.confirm en el modal),
          nunca autovincula. Al vincular/desvincular refresca la cola. */}
      <LinkTender360Modal
        open={!!linkOffer}
        offer={linkOffer}
        onClose={() => setLinkOffer(null)}
        onLinked={() => { setLinkOffer(null); load(); }}
      />
    </div>
  );
}

function QualityReadOnlyTable({ testId, title, hint, rows, columns, renderActions }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4" data-testid={testId}>
      <h4 className="text-xs font-bold text-gray-800 mb-1">{title} ({(rows || []).length})</h4>
      {hint && <p className="text-[11px] text-gray-400 mb-2">{hint}</p>}
      {(!rows || rows.length === 0) ? (
        <p className="text-[11px] text-gray-400" data-testid={`${testId}-empty`}>Sin registros.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-gray-500 text-left">
                {columns.map(c => <th key={c.k} className="pb-1 pr-3 font-semibold">{c.label}</th>)}
                {renderActions && <th className="pb-1 pr-3 font-semibold">Acción</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-gray-50" data-testid={`${testId}-row-${i}`}>
                  {columns.map(c => <td key={c.k} className="py-1 pr-3 text-gray-700 truncate max-w-[220px]">{c.render ? c.render(r[c.k], r) : String(r[c.k] ?? "—")}</td>)}
                  {renderActions && <td className="py-1 pr-3">{renderActions(r)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Iter304 · D02 Fase 2 — Identidad canónica Expediente/Lote (modo sombra).
// Panel de solo lectura + botón para recalcular. No toca ninguna colección
// operativa — escribe únicamente en `expedientes_canonicos` (derivada).
function CanonicalExpedientesPanel() {
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Iter316 · D02 "Cola de confirmación" — prerequisito documentado antes
  // de apoyar C01 (Cuenta 360º) en expedientes_canonicos con confianza.
  const [pending, setPending] = useState(null);
  const [reviewing, setReviewing] = useState(null); // canonical_key en curso

  const loadPreview = async () => {
    try {
      const r = await axios.get(`${API}/admin/canonical/preview?cross_source_only=true&limit=30`);
      setPreview(r.data);
    } catch { /* aún no se ha calculado */ }
  };
  const loadPending = async () => {
    try {
      const r = await axios.get(`${API}/admin/canonical/pending-review?limit=30`);
      setPending(r.data);
    } catch { /* aún no se ha calculado */ }
  };
  useEffect(() => { loadPreview(); loadPending(); }, []);

  const rebuild = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/canonical/rebuild`);
      setStats(r.data);
      toast.success(`Identidad canónica recalculada: ${r.data.cross_source_matches} cruces entre fuentes detectados`);
      await loadPreview();
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error recalculando identidad canónica");
    } finally { setLoading(false); }
  };

  const review = async (canonical_key, decision) => {
    setReviewing(canonical_key);
    try {
      await axios.post(`${API}/admin/canonical/review`, { canonical_key, decision });
      toast.success(decision === "confirmado" ? "✓ Cruce confirmado" : "✓ Marcado como no relacionado");
      await loadPending();
      await loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la revisión");
    } finally { setReviewing(null); }
  };

  return (
    <div className="border border-indigo-100 rounded-xl p-4 bg-indigo-50/30" data-testid="quality-canonical-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-xs font-bold text-gray-800">Identidad canónica Expediente/Lote (D02 · modo sombra)</h4>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Cruza <strong>Ofertas Comercial</strong>, <strong>Cuadro de Mando (PLACSP/TED)</strong> y <strong>Tender360/Mercado</strong>
            {" "}por (organismo + nº expediente + lote), sin modificar ninguna colección — resultado en una colección sombra de solo lectura.
          </p>
        </div>
        <button onClick={rebuild} disabled={loading} data-testid="quality-canonical-rebuild-btn"
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1.5 shrink-0">
          {loading ? <Loader2 size={12} className="animate-spin"/> : null}
          Recalcular ahora
        </button>
      </div>
      {preview && (
        <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
          <span className="px-2 py-1 rounded bg-white border border-gray-200">Total expedientes: <strong>{preview.total?.toLocaleString("es-ES")}</strong></span>
          <span className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">Alta confianza: <strong>{preview.by_confidence?.alta || 0}</strong></span>
          <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-700">Media (revisar): <strong>{preview.by_confidence?.media || 0}</strong></span>
          <span className="px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-700">Confirmadas manualmente: <strong>{preview.by_confidence?.manual || 0}</strong></span>
          <span className="px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-500">Fuente única (sin cruce, no es anomalía): <strong>{(preview.by_confidence?.unica_fuente || 0).toLocaleString("es-ES")}</strong></span>
          <button onClick={() => setShowPreview(s => !s)} data-testid="quality-canonical-toggle-preview"
            className="text-indigo-600 hover:underline">{showPreview ? "Ocultar" : "Ver"} cruces entre fuentes →</button>
        </div>
      )}
      {showPreview && preview && (
        <div className="mt-2 max-h-56 overflow-y-auto space-y-1.5">
          {preview.items.length === 0 ? (
            <p className="text-[11px] text-gray-400">Sin cruces entre fuentes todavía (o no se ha recalculado).</p>
          ) : preview.items.map((it, i) => (
            <div key={i} className="text-[11px] border border-gray-100 rounded p-1.5 bg-white" data-testid={`quality-canonical-row-${i}`}>
              <span className={`text-[9px] font-bold uppercase px-1 rounded mr-1.5 ${it.link_confidence === "alta" ? "bg-emerald-100 text-emerald-700" : it.link_confidence === "manual" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
                {it.link_confidence}
              </span>
              <strong>{it.organismo}</strong> · Exp. {it.numero_expediente} {it.lote !== "0" ? `· Lote ${it.lote}` : ""}
              <span className="text-gray-400 ml-1">
                ({it.links.ofertas_comercial_ids.length} comercial, {it.links.external_opportunities_ids.length} PLACSP/TED, {it.links.mercado_opportunities_ids.length} mercado)
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Iter316 · Cola de confirmación — confianza media: lote ambiguo +
          2+ fuentes. No se borra ni fusiona nada, solo se marca una
          decisión humana que sobrevive a /canonical/rebuild. */}
      <div className="mt-3 pt-3 border-t border-indigo-100">
        <h5 className="text-[11px] font-bold text-gray-700 mb-1">
          Cruces de confianza media — revisión manual {pending ? `(${pending.total} pendientes)` : ""}
        </h5>
        <p className="text-[10px] text-gray-500 mb-2">
          Mismo organismo + expediente, pero con registros sin lote detectado que podrían pertenecer a cualquiera de los lotes numerados. Confirma si el cruce es correcto o márcalo como no relacionado — ninguna acción borra ni fusiona datos.
        </p>
        {!pending || pending.items.length === 0 ? (
          <p className="text-[11px] text-gray-400" data-testid="quality-canonical-pending-empty">Sin cruces de confianza media pendientes de revisar.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {pending.items.map((it, i) => (
              <div key={it.canonical_key} className="text-[11px] border border-amber-200 bg-amber-50/50 rounded p-2" data-testid={`quality-canonical-pending-${i}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <strong>{it.organismo}</strong> · Exp. {it.numero_expediente} {it.lote !== "0" ? `· Lote ${it.lote}` : ""}
                    <span className="text-gray-400 ml-1">
                      ({it.links.ofertas_comercial_ids.length} comercial, {it.links.external_opportunities_ids.length} PLACSP/TED, {it.links.mercado_opportunities_ids.length} mercado
                      {it.ambiguous_no_lote_ids?.length ? `, ${it.ambiguous_no_lote_ids.length} sin lote detectado` : ""})
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => review(it.canonical_key, "confirmado")} disabled={reviewing === it.canonical_key}
                            data-testid={`quality-canonical-confirm-${i}`}
                            className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                      {reviewing === it.canonical_key ? <Loader2 size={11} className="animate-spin" /> : null} Confirmar cruce
                    </button>
                    <button onClick={() => review(it.canonical_key, "separado")} disabled={reviewing === it.canonical_key}
                            data-testid={`quality-canonical-separate-${i}`}
                            className="text-[10px] font-semibold px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40">
                      No relacionado
                    </button>
                  </div>
                </div>
                <div className="text-gray-500 mt-0.5 truncate">{it.objeto_muestra}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



